import { PayloadAction } from '@reduxjs/toolkit';
import logger from '@/lib/logger';
import { compareRequiredSystemActions, isRequiredSystemAction } from '@/lib/callActions';
import { isNonEmergencyEscalationText } from '@/lib/officerSpeechGuards';
// NO client-side semantic similarity — all semantic dedup is handled by the
// backend LLM (/dedupe/check). Frontend only does exact normalized text match.
import { normalizeEntitiesPayload } from '@/lib/entityUtils';
import { restoreSavedCallerInto } from '@/lib/callerInfoBar';
import type {
  ActivityEvent,
  AiAnalysisState,
  AnswerStatus,
  Caller,
  Entities,
  ExtractionErrors,
  Incident,
  InterimState,
  MapLayerConfigSnapshot,
  PinnedItem,
  SuggestedAction,
} from './aiAnalysisTypes';
import {
  REORDER_DEBOUNCE_MS,
  calculateBalancedOrder,
  clampPriority,
  dedupeById,
  getDeprioritizedPriority,
  mergeActivityFeedEvents,
  orderChangedSignificantly,
  parsePriority,
  simpleHash,
  splitActionsByDisplayCaps,
  updatePriorityChangeTimesForOrder,
} from './aiAnalysisHelpers';
import { hasValidPartialAnswerShape, sanitizeActionAnswerState } from './aiAnalysisAnswerRules';
import { initialState } from './aiAnalysisState';
import { applyGeocodeUnit, isStaleGeocodeUnit } from './aiAnalysisGeocodeUnit';
import { conflictFollowUpNote } from '@/lib/conflictSources';

/**
 * The per-question reword ("improvementPatch") is a transient frontend overlay:
 * the backend computes it during the answer-check and ships it ONLY over the data
 * channel (transcriber.py response_data["improvementPatch"] → setImprovementPatch).
 * It is NEVER persisted into the canonical action store (the answer-lifecycle
 * persist early-returns for `unanswered`, which is exactly when a reword fires).
 *
 * Because every canonical refresh (hydrateCanonicalActionProjection) and the
 * legacy payload (setAiAnalysisPayload) rebuild the action list from the canonical
 * projection — which carries NO improvementPatch — the overlay is dropped on the
 * very next cycle and the displayed text snaps back to the original `details`.
 * That is the "rewrite label shows up, then vanishes again" text flip-flop.
 *
 * Fix: re-apply the overlay by id across a rebuild, but ONLY when the incoming
 * action still has the SAME question text the reword was created against and is
 * not yet answered — so a reword sticks to its question, and is correctly dropped
 * once the text is regenerated or the question gets answered.
 */
type ImprovementOverlay = NonNullable<SuggestedAction['improvementPatch']>;

const normOverlayText = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

function buildImprovementOverlayIndex(
  prev: Array<SuggestedAction | undefined>,
): Map<string, { patch: ImprovementOverlay; original?: string; normText: string }> {
  const index = new Map<
    string,
    { patch: ImprovementOverlay; original?: string; normText: string }
  >();
  for (const a of prev) {
    if (!a || !a.id || !a.improvementPatch?.improvedQuestion) continue;
    if (index.has(a.id)) continue;
    index.set(a.id, {
      patch: a.improvementPatch,
      original: a.originalDetails,
      // The text the reword was created against is the ORIGINAL question text
      // (details), not the reworded one — anchor on that so a stale reword for a
      // regenerated question is dropped instead of re-applied.
      normText: normOverlayText(a.originalDetails || a.details),
    });
  }
  return index;
}

function reapplyImprovementOverlay<T extends SuggestedAction>(
  item: T,
  index: Map<string, { patch: ImprovementOverlay; original?: string; normText: string }>,
): T {
  if (!item || !item.id) return item;
  // Incoming already carries a reword (e.g. once persistence round-trips it) — keep it.
  if (item.improvementPatch?.improvedQuestion) return item;
  // Answered question shows its real answer/highlight, not a reword overlay.
  if (item.answerStatus === 'complete' || item.answered === true) return item;
  const prior = index.get(item.id);
  if (!prior) return item;
  const incomingText = normOverlayText(item.details);
  // Only re-apply if the question text is unchanged since the reword was made.
  if (prior.normText && incomingText && prior.normText !== incomingText) return item;
  return {
    ...item,
    improvementPatch: prior.patch,
    originalDetails: item.originalDetails || prior.original || item.details,
  };
}

/**
 * Derive entityType/sourceEntityId from entityIds array.
 * Entity IDs follow the format "{type}-{hash}" e.g. "person-abc123", "location-xyz789".
 * Sets entityType and sourceEntityId from the first entity ID if not already set.
 */
function deriveEntityFields(item: SuggestedAction): SuggestedAction {
  if (item.entityType || item.sourceEntityId) return item; // already set
  const ids = item.entityIds;
  if (!ids || ids.length === 0) return item;
  const firstId = ids[0];
  if (typeof firstId !== 'string' || !firstId.includes('-')) return item;
  const dashIdx = firstId.indexOf('-');
  const entityType = firstId.substring(0, dashIdx) as SuggestedAction['entityType'];
  return { ...item, entityType, sourceEntityId: firstId };
}

const pickActionSourceMetadata = (item: any): Record<string, unknown> | undefined => {
  const metadata = item?.source_metadata || item?.sourceMetadata;
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata)
    ? metadata
    : undefined;
};

const mapActionProvenance = (item: any): Partial<SuggestedAction> => {
  const sourceMetadata = pickActionSourceMetadata(item);
  const metadataValue = (key: string) =>
    sourceMetadata?.[key] ?? sourceMetadata?.[key[0].toLowerCase() + key.slice(1)];
  return {
    actionKey: item.actionKey || item.action_key,
    action_key: item.action_key,
    source_metadata: sourceMetadata,
    sourceMetadata,
    pipelineOrigin:
      item.pipelineOrigin || item.pipeline_origin || (metadataValue('pipelineOrigin') as string),
    pipelineMode:
      item.pipelineMode || item.pipeline_mode || (metadataValue('pipelineMode') as string),
    pipelineRunId:
      item.pipelineRunId || item.pipeline_run_id || (metadataValue('pipelineRunId') as string),
    frameId: item.frameId || item.frame_id || (metadataValue('frameId') as string),
    frameSignature:
      item.frameSignature || item.frame_signature || (metadataValue('frameSignature') as string),
    generatorLane:
      item.generatorLane || item.generator_lane || (metadataValue('generatorLane') as string),
    writeOwner: item.writeOwner || item.write_owner || (metadataValue('writeOwner') as string),
    comparisonGroupId:
      item.comparisonGroupId ||
      item.comparison_group_id ||
      (metadataValue('comparisonGroupId') as string),
    experimental: item.experimental ?? (metadataValue('experimental') as boolean | undefined),
  };
};

const normalizeInterimUpdate = (interim: Partial<InterimState>): Partial<InterimState> => ({
  ...interim,
  pending: true,
});

const getActiveActionOrder = (state: AiAnalysisState): string[] =>
  [...state.suggestedActions, ...state.queuedActions].map((item) => item.id);

const resortVisibleAndQueuedActions = (
  state: AiAnalysisState,
  oldOrder: string[],
  now = Date.now(),
) => {
  const combinedActions = [...state.suggestedActions, ...state.queuedActions];
  const sortedActions = [...combinedActions].sort(
    (a, b) => (a.priority ?? 50) - (b.priority ?? 50),
  );
  const { visible, queued } = splitActionsByDisplayCaps(sortedActions);
  state.suggestedActions = visible;
  state.queuedActions = queued;
  const newOrder = getActiveActionOrder(state);
  updatePriorityChangeTimesForOrder(state, oldOrder, newOrder, now);
};

const archiveCompletedQueuedAction = (state: AiAnalysisState, promptId: string) => {
  const queuedIndex = state.queuedActions.findIndex((item) => item.id === promptId);
  if (queuedIndex === -1) {
    return false;
  }

  const oldOrder = getActiveActionOrder(state);
  const completedAction = {
    ...state.queuedActions[queuedIndex],
    answeredAt: state.queuedActions[queuedIndex].answeredAt || new Date().toISOString(),
  };

  state.queuedActions = state.queuedActions.filter((item) => item.id !== promptId);
  state.resolvedSuggestions = [
    completedAction,
    ...state.resolvedSuggestions.filter((item) => item.id !== promptId),
  ];

  if (!state.discardedSuggestions.some((item) => item.id === promptId)) {
    state.discardedSuggestions.push(completedAction);
  }

  const archiveIndex = state.allSuggestedActions.findIndex((item) => item.id === promptId);
  if (archiveIndex !== -1) {
    state.allSuggestedActions[archiveIndex] = {
      ...state.allSuggestedActions[archiveIndex],
      ...completedAction,
    };
  } else {
    state.allSuggestedActions.push(completedAction);
  }

  resortVisibleAndQueuedActions(state, oldOrder);
  return true;
};

const clearPartialOnlyAnswerFields = (target: SuggestedAction | undefined) => {
  if (!target) return;
  target.partialAnswer = undefined;
  target.highlightedQuestion = undefined;
  target.questionParts = undefined;
  target.unansweredParts = undefined;
};

const removePromptFromPartialState = (state: AiAnalysisState, promptId: string) => {
  state.partialPrompts = state.partialPrompts.filter((id) => id !== promptId);
  state.partialSuggestions = state.partialSuggestions.filter((item) => item.id !== promptId);
};

const hasCompleteAnswerState = (
  action: Partial<SuggestedAction> | undefined,
  answeredIds: Set<string> = new Set(),
) =>
  Boolean(
    action?.answerStatus === 'complete' ||
      action?.answered === true ||
      (action?.id && answeredIds.has(action.id)),
  );

const hasOfficerAddressedState = (action: Partial<SuggestedAction> | undefined) =>
  Boolean(action?.officerAddressed === true || action?.officerAddressedPartial === true);

const copyOfficerAddressedState = (
  incoming: SuggestedAction,
  existing: SuggestedAction,
): SuggestedAction => ({
  ...incoming,
  officerAddressed: existing.officerAddressed,
  officerAddressedAt: existing.officerAddressedAt,
  officerAddressedPartial: existing.officerAddressedPartial,
  officerQuote: existing.officerQuote,
  officerAddressedReason: existing.officerAddressedReason,
});

// NOTE (owner decision 2026-07-23): a partial answer must NOT clear the
// officer-addressed flags. A 2026-07-21 change wiped officerAddressed /
// officerAddressedPartial on every partial verdict "so the officer follows up" —
// but layout precedence already returns partial items to the ACTIVE group
// regardless of the flag (partitionActionsForLayout: completed > partial >
// addressed), so the clearing changed no grouping. What it actually did was
// destroy the asked (mic) badge: the answer evaluators re-run every ~6s and are
// non-monotonic, so nearly every addressed item eventually caught a partial
// verdict and lost its badge — "the addressed flag no longer works". The flag now
// survives partial verdicts; the mirrored server-side clearing in
// lib/server/callActions.ts answer_partial was reverted at the same time.

const findActionById = (state: AiAnalysisState, promptId: string): SuggestedAction | undefined =>
  state.suggestedActions.find((item) => item.id === promptId) ||
  state.queuedActions.find((item) => item.id === promptId) ||
  state.allSuggestedActions.find((item) => item.id === promptId) ||
  state.resolvedSuggestions.find((item) => item.id === promptId);

const clearInvalidPartialState = (state: AiAnalysisState, promptId: string) => {
  removePromptFromPartialState(state, promptId);

  for (const list of [state.suggestedActions, state.queuedActions, state.allSuggestedActions]) {
    const target = list.find((item) => item.id === promptId);
    if (!target || target.answerStatus === 'complete' || target.answered === true) continue;
    target.answerStatus = undefined;
    clearPartialOnlyAnswerFields(target);
  }
};

/**
 * Keys that must never make two entities compare as different.
 *
 * `lastUpdatedAt` is the "time since change" stamp itself. Comparing it is
 * circular: an entity already in the store carries a stamp, an incoming AI
 * payload never does, so every comparison reported "changed" and re-stamped the
 * entity — the badge reset on every analysis pass even when no property had been
 * defined or altered. `mergeMatchedEntity` already skips this key when merging
 * values, so equality has to skip it too.
 *
 * `note` is the officer's own annotation and was already meant to be excluded.
 */
const EQUALITY_IGNORED_ENTITY_KEYS = new Set(['lastUpdatedAt', 'note']);

/**
 * Epoch values below this boundary are interpreted as seconds and converted to
 * milliseconds at the Redux ingest boundary. 10 billion seconds reaches into
 * 2286, while 10 billion milliseconds is 1970-04-26. `lastUpdatedAt` describes
 * a runtime entity change, so a genuine millisecond value from before that date
 * is not plausible for this application; if one is supplied, it is deliberately
 * treated as seconds and multiplied by 1,000.
 */
const LAST_UPDATED_AT_SECONDS_CUTOFF = 10_000_000_000;

const normalizeEntityLastUpdatedAt = <T>(entity: T): T => {
  if (!entity || typeof entity !== 'object' || Array.isArray(entity)) return entity;

  const record = entity as Record<string, unknown>;
  const stamp = record.lastUpdatedAt;
  if (
    typeof stamp !== 'number' ||
    !Number.isFinite(stamp) ||
    stamp <= 0 ||
    stamp >= LAST_UPDATED_AT_SECONDS_CUTOFF
  ) {
    return entity;
  }

  return {
    ...record,
    lastUpdatedAt: Math.round(stamp * 1_000),
  } as T;
};

const ENTITY_TIMESTAMP_ARRAY_FIELDS = [
  'officers',
  'people',
  'organizations',
  'locations',
  'objects',
  'activity',
  'policiesAndLaws',
  'other',
] as const;

/** Normalize all entity timestamps without mutating incoming payload objects. */
const normalizeEntityTimestamps = <T extends Partial<Entities> | undefined>(entities: T): T => {
  if (!entities) return entities;

  const source = entities as Record<string, unknown>;
  let normalized: Record<string, unknown> = source;
  const setNormalizedField = (key: string, value: unknown) => {
    if (value === source[key]) return;
    if (normalized === source) normalized = { ...source };
    normalized[key] = value;
  };

  setNormalizedField('caller', normalizeEntityLastUpdatedAt(source.caller));
  setNormalizedField('incident', normalizeEntityLastUpdatedAt(source.incident));

  for (const field of ENTITY_TIMESTAMP_ARRAY_FIELDS) {
    const value = source[field];
    if (!Array.isArray(value)) continue;

    const next = value.map((item) => normalizeEntityLastUpdatedAt(item));
    const changed = next.some((item, index) => item !== value[index]);
    if (changed) setNormalizedField(field, next);
  }

  return normalized as T;
};

export const aiAnalysisReducers = {
  /**
   * Reorder actions with type balancing and debouncing
   * Called periodically by component to update stable display order
   * Only updates if enough time has passed AND order changed significantly
   */
  reorderActions: (state: AiAnalysisState, action: PayloadAction<{ force?: boolean }>) => {
    const now = Date.now();
    const force = action.payload?.force ?? false;

    // Debounce: only reorder every 5 seconds unless forced
    if (!force && now - state.lastReorderTime < REORDER_DEBOUNCE_MS) {
      return;
    }

    // Combine active and queued actions for reordering
    const allActions = [...state.suggestedActions, ...state.queuedActions];
    const oldOrder = allActions.map((a) => a.id);

    // Calculate new balanced order
    const newOrder = calculateBalancedOrder(allActions);

    // Check if order changed significantly
    if (!force && !orderChangedSignificantly(state.displayOrder, newOrder)) {
      return;
    }

    const newOrderSet = new Set(newOrder);
    const newFullOrder = [...newOrder, ...oldOrder.filter((id) => !newOrderSet.has(id))];
    updatePriorityChangeTimesForOrder(state, oldOrder, newFullOrder, now);

    // Update display order
    state.displayOrder = newOrder;
    state.lastReorderTime = now;

    // Also update suggestedActions to match displayOrder for compatibility
    const orderedActions = newOrder
      .map((id) => allActions.find((a) => a.id === id))
      .filter((a): a is SuggestedAction => a !== undefined);

    state.suggestedActions = orderedActions;
    state.queuedActions = allActions.filter((a) => !newOrder.includes(a.id));

    logger.info('aiAnalysisSlice', 'reorderActions: Display order updated', {
      displayOrder: newOrder,
      activeCount: state.suggestedActions.length,
      queuedCount: state.queuedActions.length,
    });
  },

  /**
   * Force immediate reorder (e.g., when item is removed or added)
   */
  forceReorderActions: (state: AiAnalysisState) => {
    const allActions = [...state.suggestedActions, ...state.queuedActions];
    const oldOrder = allActions.map((a) => a.id);
    const newOrder = calculateBalancedOrder(allActions);
    const now = Date.now();

    const newOrderSet = new Set(newOrder);
    const newFullOrder = [...newOrder, ...oldOrder.filter((id) => !newOrderSet.has(id))];
    updatePriorityChangeTimesForOrder(state, oldOrder, newFullOrder, now);

    state.displayOrder = newOrder;
    state.lastReorderTime = now;

    // Update suggestedActions to match displayOrder
    const orderedActions = newOrder
      .map((id) => allActions.find((a) => a.id === id))
      .filter((a): a is SuggestedAction => a !== undefined);

    state.suggestedActions = orderedActions;
    state.queuedActions = allActions.filter((a) => !newOrder.includes(a.id));
  },

  setSuggestedActions: (state: AiAnalysisState, action: PayloadAction<SuggestedAction[]>) => {
    const now = Date.now();
    const oldOrder = [...state.suggestedActions, ...state.queuedActions].map((a) => a.id);

    // Backend now generates IDs - just use them directly
    // If somehow an item doesn't have an ID, generate one as fallback
    const actionsWithIds = action.payload.map((item, idx) => {
      let a = item;
      if (!a.id) {
        // Fallback: generate ID from details if backend didn't provide one
        logger.warn(
          'AiAnalysisReducers',
          'Suggestion missing ID from backend, generating fallback',
          { item },
        );
        a = { ...a, id: `fallback_${idx}_${Date.now()}` };
      }
      return deriveEntityFields(a);
    });

    // Sort by priority to ensure consistent order (lower = higher priority)
    actionsWithIds.sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50));
    const { visible, queued } = splitActionsByDisplayCaps(actionsWithIds);
    state.suggestedActions = visible;
    state.queuedActions = queued;
    const newOrder = [...state.suggestedActions, ...state.queuedActions].map((a) => a.id);
    updatePriorityChangeTimesForOrder(state, oldOrder, newOrder, now);
  },

  // Add a single suggested action (with deduplication by ID)
  addSuggestedAction: (state: AiAnalysisState, action: PayloadAction<SuggestedAction>) => {
    const newAction = deriveEntityFields(action.payload);

    // Initialize staleness tracking fields for new items
    const now = Date.now();
    if (!newAction.createdAt) {
      newAction.createdAt = now;
    }
    if (newAction.timesIgnored === undefined) {
      newAction.timesIgnored = 0;
    }

    // Check if action with same ID already exists
    const existingIndex = state.suggestedActions.findIndex((a) => a.id === newAction.id);
    if (existingIndex !== -1) {
      // Already exists, don't add duplicate
      logger.info('aiAnalysisSlice', 'addSuggestedAction: Action already exists, skipping', {
        id: newAction.id,
      });
      return;
    }

    // Check if already answered or detected
    if (
      state.answeredPrompts.includes(newAction.id) ||
      state.detectedPrompts.includes(newAction.id)
    ) {
      logger.info(
        'aiAnalysisSlice',
        'addSuggestedAction: Action already answered/detected, skipping',
        { id: newAction.id },
      );
      return;
    }

    // Check if this exact ID was previously discarded or removed by the officer
    if (
      state.discardedSuggestions.some((a) => a.id === newAction.id) ||
      state.removedSuggestions.some((a) => a.id === newAction.id)
    ) {
      logger.info('aiAnalysisSlice', 'addSuggestedAction: Action was discarded/removed, skipping', {
        id: newAction.id,
      });
      return;
    }

    // Exact-match dedup only: the complete, unchanged details string.
    // Semantic similarity is handled by the backend LLM dedup (/dedupe/check);
    // lowercasing or stripping punctuation would merge distinct identifiers
    // (case numbers, non-ASCII names) into one action.
    const newContent = newAction.details || '';
    // Required system cards have distinct workflow IDs even when an ordinary
    // action already uses the same words (for example the closing farewell).
    if (newContent && !isRequiredSystemAction(newAction)) {
      const allLists = [
        state.suggestedActions,
        state.allSuggestedActions,
        state.resolvedSuggestions,
        state.discardedSuggestions,
        state.removedSuggestions,
      ];
      for (const list of allLists) {
        for (const existing of list) {
          if ((existing.details || '') === newContent) {
            logger.info('aiAnalysisSlice', 'addSuggestedAction: Exact duplicate exists, skipping', {
              newId: newAction.id,
              existingId: existing.id,
            });
            return;
          }
        }
      }
    }

    const oldOrder = [...state.suggestedActions, ...state.queuedActions].map((a) => a.id);

    // Add to the list and sort by priority (lower = higher priority)
    const allActions = [...state.suggestedActions, ...state.queuedActions, newAction];
    allActions.sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50));
    const { visible, queued } = splitActionsByDisplayCaps(allActions);
    state.suggestedActions = visible;
    state.queuedActions = queued;

    const updateNow = Date.now();
    const newOrder = allActions.map((a) => a.id);
    updatePriorityChangeTimesForOrder(state, oldOrder, newOrder, updateNow);

    // Also add to the archive
    if (!state.allSuggestedActions.some((a) => a.id === newAction.id)) {
      state.allSuggestedActions.push(newAction);
    }

    logger.info('aiAnalysisSlice', 'addSuggestedAction: Added new action', {
      id: newAction.id,
      type: newAction.type,
      priority: newAction.priority,
      details: newAction.details.substring(0, 50),
    });
  },

  // A8/B: record the agent-backend's force-generate-response ACK for an
  // officer's explicit "generate more" request. This does NOT add actions —
  // the generated cards arrive through the canonical projection like every
  // other framework-generated action. It only lets the SuggestedActions column
  // clear the group's spinner and, on failure, show the officer why. (Replaces
  // addForceGeneratedActions, the old side door that injected REST-generated
  // actions straight into Redux, bypassing canonical.)
  recordForceGenerateAck: (
    state: AiAnalysisState,
    action: PayloadAction<{
      requestId: string;
      phase: 'accepted' | 'terminal';
      outcome?: 'generated' | 'nothing_new' | 'failed';
      generatedCount?: number;
      error?: string;
    }>,
  ) => {
    state.forceGenerateAcks.push({
      requestId: action.payload.requestId,
      phase: action.payload.phase,
      outcome: action.payload.outcome,
      generatedCount: action.payload.generatedCount,
      error: action.payload.error,
      at: Date.now(),
    });
    // Bounded: keep only the most recent events so the queue can't grow without
    // limit over a long call (the column consumes each by requestId).
    if (state.forceGenerateAcks.length > 50) {
      state.forceGenerateAcks.splice(0, state.forceGenerateAcks.length - 50);
    }
    if (action.payload.phase === 'terminal' && action.payload.outcome === 'failed') {
      logger.warn('aiAnalysisSlice', 'recordForceGenerateAck: manual generation failed', {
        requestId: action.payload.requestId,
        error: action.payload.error,
      });
    }
  },

  // Auto-detection: Mark prompt as answered directly (skip yellow detected state)
  // This now does the same as markPromptAnswered but also stores confidence
  markPromptDetected: (
    state: AiAnalysisState,
    action: PayloadAction<
      | string
      | {
          promptId: string;
          answer?: string;
          partialAnswer?: string;
          confidence?: number;
          tierMatched?:
            | 'ideal'
            | 'acceptable'
            | 'minimum'
            | 'negative'
            | 'declined'
            | 'unable'
            | null;
          answerSource?: 'automatic' | 'manual';
          highlightedQuestion?: string;
          questionParts?: Array<{
            text: string;
            status: 'answered' | 'unanswered' | 'plain';
            partRef?: string;
          }>;
          answeredParts?: Array<{
            part: string;
            answer: string;
            transcriptQuote?: string;
            reasoning?: string;
          }>;
          unansweredParts?: string[];
          answerReasoning?: string;
        }
    >,
  ) => {
    const promptId = typeof action.payload === 'string' ? action.payload : action.payload.promptId;
    const answer = typeof action.payload === 'string' ? undefined : action.payload.answer;
    const confidence = typeof action.payload === 'string' ? undefined : action.payload.confidence;
    const tierMatched = typeof action.payload === 'string' ? undefined : action.payload.tierMatched;
    const questionParts =
      typeof action.payload === 'string' ? undefined : action.payload.questionParts;
    const answeredParts =
      typeof action.payload === 'string' ? undefined : action.payload.answeredParts;
    const answerReasoning =
      typeof action.payload === 'string' ? undefined : action.payload.answerReasoning;
    const answeredAt = new Date().toISOString();
    // Determine answer source: explicit, confidence=0 means manual, otherwise automatic
    const answerSource =
      typeof action.payload === 'string'
        ? 'automatic'
        : action.payload.answerSource || (action.payload.confidence === 0 ? 'manual' : 'automatic');

    // Go directly to answered state (skip detected intermediate state)
    if (!state.answeredPrompts.includes(promptId)) {
      state.answeredPrompts.push(promptId);
    }
    // Remove from detected and partial lists
    state.detectedPrompts = state.detectedPrompts.filter((id) => id !== promptId);
    state.partialPrompts = state.partialPrompts.filter((id) => id !== promptId);
    state.partialSuggestions = state.partialSuggestions.filter((a) => a.id !== promptId);

    // Update the action object in suggestedActions with full answered state
    const actionIndex = state.suggestedActions.findIndex((a) => a.id === promptId);
    const queuedIndex = state.queuedActions.findIndex((a) => a.id === promptId);
    if (actionIndex !== -1) {
      state.suggestedActions[actionIndex].answerStatus = 'complete';
      state.suggestedActions[actionIndex].answered = true;
      state.suggestedActions[actionIndex].answeredAt = answeredAt;
      state.suggestedActions[actionIndex].answerSource = answerSource;
      if (answer) {
        state.suggestedActions[actionIndex].answer = answer;
      }
      if (confidence !== undefined) {
        state.suggestedActions[actionIndex].confidence = confidence;
      }
      if (tierMatched !== undefined) {
        state.suggestedActions[actionIndex].tierMatched = tierMatched;
      }
      if (answerReasoning) {
        state.suggestedActions[actionIndex].answerReasoning = answerReasoning;
      }
      if (answeredParts && answeredParts.length > 0) {
        state.suggestedActions[actionIndex].answeredParts = answeredParts;
      }
      // Complete answers render as plain text — clear all highlighting fields.
      clearPartialOnlyAnswerFields(state.suggestedActions[actionIndex]);
    } else if (queuedIndex !== -1) {
      state.queuedActions[queuedIndex].answerStatus = 'complete';
      state.queuedActions[queuedIndex].answered = true;
      state.queuedActions[queuedIndex].answeredAt = answeredAt;
      state.queuedActions[queuedIndex].answerSource = answerSource;
      if (answer) {
        state.queuedActions[queuedIndex].answer = answer;
      }
      if (confidence !== undefined) {
        state.queuedActions[queuedIndex].confidence = confidence;
      }
      if (tierMatched !== undefined) {
        state.queuedActions[queuedIndex].tierMatched = tierMatched;
      }
      if (answerReasoning) {
        state.queuedActions[queuedIndex].answerReasoning = answerReasoning;
      }
      if (answeredParts && answeredParts.length > 0) {
        state.queuedActions[queuedIndex].answeredParts = answeredParts;
      }
      clearPartialOnlyAnswerFields(state.queuedActions[queuedIndex]);
    } else {
      // Question was removed (countdown expired) but backend detected answer
      // Check if it exists in the archive and re-add it as answered
      const archivedAction = state.allSuggestedActions.find((a) => a.id === promptId);
      // Only restore if not already in the list (prevent duplicates)
      const alreadyExists =
        state.suggestedActions.some((a) => a.id === promptId) ||
        state.queuedActions.some((a) => a.id === promptId);
      const alreadyResolved = state.resolvedSuggestions.some((a) => a.id === promptId);
      if (archivedAction && !alreadyExists && !alreadyResolved) {
        const answeredAction = {
          ...archivedAction,
          answerStatus: 'complete' as const,
          answered: true,
          answeredAt,
          answer: answer || archivedAction.answer,
          confidence: confidence !== undefined ? confidence : archivedAction.confidence,
          tierMatched: tierMatched !== undefined ? tierMatched : archivedAction.tierMatched,
          answerReasoning: answerReasoning || archivedAction.answerReasoning,
          partialAnswer: undefined,
          answerSource: answerSource,
          questionParts: undefined,
          answeredParts: answeredParts || archivedAction.answeredParts,
          highlightedQuestion: undefined,
          unansweredParts: undefined,
        };
        // Add it back to the active list as answered
        state.suggestedActions.push(answeredAction);
        logger.info('aiAnalysisSlice', 'Restored removed question as answered', {
          promptId,
          question: archivedAction.details,
        });
      } else if (alreadyResolved) {
        logger.debug('aiAnalysisSlice', 'Skipped restoring already-resolved suggestion', {
          promptId,
          inResolvedSuggestions: true,
        });
      }
    }

    // IMPORTANT: Also update in allSuggestedActions archive
    const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === promptId);
    if (archiveIndex !== -1) {
      state.allSuggestedActions[archiveIndex].answerStatus = 'complete';
      state.allSuggestedActions[archiveIndex].answered = true;
      state.allSuggestedActions[archiveIndex].answeredAt = answeredAt;
      state.allSuggestedActions[archiveIndex].answerSource = answerSource;
      if (answer) {
        state.allSuggestedActions[archiveIndex].answer = answer;
      }
      if (confidence !== undefined) {
        state.allSuggestedActions[archiveIndex].confidence = confidence;
      }
      if (tierMatched !== undefined) {
        state.allSuggestedActions[archiveIndex].tierMatched = tierMatched;
      }
      if (answerReasoning) {
        state.allSuggestedActions[archiveIndex].answerReasoning = answerReasoning;
      }
      if (answeredParts && answeredParts.length > 0) {
        state.allSuggestedActions[archiveIndex].answeredParts = answeredParts;
      }
      // Complete answers render as plain text — clear all highlighting fields.
      clearPartialOnlyAnswerFields(state.allSuggestedActions[archiveIndex]);
    }

    if (actionIndex === -1 && queuedIndex !== -1) {
      archiveCompletedQueuedAction(state, promptId);
    }
  },

  // Undo detection/answered - reverts an item back to unanswered state
  undoDetection: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const promptId = action.payload;
    state.detectedPrompts = state.detectedPrompts.filter((id) => id !== promptId);
    state.answeredPrompts = state.answeredPrompts.filter((id) => id !== promptId);
    removePromptFromPartialState(state, promptId);

    // Clear the answer from the action object
    const actionIndex = state.suggestedActions.findIndex((a) => a.id === promptId);
    if (actionIndex !== -1) {
      state.suggestedActions[actionIndex].answer = undefined;
      state.suggestedActions[actionIndex].answerStatus = undefined;
      state.suggestedActions[actionIndex].answerReason = undefined;
      state.suggestedActions[actionIndex].confidence = undefined;
      state.suggestedActions[actionIndex].answered = undefined;
      state.suggestedActions[actionIndex].answeredAt = undefined;
      clearPartialOnlyAnswerFields(state.suggestedActions[actionIndex]);
    }

    const queuedIndex = state.queuedActions.findIndex((a) => a.id === promptId);
    if (queuedIndex !== -1) {
      state.queuedActions[queuedIndex].answer = undefined;
      state.queuedActions[queuedIndex].answerStatus = undefined;
      state.queuedActions[queuedIndex].answerReason = undefined;
      state.queuedActions[queuedIndex].confidence = undefined;
      state.queuedActions[queuedIndex].answered = undefined;
      state.queuedActions[queuedIndex].answeredAt = undefined;
      clearPartialOnlyAnswerFields(state.queuedActions[queuedIndex]);
    }

    // Also update in allSuggestedActions
    const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === promptId);
    if (archiveIndex !== -1) {
      state.allSuggestedActions[archiveIndex].answer = undefined;
      state.allSuggestedActions[archiveIndex].answerStatus = undefined;
      state.allSuggestedActions[archiveIndex].answerReason = undefined;
      state.allSuggestedActions[archiveIndex].confidence = undefined;
      state.allSuggestedActions[archiveIndex].answered = undefined;
      state.allSuggestedActions[archiveIndex].answeredAt = undefined;
      clearPartialOnlyAnswerFields(state.allSuggestedActions[archiveIndex]);
    }
  },

  // Mark when officer verbally addresses this action (metadata, NOT a gate for answer detection)
  markOfficerAddressed: (
    state: AiAnalysisState,
    action: PayloadAction<{
      promptId: string;
      partial?: boolean;
      officer_quote?: string;
      reason?: string;
    }>,
  ) => {
    const { promptId, partial, officer_quote, reason } = action.payload;
    const timestamp = new Date().toISOString();

    // Update in suggestedActions
    const actionIndex = state.suggestedActions.findIndex((a) => a.id === promptId);
    if (actionIndex !== -1) {
      state.suggestedActions[actionIndex].officerAddressed = true;
      state.suggestedActions[actionIndex].officerAddressedAt = timestamp;
      if (partial !== undefined) {
        state.suggestedActions[actionIndex].officerAddressedPartial = partial;
      }
      if (officer_quote) {
        state.suggestedActions[actionIndex].officerQuote = officer_quote;
      }
      if (reason) {
        state.suggestedActions[actionIndex].officerAddressedReason = reason;
      }
    }

    // Also update in allSuggestedActions archive
    const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === promptId);
    if (archiveIndex !== -1) {
      state.allSuggestedActions[archiveIndex].officerAddressed = true;
      state.allSuggestedActions[archiveIndex].officerAddressedAt = timestamp;
      if (partial !== undefined) {
        state.allSuggestedActions[archiveIndex].officerAddressedPartial = partial;
      }
      if (officer_quote) {
        state.allSuggestedActions[archiveIndex].officerQuote = officer_quote;
      }
      if (reason) {
        state.allSuggestedActions[archiveIndex].officerAddressedReason = reason;
      }
    }

    // Officer-side actions complete when the officer performs them.
    // For `question` type, caller must answer — officer addressing is not completion.
    // For `task` / `suggestion` types, officer addressing IS the completion criterion,
    // so fold them into the normal answered pipeline (countdown → fade → remove).
    const targetAction =
      actionIndex !== -1
        ? state.suggestedActions[actionIndex]
        : archiveIndex !== -1
          ? state.allSuggestedActions[archiveIndex]
          : undefined;
    const isOfficerSideType = targetAction?.type === 'task' || targetAction?.type === 'suggestion';
    if (isOfficerSideType && !partial) {
      if (!state.answeredPrompts.includes(promptId)) {
        state.answeredPrompts.push(promptId);
      }
      // Mirror the partial→complete cleanup done by markPromptAnswered so this
      // item stops leaking into "partial" LLM context builders.
      state.partialPrompts = state.partialPrompts.filter((id) => id !== promptId);
      state.partialSuggestions = state.partialSuggestions.filter((a) => a.id !== promptId);
      state.detectedPrompts = state.detectedPrompts.filter((id) => id !== promptId);
      if (actionIndex !== -1) {
        state.suggestedActions[actionIndex].answerStatus = 'complete';
        state.suggestedActions[actionIndex].answered = true;
        state.suggestedActions[actionIndex].answeredAt = timestamp;
        if (officer_quote && !state.suggestedActions[actionIndex].answer) {
          state.suggestedActions[actionIndex].answer = officer_quote;
        }
        clearPartialOnlyAnswerFields(state.suggestedActions[actionIndex]);
      }
      if (archiveIndex !== -1) {
        state.allSuggestedActions[archiveIndex].answerStatus = 'complete';
        state.allSuggestedActions[archiveIndex].answered = true;
        state.allSuggestedActions[archiveIndex].answeredAt = timestamp;
        if (officer_quote && !state.allSuggestedActions[archiveIndex].answer) {
          state.allSuggestedActions[archiveIndex].answer = officer_quote;
        }
        clearPartialOnlyAnswerFields(state.allSuggestedActions[archiveIndex]);
      }
      const queuedIndex = state.queuedActions.findIndex((a) => a.id === promptId);
      if (queuedIndex !== -1) {
        state.queuedActions[queuedIndex].officerAddressed = true;
        state.queuedActions[queuedIndex].officerAddressedAt = timestamp;
        state.queuedActions[queuedIndex].answerStatus = 'complete';
        state.queuedActions[queuedIndex].answered = true;
        state.queuedActions[queuedIndex].answeredAt = timestamp;
        if (officer_quote && !state.queuedActions[queuedIndex].answer) {
          state.queuedActions[queuedIndex].answer = officer_quote;
        }
        clearPartialOnlyAnswerFields(state.queuedActions[queuedIndex]);
      }
    }

    logger.info('aiAnalysisSlice', 'Officer addressed action', {
      promptId,
      partial: partial || false,
      hasQuote: !!officer_quote,
      hasReason: !!reason,
      completedAsOfficerSide: isOfficerSideType && !partial,
    });
  },

  // ═══════════════════════════════════════════════════════════════════
  // INTERIM STATE MANAGEMENT
  // Fast interim detection updates (from GPT-5-Nano analysis on interim transcripts)
  // Interim state is SEPARATE from final state - final ALWAYS wins
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Update the interim state for a suggested action.
   * Interim state is preliminary detection from fast analysis on interim (non-final) transcripts.
   * This NEVER overwrites final state properties - interim is stored separately.
   */
  updateInterimState: (
    state: AiAnalysisState,
    action: PayloadAction<{
      actionId: string;
      interim: Partial<InterimState>;
    }>,
  ) => {
    const { actionId, interim } = action.payload;
    const normalizedInterim = normalizeInterimUpdate(interim);
    const detectedAt = new Date().toISOString();

    // Update in suggestedActions
    const actionIndex = state.suggestedActions.findIndex((a) => a.id === actionId);
    if (actionIndex !== -1) {
      // Merge with existing interim state
      state.suggestedActions[actionIndex].interim = {
        ...state.suggestedActions[actionIndex].interim,
        ...normalizedInterim,
        detectedAt,
      };
    }

    // Also update in queuedActions
    const queuedIndex = state.queuedActions.findIndex((a) => a.id === actionId);
    if (queuedIndex !== -1) {
      state.queuedActions[queuedIndex].interim = {
        ...state.queuedActions[queuedIndex].interim,
        ...normalizedInterim,
        detectedAt,
      };
    }

    // Also update in allSuggestedActions archive
    const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === actionId);
    if (archiveIndex !== -1) {
      state.allSuggestedActions[archiveIndex].interim = {
        ...state.allSuggestedActions[archiveIndex].interim,
        ...normalizedInterim,
        detectedAt,
      };
    }

    logger.info('aiAnalysisSlice', 'Updated interim state', {
      actionId,
      interim,
    });
  },

  /**
   * Clear the interim state for a suggested action.
   * Called when final state arrives or when interim is no longer valid.
   */
  clearInterimState: (state: AiAnalysisState, action: PayloadAction<{ actionId: string }>) => {
    const { actionId } = action.payload;

    // Clear interim by setting to undefined across all action arrays
    for (const arr of [state.suggestedActions, state.queuedActions, state.allSuggestedActions]) {
      const idx = arr.findIndex((a) => a.id === actionId);
      if (idx !== -1) arr[idx].interim = undefined;
    }

    logger.debug('aiAnalysisSlice', 'Cleared interim state', { actionId });
  },

  // batchUpdateInterimStates, clearInterimOnFinalConflict, clearInterimOnFinal removed
  // — interim is now cleared via clearInterimState dispatched on final arrival

  // Manual or auto-confirm: Mark as answered (green state)
  markPromptAnswered: (
    state: AiAnalysisState,
    action: PayloadAction<
      | string
      | {
          promptId: string;
          answer?: string;
          confidence?: number;
          tierMatched?:
            | 'ideal'
            | 'acceptable'
            | 'minimum'
            | 'negative'
            | 'declined'
            | 'unable'
            | null;
          highlightedQuestion?: string;
          questionParts?: Array<{
            text: string;
            status: 'answered' | 'unanswered' | 'plain';
            partRef?: string;
          }>;
          answeredParts?: Array<{
            part: string;
            answer: string;
            transcriptQuote?: string;
            reasoning?: string;
          }>;
          unansweredParts?: string[];
          answerReasoning?: string;
        }
    >,
  ) => {
    const promptId = typeof action.payload === 'string' ? action.payload : action.payload.promptId;
    const answer = typeof action.payload === 'string' ? undefined : action.payload.answer;
    const confidence = typeof action.payload === 'string' ? undefined : action.payload.confidence;
    const tierMatched = typeof action.payload === 'string' ? undefined : action.payload.tierMatched;
    const highlightedQuestion =
      typeof action.payload === 'string' ? undefined : action.payload.highlightedQuestion;
    const questionParts =
      typeof action.payload === 'string' ? undefined : action.payload.questionParts;
    const answeredParts =
      typeof action.payload === 'string' ? undefined : action.payload.answeredParts;
    const answerReasoning =
      typeof action.payload === 'string' ? undefined : action.payload.answerReasoning;
    const answeredAt = new Date().toISOString();

    if (!state.answeredPrompts.includes(promptId)) {
      state.answeredPrompts.push(promptId);
    }
    // Remove from detected list and partial lists (handles partial → complete transition)
    state.detectedPrompts = state.detectedPrompts.filter((id) => id !== promptId);
    state.partialPrompts = state.partialPrompts.filter((id) => id !== promptId);
    state.partialSuggestions = state.partialSuggestions.filter((a) => a.id !== promptId);

    // Update the action object in suggestedActions
    const actionIndex = state.suggestedActions.findIndex((a) => a.id === promptId);
    const queuedIndex = state.queuedActions.findIndex((a) => a.id === promptId);
    if (actionIndex !== -1) {
      // If upgrading from partial, combine answers
      const existingPartial = state.suggestedActions[actionIndex].partialAnswer;
      const finalAnswer =
        existingPartial && answer
          ? `${existingPartial}; ${answer}`
          : answer || existingPartial || state.suggestedActions[actionIndex].answer;

      state.suggestedActions[actionIndex].answerStatus = 'complete';
      state.suggestedActions[actionIndex].answered = true;
      state.suggestedActions[actionIndex].answeredAt = answeredAt;
      if (finalAnswer) {
        state.suggestedActions[actionIndex].answer = finalAnswer;
      }
      // Store confidence and reasoning for complete answers
      if (confidence !== undefined) {
        state.suggestedActions[actionIndex].confidence = confidence;
      }
      if (tierMatched !== undefined) {
        state.suggestedActions[actionIndex].tierMatched = tierMatched;
      }
      if (answerReasoning) {
        state.suggestedActions[actionIndex].answerReasoning = answerReasoning;
      }
      if (answeredParts && answeredParts.length > 0) {
        state.suggestedActions[actionIndex].answeredParts = answeredParts;
      }
      // Complete answers render as plain text — clear all highlighting fields.
      clearPartialOnlyAnswerFields(state.suggestedActions[actionIndex]);
    } else if (queuedIndex !== -1) {
      const existingPartial = state.queuedActions[queuedIndex].partialAnswer;
      const finalAnswer =
        existingPartial && answer
          ? `${existingPartial}; ${answer}`
          : answer || existingPartial || state.queuedActions[queuedIndex].answer;

      state.queuedActions[queuedIndex].answerStatus = 'complete';
      state.queuedActions[queuedIndex].answered = true;
      state.queuedActions[queuedIndex].answeredAt = answeredAt;
      if (finalAnswer) {
        state.queuedActions[queuedIndex].answer = finalAnswer;
      }
      if (confidence !== undefined) {
        state.queuedActions[queuedIndex].confidence = confidence;
      }
      if (tierMatched !== undefined) {
        state.queuedActions[queuedIndex].tierMatched = tierMatched;
      }
      if (answerReasoning) {
        state.queuedActions[queuedIndex].answerReasoning = answerReasoning;
      }
      if (answeredParts && answeredParts.length > 0) {
        state.queuedActions[queuedIndex].answeredParts = answeredParts;
      }
      clearPartialOnlyAnswerFields(state.queuedActions[queuedIndex]);
    } else {
      // Question was removed (countdown expired) but backend detected it was answered
      // Check if it exists in the archive and re-add it as answered
      const archivedAction = state.allSuggestedActions.find((a) => a.id === promptId);
      // Only restore if not already in the list (prevent duplicates)
      const alreadyExists =
        state.suggestedActions.some((a) => a.id === promptId) ||
        state.queuedActions.some((a) => a.id === promptId);
      // CRITICAL: Never restore items that completed the full lifecycle
      // (answered -> countdown -> archived to resolvedSuggestions)
      // These items have already been properly completed and removed - don't resurrect them
      const alreadyResolved = state.resolvedSuggestions.some((a) => a.id === promptId);

      if (archivedAction && !alreadyExists && !alreadyResolved) {
        const existingPartial = archivedAction.partialAnswer;
        const finalAnswer =
          existingPartial && answer
            ? `${existingPartial}; ${answer}`
            : answer || existingPartial || archivedAction.answer;

        const answeredAction = {
          ...archivedAction,
          answerStatus: 'complete' as const,
          answered: true,
          answeredAt,
          answer: finalAnswer,
          confidence,
          tierMatched,
          answerReasoning,
          partialAnswer: undefined,
          questionParts: undefined,
          answeredParts,
          highlightedQuestion: undefined,
          unansweredParts: undefined,
        };
        // Add it back to the active list as answered
        state.suggestedActions.push(answeredAction);
        logger.info('aiAnalysisSlice', 'Restored removed question as answered', {
          promptId,
          question: archivedAction.details,
        });
      } else if (alreadyResolved) {
        // Item completed its full lifecycle - just update archive, don't restore to UI
        logger.debug('aiAnalysisSlice', 'Skipped restoring already-resolved suggestion', {
          promptId,
          inResolvedSuggestions: true,
        });
      }
    }

    // IMPORTANT: Also update in allSuggestedActions archive (for filtering duplicates)
    const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === promptId);
    if (archiveIndex !== -1) {
      const existingPartial = state.allSuggestedActions[archiveIndex].partialAnswer;
      const finalAnswer =
        existingPartial && answer
          ? `${existingPartial}; ${answer}`
          : answer || existingPartial || state.allSuggestedActions[archiveIndex].answer;

      state.allSuggestedActions[archiveIndex].answerStatus = 'complete';
      state.allSuggestedActions[archiveIndex].answered = true;
      state.allSuggestedActions[archiveIndex].answeredAt = answeredAt;
      if (finalAnswer) {
        state.allSuggestedActions[archiveIndex].answer = finalAnswer;
      }
      if (confidence !== undefined) {
        state.allSuggestedActions[archiveIndex].confidence = confidence;
      }
      if (tierMatched !== undefined) {
        state.allSuggestedActions[archiveIndex].tierMatched = tierMatched;
      }
      if (answerReasoning) {
        state.allSuggestedActions[archiveIndex].answerReasoning = answerReasoning;
      }
      if (answeredParts && answeredParts.length > 0) {
        state.allSuggestedActions[archiveIndex].answeredParts = answeredParts;
      }
      clearPartialOnlyAnswerFields(state.allSuggestedActions[archiveIndex]);
    }

    if (actionIndex === -1 && queuedIndex !== -1) {
      archiveCompletedQueuedAction(state, promptId);
    }

    // Check queue and current actions for similar questions
    const resolvedAction =
      actionIndex !== -1
        ? state.suggestedActions[actionIndex]
        : state.resolvedSuggestions.find((a) => a.id === promptId);

    if (resolvedAction) {
      // Check queue for similar questions and remove them
      const answeredDetails = resolvedAction.details;
      const queueLengthBefore = state.queuedActions.length;

      // Exact-match-only dedup. Semantic similarity is handled by the backend
      // LLM dedup (/dedupe/check) — NO regex/pattern matching on the frontend.
      const areSimilar = (text1: string, text2: string): boolean => {
        const normalize = (text: string) =>
          text
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        return normalize(text1) === normalize(text2);
      };

      // Filter out similar questions/tasks from queue
      state.queuedActions = state.queuedActions.filter((queuedAction) => {
        const isSimilar = areSimilar(answeredDetails, queuedAction.details);
        if (isSimilar) {
          logger.info('aiAnalysisSlice', 'Removed similar question from queue', {
            answeredQuestion: answeredDetails,
            removedFromQueue: queuedAction.details,
          });
        }
        return !isSimilar;
      });

      const removedCount = queueLengthBefore - state.queuedActions.length;
      if (removedCount > 0) {
        logger.info('aiAnalysisSlice', 'Queue cleanup after answer', {
          answeredPromptId: promptId,
          answeredDetails,
          removedFromQueueCount: removedCount,
          queueLengthBefore,
          queueLengthAfter: state.queuedActions.length,
        });
      }
    }
  },

  // Start fade out animation
  startFadeOut: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const promptId = action.payload;
    if (!state.fadingPrompts.includes(promptId)) {
      state.fadingPrompts.push(promptId);
    }
  },

  // Remove prompt completely after fade
  removePrompt: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const promptId = action.payload;
    const oldOrder = getActiveActionOrder(state);

    // Find the action before removing it
    const actionToArchive =
      state.suggestedActions.find((a) => a.id === promptId) ||
      state.queuedActions.find((a) => a.id === promptId);

    // Add to allSuggestedActions archive if not already there
    if (actionToArchive && !state.allSuggestedActions.find((a) => a.id === promptId)) {
      state.allSuggestedActions.push(actionToArchive);
    }

    // If the action was answered, add it to resolvedSuggestions
    if (actionToArchive && state.answeredPrompts.includes(promptId)) {
      // Check if not already in resolved suggestions
      if (!state.resolvedSuggestions.find((a) => a.id === promptId)) {
        state.resolvedSuggestions.unshift(actionToArchive); // Add to beginning
      }
    }

    // Add to discardedSuggestions so backend knows not to re-detect this item.
    // Maps to backend's discardedActions, used by _populate_alert_signatures().
    if (actionToArchive && !state.discardedSuggestions.find((a) => a.id === promptId)) {
      state.discardedSuggestions.push(actionToArchive);
    }

    state.suggestedActions = state.suggestedActions.filter((a) => a.id !== promptId);
    state.queuedActions = state.queuedActions.filter((a) => a.id !== promptId); // Also remove from queue!
    state.fadingPrompts = state.fadingPrompts.filter((id) => id !== promptId);
    // Keep answeredPrompts - we need it for filtering in incident report
    // state.answeredPrompts = state.answeredPrompts.filter(id => id !== promptId);
    state.detectedPrompts = state.detectedPrompts.filter((id) => id !== promptId);
    state.failedPrompts = state.failedPrompts.filter((id) => id !== promptId);

    resortVisibleAndQueuedActions(state, oldOrder);
  },

  // Toggle lock state for a prompt (prevents auto-disappearing)
  toggleLockPrompt: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const promptId = action.payload;
    const index = state.lockedPrompts.indexOf(promptId);

    if (index === -1) {
      // Lock it
      state.lockedPrompts.push(promptId);
    } else {
      // Unlock it
      state.lockedPrompts.splice(index, 1);
    }
  },

  // De-prioritize a prompt (push below the current top 10)
  deprioritizePrompt: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const promptId = action.payload;
    const now = Date.now();
    const oldOrder = [...state.suggestedActions, ...state.queuedActions].map((a) => a.id);
    const allActions = [...state.suggestedActions, ...state.queuedActions];
    const target = allActions.find((item) => item.id === promptId);

    if (!target) {
      const archiveIndex = state.allSuggestedActions.findIndex((item) => item.id === promptId);
      if (archiveIndex !== -1) {
        state.allSuggestedActions[archiveIndex].userDeprioritized = true;
        state.allSuggestedActions[archiveIndex].userDeprioritizedAt = now;
      }
      logger.warn('aiAnalysisSlice', 'deprioritizePrompt: Action not found in active lists', {
        promptId,
      });
      return;
    }

    const previousPriority = clampPriority(target.priority);
    const newPriority = getDeprioritizedPriority(allActions, previousPriority);

    const applyUpdate = (items: SuggestedAction[]): boolean => {
      const index = items.findIndex((item) => item.id === promptId);
      if (index === -1) return false;
      items[index].priority = newPriority;
      items[index].userDeprioritized = true;
      items[index].userDeprioritizedAt = now;
      return true;
    };

    const updatedActive = applyUpdate(state.suggestedActions) || applyUpdate(state.queuedActions);
    applyUpdate(state.allSuggestedActions);

    if (!updatedActive) {
      logger.warn('aiAnalysisSlice', 'deprioritizePrompt: Action only found in archive', {
        promptId,
      });
      return;
    }

    const sortedActions = [...state.suggestedActions, ...state.queuedActions].sort(
      (a, b) => clampPriority(a.priority) - clampPriority(b.priority),
    );
    const { visible, queued } = splitActionsByDisplayCaps(sortedActions);
    state.suggestedActions = visible;
    state.queuedActions = queued;

    const newOrder = [...state.suggestedActions, ...state.queuedActions].map((a) => a.id);
    updatePriorityChangeTimesForOrder(state, oldOrder, newOrder, now);
    state.lastPriorityChangeTimes[promptId] = now;
    state.displayOrder = state.suggestedActions.map((a) => a.id);
    state.lastItemsChangeTime = now;

    logger.info('aiAnalysisSlice', 'deprioritizePrompt: Deprioritized action', {
      promptId,
      previousPriority,
      newPriority,
    });
  },

  // Mark prompt as failed (manual mark but no answer found)
  markPromptFailed: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const promptId = action.payload;
    if (!state.failedPrompts.includes(promptId)) {
      state.failedPrompts.push(promptId);
    }
  },

  // Clear failed state for a prompt
  clearPromptFailed: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const promptId = action.payload;
    state.failedPrompts = state.failedPrompts.filter((id) => id !== promptId);
  },

  // Mark prompt as partially answered
  markPromptPartial: (
    state: AiAnalysisState,
    action: PayloadAction<{
      promptId: string;
      partialAnswer: string;
      highlightedQuestion: string;
      confidence?: number;
      answeredParts?:
        | Array<{ part: string; answer: string; transcriptQuote?: string; reasoning?: string }>
        | string[]; // Support both new and legacy format
      unansweredParts?: string[];
      questionParts?: Array<{
        text: string;
        status: 'answered' | 'unanswered' | 'plain';
        partRef?: string;
      }>;
      answerReason?: string; // Why the AI detected this answer (overall reasoning)
    }>,
  ) => {
    const {
      promptId,
      partialAnswer,
      highlightedQuestion,
      confidence,
      answeredParts,
      unansweredParts,
      questionParts,
      answerReason,
    } = action.payload;

    // CRITICAL: Never allow partial to override an answered prompt
    // This prevents race conditions where late partial responses arrive after the prompt was marked complete
    if (state.answeredPrompts.includes(promptId)) {
      logger.warn('aiAnalysisSlice', 'Blocked partial answer for already-answered prompt', {
        promptId: promptId,
      });
      return;
    }

    // Helper to normalize answeredParts to new format
    const normalizeAnsweredParts = (
      parts:
        | Array<{ part: string; answer: string; transcriptQuote?: string; reasoning?: string }>
        | string[]
        | undefined,
    ):
      | Array<{ part: string; answer: string; transcriptQuote?: string; reasoning?: string }>
      | undefined => {
      if (!parts || parts.length === 0) return undefined;
      // Check if it's already in new format
      if (typeof parts[0] === 'object' && 'part' in parts[0]) {
        return parts as Array<{
          part: string;
          answer: string;
          transcriptQuote?: string;
          reasoning?: string;
        }>;
      }
      // Legacy string array - convert to new format (answer will be empty, populated from partialAnswer if possible)
      return (parts as string[]).map((p) => ({ part: p, answer: '' }));
    };

    const normalizedParts = normalizeAnsweredParts(answeredParts);
    const partialCandidate: SuggestedAction = {
      ...(findActionById(state, promptId) || {
        id: promptId,
        type: 'question',
        details: '',
      }),
      answerStatus: 'partial',
      partialAnswer,
      highlightedQuestion,
      answeredParts: normalizedParts,
      unansweredParts,
      questionParts,
    };

    if (!hasValidPartialAnswerShape(partialCandidate)) {
      const existing = findActionById(state, promptId);
      // Sticky partial: once an action is ESTABLISHED as partial (answerStatus
      // 'partial' or already tracked in partialPrompts), a later turn whose
      // highlight frame is momentarily incomplete must NOT clear it — the
      // backend still considers it partial and the shape fluctuates turn to
      // turn. Only clear a partial that was never established. This stops the
      // partial card blinking in/out of the officer's queue.
      const alreadyEstablishedPartial =
        existing?.answerStatus === 'partial' || state.partialPrompts.includes(promptId);
      if (!alreadyEstablishedPartial) {
        clearInvalidPartialState(state, promptId);
      }
      logger.warn('aiAnalysisSlice', 'Rejected invalid partial answer shape', {
        promptId,
        answeredParts: normalizedParts?.length || 0,
        unansweredParts: unansweredParts?.length || 0,
        questionParts: questionParts?.map((part) => part.status) || [],
        hasHighlightedQuestion: Boolean(highlightedQuestion),
      });
      return;
    }

    // Add to partial prompts if not already there
    if (!state.partialPrompts.includes(promptId)) {
      state.partialPrompts.push(promptId);
    }

    // Remove from other states
    state.detectedPrompts = state.detectedPrompts.filter((id) => id !== promptId);
    state.failedPrompts = state.failedPrompts.filter((id) => id !== promptId);

    // Officer-addressed flags survive partial verdicts (owner decision 2026-07-23;
    // see the note above the removed clearOfficerAddressedForPrompt helper).

    // No-op guard: compare new array/object values to current ones via JSON
    // string. Only assign when actually changed. Without this, every dispatch
    // creates a new array/object reference even when content is identical,
    // causing consumers (action card highlighting render) to flicker.
    const sameJson = (a: unknown, b: unknown) => {
      if (a === b) return true;
      try {
        return JSON.stringify(a) === JSON.stringify(b);
      } catch {
        return false;
      }
    };

    // Update the action in suggestedActions
    const actionIndex = state.suggestedActions.findIndex((a) => a.id === promptId);
    if (actionIndex !== -1) {
      const tgt = state.suggestedActions[actionIndex];
      if (tgt.answerStatus !== 'partial') tgt.answerStatus = 'partial';
      if (tgt.partialAnswer !== partialAnswer) tgt.partialAnswer = partialAnswer;
      if (tgt.highlightedQuestion !== highlightedQuestion)
        tgt.highlightedQuestion = highlightedQuestion;
      if (confidence !== undefined && tgt.confidence !== confidence) {
        tgt.confidence = confidence;
      }
      if (normalizedParts && !sameJson(tgt.answeredParts, normalizedParts)) {
        tgt.answeredParts = normalizedParts;
      }
      if (unansweredParts && !sameJson(tgt.unansweredParts, unansweredParts)) {
        tgt.unansweredParts = unansweredParts;
      }
      if (questionParts && !sameJson(tgt.questionParts, questionParts)) {
        tgt.questionParts = questionParts;
      }
      if (answerReason && tgt.answerReasoning !== answerReason) {
        tgt.answerReasoning = answerReason;
      }

      const partialIndex = state.partialSuggestions.findIndex((a) => a.id === promptId);
      if (partialIndex !== -1) {
        state.partialSuggestions[partialIndex] = { ...state.suggestedActions[actionIndex] };
      } else {
        state.partialSuggestions.unshift({ ...state.suggestedActions[actionIndex] });
      }
    }

    // Also update in allSuggestedActions archive
    const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === promptId);
    if (archiveIndex !== -1) {
      state.allSuggestedActions[archiveIndex].answerStatus = 'partial';
      state.allSuggestedActions[archiveIndex].partialAnswer = partialAnswer;
      state.allSuggestedActions[archiveIndex].highlightedQuestion = highlightedQuestion;
      if (confidence !== undefined) {
        state.allSuggestedActions[archiveIndex].confidence = confidence;
      }
      if (normalizedParts) {
        state.allSuggestedActions[archiveIndex].answeredParts = normalizedParts;
      }
      if (unansweredParts) {
        state.allSuggestedActions[archiveIndex].unansweredParts = unansweredParts;
      }
      if (questionParts) {
        state.allSuggestedActions[archiveIndex].questionParts = questionParts;
      }
      if (answerReason) {
        state.allSuggestedActions[archiveIndex].answerReasoning = answerReason;
      }
    }

    logger.info('aiAnalysisSlice', 'Marked prompt as partial', {
      promptId,
      partialAnswer: partialAnswer.substring(0, 100),
      highlightedQuestion: highlightedQuestion.substring(0, 100),
      confidence,
      answerReason,
    });
  },

  // Mark prompt as a closure tier - caller declined or was unable to provide information.
  // This sets answerStatus='complete' and preserves the exact closure tier.
  // NOTE: Tasks (type='task') cannot be declined - only questions can be declined.
  markPromptNegativeTier: (
    state: AiAnalysisState,
    action: PayloadAction<{
      promptId: string;
      reason?: string;
      confidence?: number;
      tierMatched?: 'negative' | 'declined' | 'unable';
    }>,
  ) => {
    const { promptId, reason, confidence, tierMatched = 'negative' } = action.payload;
    const answeredAt = new Date().toISOString();

    // Find the action first to check its type
    const actionIndex = state.suggestedActions.findIndex((a) => a.id === promptId);
    const queuedIndex = state.queuedActions.findIndex((a) => a.id === promptId);
    const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === promptId);

    // Get the action from whichever list contains it
    const foundAction =
      actionIndex !== -1
        ? state.suggestedActions[actionIndex]
        : queuedIndex !== -1
          ? state.queuedActions[queuedIndex]
          : archiveIndex !== -1
            ? state.allSuggestedActions[archiveIndex]
            : null;

    // Tasks cannot be declined - only questions can be declined
    if (foundAction?.type === 'task') {
      logger.warn(
        'aiAnalysisSlice',
        'Cannot mark task as negative tier - only questions can be declined',
        {
          promptId,
          type: foundAction.type,
        },
      );
      return;
    }

    // Remove from partial prompts (negative tier is a form of complete)
    state.partialPrompts = state.partialPrompts.filter((id) => id !== promptId);
    state.partialSuggestions = state.partialSuggestions.filter((a) => a.id !== promptId);
    state.detectedPrompts = state.detectedPrompts.filter((id) => id !== promptId);

    // Add to answeredPrompts since negative tier = complete
    if (!state.answeredPrompts.includes(promptId)) {
      state.answeredPrompts.push(promptId);
    }

    // Update in suggestedActions
    if (actionIndex !== -1) {
      state.suggestedActions[actionIndex].answerStatus = 'complete';
      state.suggestedActions[actionIndex].tierMatched = tierMatched;
      state.suggestedActions[actionIndex].answered = true; // Deprecated field for compatibility
      state.suggestedActions[actionIndex].answeredAt = answeredAt;
      // Clear any interim state since we have a definitive final state
      state.suggestedActions[actionIndex].interim = undefined;
      if (reason) {
        state.suggestedActions[actionIndex].answerReasoning = reason;
        state.suggestedActions[actionIndex].answer = reason;
      }
      if (confidence !== undefined) {
        state.suggestedActions[actionIndex].confidence = confidence;
      }
      clearPartialOnlyAnswerFields(state.suggestedActions[actionIndex]);
    }

    // Update in queuedActions
    if (queuedIndex !== -1) {
      state.queuedActions[queuedIndex].answerStatus = 'complete';
      state.queuedActions[queuedIndex].tierMatched = tierMatched;
      state.queuedActions[queuedIndex].answered = true;
      state.queuedActions[queuedIndex].answeredAt = answeredAt;
      state.queuedActions[queuedIndex].interim = undefined;
      if (reason) {
        state.queuedActions[queuedIndex].answerReasoning = reason;
        state.queuedActions[queuedIndex].answer = reason;
      }
      if (confidence !== undefined) {
        state.queuedActions[queuedIndex].confidence = confidence;
      }
      clearPartialOnlyAnswerFields(state.queuedActions[queuedIndex]);
    }

    // Update in allSuggestedActions archive
    if (archiveIndex !== -1) {
      state.allSuggestedActions[archiveIndex].answerStatus = 'complete';
      state.allSuggestedActions[archiveIndex].tierMatched = tierMatched;
      state.allSuggestedActions[archiveIndex].answered = true;
      state.allSuggestedActions[archiveIndex].answeredAt = answeredAt;
      state.allSuggestedActions[archiveIndex].interim = undefined;
      if (reason) {
        state.allSuggestedActions[archiveIndex].answerReasoning = reason;
        state.allSuggestedActions[archiveIndex].answer = reason;
      }
      if (confidence !== undefined) {
        state.allSuggestedActions[archiveIndex].confidence = confidence;
      }
      clearPartialOnlyAnswerFields(state.allSuggestedActions[archiveIndex]);
    }

    if (actionIndex === -1 && queuedIndex !== -1) {
      archiveCompletedQueuedAction(state, promptId);
    }

    logger.info('aiAnalysisSlice', 'Marked prompt as closure tier completion', {
      promptId,
      reason,
      confidence,
      tierMatched,
      type: foundAction?.type,
    });
  },

  // Update partial answer with latest information (replaces previous partial answer)
  appendToPartialAnswer: (
    state: AiAnalysisState,
    action: PayloadAction<{
      promptId: string;
      newPartialAnswer: string;
      newHighlightedQuestion: string;
      updatedAnsweredParts?:
        | Array<{ part: string; answer: string; transcriptQuote?: string; reasoning?: string }>
        | string[];
      updatedUnansweredParts?: string[];
      updatedQuestionParts?: Array<{
        text: string;
        status: 'answered' | 'unanswered' | 'plain';
        partRef?: string;
      }>;
      answerReason?: string;
    }>,
  ) => {
    const {
      promptId,
      newPartialAnswer,
      newHighlightedQuestion,
      updatedAnsweredParts,
      updatedUnansweredParts,
      updatedQuestionParts,
      answerReason,
    } = action.payload;

    // CRITICAL: Never allow partial update to override an answered prompt
    // This prevents race conditions where late partial responses arrive after the prompt was marked complete
    if (state.answeredPrompts.includes(promptId)) {
      logger.warn('aiAnalysisSlice', 'Blocked appendToPartialAnswer for already-answered prompt', {
        promptId: promptId,
      });
      return;
    }

    // Helper to normalize answeredParts to new format
    const normalizeAnsweredParts = (
      parts:
        | Array<{ part: string; answer: string; transcriptQuote?: string; reasoning?: string }>
        | string[]
        | undefined,
    ):
      | Array<{ part: string; answer: string; transcriptQuote?: string; reasoning?: string }>
      | undefined => {
      if (!parts || parts.length === 0) return undefined;
      if (typeof parts[0] === 'object' && 'part' in parts[0]) {
        return parts as Array<{
          part: string;
          answer: string;
          transcriptQuote?: string;
          reasoning?: string;
        }>;
      }
      return (parts as string[]).map((p) => ({ part: p, answer: '' }));
    };

    const normalizedParts = normalizeAnsweredParts(updatedAnsweredParts);
    const partialCandidate: SuggestedAction = {
      ...(findActionById(state, promptId) || {
        id: promptId,
        type: 'question',
        details: '',
      }),
      answerStatus: 'partial',
      partialAnswer: newPartialAnswer,
      highlightedQuestion: newHighlightedQuestion,
      answeredParts: normalizedParts,
      unansweredParts: updatedUnansweredParts,
      questionParts: updatedQuestionParts,
    };

    if (!hasValidPartialAnswerShape(partialCandidate)) {
      const existing = findActionById(state, promptId);
      // Sticky partial: once an action is ESTABLISHED as partial (answerStatus
      // 'partial' or already tracked in partialPrompts), a later turn whose
      // highlight frame is momentarily incomplete must NOT clear it — the
      // backend still considers it partial and the shape fluctuates turn to
      // turn. Only clear a partial that was never established. This stops the
      // partial card blinking in/out of the officer's queue.
      const alreadyEstablishedPartial =
        existing?.answerStatus === 'partial' || state.partialPrompts.includes(promptId);
      if (!alreadyEstablishedPartial) {
        clearInvalidPartialState(state, promptId);
      }
      logger.warn('aiAnalysisSlice', 'Rejected invalid partial answer update shape', {
        promptId,
        answeredParts: normalizedParts?.length || 0,
        unansweredParts: updatedUnansweredParts?.length || 0,
        questionParts: updatedQuestionParts?.map((part) => part.status) || [],
        hasHighlightedQuestion: Boolean(newHighlightedQuestion),
      });
      return;
    }

    if (!state.partialPrompts.includes(promptId)) {
      state.partialPrompts.push(promptId);
    }

    // Officer-addressed flags survive partial verdicts (owner decision 2026-07-23;
    // see the note above the removed clearOfficerAddressedForPrompt helper).

    // Update the action in suggestedActions
    const actionIndex = state.suggestedActions.findIndex((a) => a.id === promptId);
    if (actionIndex !== -1) {
      // REPLACE the partial answer (not append) - the new answer should be a comprehensive summary
      state.suggestedActions[actionIndex].answerStatus = 'partial';
      state.suggestedActions[actionIndex].partialAnswer = newPartialAnswer;
      state.suggestedActions[actionIndex].highlightedQuestion = newHighlightedQuestion;
      if (normalizedParts) {
        state.suggestedActions[actionIndex].answeredParts = normalizedParts;
      }
      if (updatedUnansweredParts) {
        state.suggestedActions[actionIndex].unansweredParts = updatedUnansweredParts;
      }
      if (updatedQuestionParts) {
        state.suggestedActions[actionIndex].questionParts = updatedQuestionParts;
      }
      if (answerReason) {
        state.suggestedActions[actionIndex].answerReasoning = answerReason;
      }

      const partialIndex = state.partialSuggestions.findIndex((a) => a.id === promptId);
      if (partialIndex !== -1) {
        state.partialSuggestions[partialIndex] = { ...state.suggestedActions[actionIndex] };
      } else {
        state.partialSuggestions.unshift({ ...state.suggestedActions[actionIndex] });
      }
    }

    // Also update in allSuggestedActions archive
    const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === promptId);
    if (archiveIndex !== -1) {
      // REPLACE the partial answer (not append)
      state.allSuggestedActions[archiveIndex].answerStatus = 'partial';
      state.allSuggestedActions[archiveIndex].partialAnswer = newPartialAnswer;
      state.allSuggestedActions[archiveIndex].highlightedQuestion = newHighlightedQuestion;
      if (normalizedParts) {
        state.allSuggestedActions[archiveIndex].answeredParts = normalizedParts;
      }
      if (updatedUnansweredParts) {
        state.allSuggestedActions[archiveIndex].unansweredParts = updatedUnansweredParts;
      }
      if (updatedQuestionParts) {
        state.allSuggestedActions[archiveIndex].questionParts = updatedQuestionParts;
      }
      if (answerReason) {
        state.allSuggestedActions[archiveIndex].answerReasoning = answerReason;
      }
    }

    logger.info('aiAnalysisSlice', 'Updated partial answer', {
      promptId,
      newPartialAnswer: newPartialAnswer.substring(0, 100),
      newHighlightedQuestion: newHighlightedQuestion.substring(0, 100),
      answerReason,
    });
  },

  // Update an already-answered action's answer with a fresher comprehensive
  // summary (extend) or a corrected answer. Written across every array that
  // holds a copy of the action so the AnsweredQuestionsCarousel (sourced from
  // resolvedSuggestions) reflects the change. Intentionally separate from
  // `appendToPartialAnswer`, which guards against writes to answeredPrompts.
  extendAnswer: (
    state: AiAnalysisState,
    action: PayloadAction<{
      promptId: string;
      answer: string;
      answeredParts?: Array<{
        part: string;
        answer: string;
        transcriptQuote?: string;
        reasoning?: string;
      }>;
      unansweredParts?: string[];
      answerReasoning?: string;
      corrected?: boolean;
    }>,
  ) => {
    const { promptId, answer, answeredParts, unansweredParts, answerReasoning, corrected } =
      action.payload;

    const applyUpdate = (target: SuggestedAction | undefined) => {
      if (!target) return;
      target.answer = answer;
      if (answeredParts) target.answeredParts = answeredParts;
      if (unansweredParts && target.answerStatus !== 'complete' && target.answered !== true) {
        target.unansweredParts = unansweredParts;
      }
      if (answerReasoning) target.answerReasoning = answerReasoning;
      if (target.answerStatus === 'complete' || target.answered === true) {
        clearPartialOnlyAnswerFields(target);
      }
      if (corrected) {
        (target as SuggestedAction & { correctedAt?: string }).correctedAt =
          new Date().toISOString();
      }
    };

    applyUpdate(state.suggestedActions.find((a) => a.id === promptId));
    applyUpdate(state.queuedActions.find((a) => a.id === promptId));
    applyUpdate(state.allSuggestedActions.find((a) => a.id === promptId));
    applyUpdate(state.resolvedSuggestions.find((a) => a.id === promptId));
    applyUpdate(state.discardedSuggestions.find((a) => a.id === promptId));

    logger.info('aiAnalysisSlice', 'extendAnswer applied', {
      promptId,
      corrected: !!corrected,
      answerLength: answer.length,
    });
  },

  // Set improvement patch for unanswered/partial questions
  // This stores the LLM-generated suggestion for how to rephrase the question
  setImprovementPatch: (
    state: AiAnalysisState,
    action: PayloadAction<{
      promptId: string;
      patch: {
        improvedQuestion: string;
        strategy: string;
        reason: string;
        splitQuestions?: string[];
      };
    }>,
  ) => {
    const { promptId, patch } = action.payload;

    // Update in suggestedActions
    const actionIndex = state.suggestedActions.findIndex((a) => a.id === promptId);
    if (actionIndex !== -1) {
      state.suggestedActions[actionIndex].improvementPatch = patch;
    }

    // Update in queuedActions
    const queuedIndex = state.queuedActions.findIndex((a) => a.id === promptId);
    if (queuedIndex !== -1) {
      state.queuedActions[queuedIndex].improvementPatch = patch;
    }

    // Update in partialSuggestions
    const partialIndex = state.partialSuggestions.findIndex((a) => a.id === promptId);
    if (partialIndex !== -1) {
      state.partialSuggestions[partialIndex].improvementPatch = patch;
    }

    // Update in allSuggestedActions archive
    const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === promptId);
    if (archiveIndex !== -1) {
      state.allSuggestedActions[archiveIndex].improvementPatch = patch;
    }

    logger.info('aiAnalysisSlice', 'Set improvement patch', {
      promptId,
      strategy: patch.strategy,
      improvedQuestion: patch.improvedQuestion.substring(0, 100),
    });
  },

  // Upgrade partial answer to complete
  upgradePartialToComplete: (
    state: AiAnalysisState,
    action: PayloadAction<{
      promptId: string;
      additionalAnswer?: string;
    }>,
  ) => {
    const { promptId, additionalAnswer } = action.payload;

    // Remove from partial lists
    state.partialPrompts = state.partialPrompts.filter((id) => id !== promptId);
    state.partialSuggestions = state.partialSuggestions.filter((a) => a.id !== promptId);

    // Add to answered prompts
    if (!state.answeredPrompts.includes(promptId)) {
      state.answeredPrompts.push(promptId);
    }

    // Update the action in suggestedActions
    const actionIndex = state.suggestedActions.findIndex((a) => a.id === promptId);
    if (actionIndex !== -1) {
      const existingPartial = state.suggestedActions[actionIndex].partialAnswer || '';
      const fullAnswer = additionalAnswer
        ? `${existingPartial}; ${additionalAnswer}`
        : existingPartial;

      state.suggestedActions[actionIndex].answerStatus = 'complete';
      state.suggestedActions[actionIndex].answered = true;
      state.suggestedActions[actionIndex].answer = fullAnswer;
      state.suggestedActions[actionIndex].answeredAt = new Date().toISOString();
      // Clear partial-only fields — complete answers render as plain text.
      clearPartialOnlyAnswerFields(state.suggestedActions[actionIndex]);
    }

    // Also update in allSuggestedActions archive
    const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === promptId);
    if (archiveIndex !== -1) {
      const existingPartial = state.allSuggestedActions[archiveIndex].partialAnswer || '';
      const fullAnswer = additionalAnswer
        ? `${existingPartial}; ${additionalAnswer}`
        : existingPartial;

      state.allSuggestedActions[archiveIndex].answerStatus = 'complete';
      state.allSuggestedActions[archiveIndex].answered = true;
      state.allSuggestedActions[archiveIndex].answer = fullAnswer;
      state.allSuggestedActions[archiveIndex].answeredAt = new Date().toISOString();
      clearPartialOnlyAnswerFields(state.allSuggestedActions[archiveIndex]);
    }

    logger.info('aiAnalysisSlice', 'Upgraded partial to complete', {
      promptId,
      additionalAnswer: additionalAnswer?.substring(0, 100),
    });
  },

  // Force remove a prompt immediately (double-click)
  forceRemovePrompt: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const promptId = action.payload;
    const previousSuggestedIds = new Set(state.suggestedActions.map((a) => a.id));

    // Find the action before removing it
    const actionToArchive = state.suggestedActions.find((a) => a.id === promptId);

    // Add to allSuggestedActions archive if not already there
    if (actionToArchive && !state.allSuggestedActions.find((a) => a.id === promptId)) {
      state.allSuggestedActions.push(actionToArchive);
    }

    // Add to removedSuggestions for the "Removed" tab (unshift for newest-first ordering)
    if (actionToArchive && !state.removedSuggestions.find((a) => a.id === promptId)) {
      state.removedSuggestions.unshift({
        ...actionToArchive,
        answeredAt: new Date().toISOString(), // Track when it was removed
      });
    }

    // Add to discardedSuggestions so backend knows not to re-detect this item.
    // discardedSuggestions maps to backend's discardedActions field, which is used
    // by _populate_alert_signatures() to prevent re-triggering the same alerts.
    if (actionToArchive && !state.discardedSuggestions.find((a) => a.id === promptId)) {
      state.discardedSuggestions.push(actionToArchive);
    }

    // Remove from all lists
    state.suggestedActions = state.suggestedActions.filter((a) => a.id !== promptId);
    state.queuedActions = state.queuedActions.filter((a) => a.id !== promptId); // Also remove from queue!
    state.partialSuggestions = state.partialSuggestions.filter((a) => a.id !== promptId);
    state.fadingPrompts = state.fadingPrompts.filter((id) => id !== promptId);
    state.detectedPrompts = state.detectedPrompts.filter((id) => id !== promptId);
    state.answeredPrompts = state.answeredPrompts.filter((id) => id !== promptId);
    state.partialPrompts = state.partialPrompts.filter((id) => id !== promptId);
    state.lockedPrompts = state.lockedPrompts.filter((id) => id !== promptId);
    state.failedPrompts = state.failedPrompts.filter((id) => id !== promptId);

    const combinedActions = [...state.suggestedActions, ...state.queuedActions];
    const sortedActions = [...combinedActions].sort(
      (a, b) => (a.priority ?? 50) - (b.priority ?? 50),
    );
    const { visible, queued } = splitActionsByDisplayCaps(sortedActions);
    state.suggestedActions = visible;
    state.queuedActions = queued;

    // Initialize timestamps for promoted items (enables 60s stability lock)
    const now = Date.now();
    state.suggestedActions.forEach((item) => {
      if (!previousSuggestedIds.has(item.id) && !(item.id in state.lastPriorityChangeTimes)) {
        state.lastPriorityChangeTimes[item.id] = now;
      }
    });
  },

  // Completely purge an action by ID from ALL tracking lists.
  // Unlike removePrompt/forceRemovePrompt, this does NOT archive or discard —
  // the action is fully erased so it can be re-added fresh via addSuggestedAction.
  // Used when resetting recording consent for a new caller in the same room.
  purgeActionById: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const id = action.payload;
    state.suggestedActions = state.suggestedActions.filter((a) => a.id !== id);
    state.queuedActions = state.queuedActions.filter((a) => a.id !== id);
    state.allSuggestedActions = state.allSuggestedActions.filter((a) => a.id !== id);
    state.resolvedSuggestions = state.resolvedSuggestions.filter((a) => a.id !== id);
    state.discardedSuggestions = state.discardedSuggestions.filter((a) => a.id !== id);
    state.removedSuggestions = state.removedSuggestions.filter((a) => a.id !== id);
    state.partialSuggestions = state.partialSuggestions.filter((a) => a.id !== id);
    state.answeredPrompts = state.answeredPrompts.filter((p) => p !== id);
    state.detectedPrompts = state.detectedPrompts.filter((p) => p !== id);
    state.fadingPrompts = state.fadingPrompts.filter((p) => p !== id);
    state.lockedPrompts = state.lockedPrompts.filter((p) => p !== id);
    state.failedPrompts = state.failedPrompts.filter((p) => p !== id);
    state.partialPrompts = state.partialPrompts.filter((p) => p !== id);
    delete state.lastPriorityChangeTimes[id];
    logger.info('aiAnalysisSlice', 'purgeActionById: Fully purged action for re-use', { id });
  },

  // Follow up on an action later — removes it, creates a pinned follow-up note,
  // and adds to discarded/removed so it won't reappear from backend re-analysis.
  followUpAction: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const actionId = action.payload;
    const previousSuggestedIds = new Set(state.suggestedActions.map((a) => a.id));

    // Find the action before removing it
    const actionItem =
      state.suggestedActions.find((a) => a.id === actionId) ||
      state.queuedActions.find((a) => a.id === actionId);

    if (!actionItem) {
      logger.warn('aiAnalysisSlice', 'followUpAction: Action not found', { actionId });
      return;
    }

    // Build follow-up note content from the action details
    let noteContent = `FOLLOW UP: ${actionItem.details || actionItem.type}`;
    if (actionItem.conflict_details) {
      // Typed vs spoken stays visible in the pinned note (owner 2026-10-20).
      noteContent += conflictFollowUpNote(actionItem.conflict_details);
    }

    // Create a pinned custom note with clock icon
    const pinnedItem: PinnedItem = {
      id: `pin_followup_${actionId}_${Date.now()}`,
      type: 'custom_note',
      pinnedAt: Date.now(),
      content: noteContent,
      customIcon: 'clock',
    };
    state.pinnedItems.unshift(pinnedItem);

    // Archive the action
    if (!state.allSuggestedActions.find((a) => a.id === actionId)) {
      state.allSuggestedActions.push(actionItem);
    }

    // Add to removedSuggestions for the "Removed" tab
    if (!state.removedSuggestions.find((a) => a.id === actionId)) {
      state.removedSuggestions.unshift({
        ...actionItem,
        answeredAt: new Date().toISOString(),
      });
    }

    // Add to discardedSuggestions so backend dedup prevents re-detection
    if (!state.discardedSuggestions.find((a) => a.id === actionId)) {
      state.discardedSuggestions.push(actionItem);
    }

    // Remove from all active lists (same cleanup as forceRemovePrompt)
    state.suggestedActions = state.suggestedActions.filter((a) => a.id !== actionId);
    state.queuedActions = state.queuedActions.filter((a) => a.id !== actionId);
    state.partialSuggestions = state.partialSuggestions.filter((a) => a.id !== actionId);
    state.fadingPrompts = state.fadingPrompts.filter((id) => id !== actionId);
    state.detectedPrompts = state.detectedPrompts.filter((id) => id !== actionId);
    state.answeredPrompts = state.answeredPrompts.filter((id) => id !== actionId);
    state.partialPrompts = state.partialPrompts.filter((id) => id !== actionId);
    state.lockedPrompts = state.lockedPrompts.filter((id) => id !== actionId);
    state.failedPrompts = state.failedPrompts.filter((id) => id !== actionId);

    // Re-sort and split remaining actions
    const combinedActions = [...state.suggestedActions, ...state.queuedActions];
    const sortedActions = [...combinedActions].sort(
      (a, b) => (a.priority ?? 50) - (b.priority ?? 50),
    );
    const { visible, queued } = splitActionsByDisplayCaps(sortedActions);
    state.suggestedActions = visible;
    state.queuedActions = queued;

    // Initialize timestamps for promoted items
    const now = Date.now();
    state.suggestedActions.forEach((item) => {
      if (!previousSuggestedIds.has(item.id) && !(item.id in state.lastPriorityChangeTimes)) {
        state.lastPriorityChangeTimes[item.id] = now;
      }
    });

    logger.info(
      'aiAnalysisSlice',
      'followUpAction: Action deferred, pinned follow-up note created',
      {
        actionId,
        pinnedNoteId: pinnedItem.id,
      },
    );
  },

  // Restore a removed/discarded prompt back to active suggestions
  restorePrompt: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const promptId = action.payload;

    // Find the action in removed or discarded suggestions
    let actionToRestore = state.removedSuggestions.find((a) => a.id === promptId);
    let sourceList: 'removed' | 'discarded' | null = actionToRestore ? 'removed' : null;

    if (!actionToRestore) {
      actionToRestore = state.discardedSuggestions.find((a) => a.id === promptId);
      sourceList = actionToRestore ? 'discarded' : null;
    }

    // Also check allSuggestedActions archive as fallback
    if (!actionToRestore) {
      actionToRestore = state.allSuggestedActions.find((a) => a.id === promptId);
      sourceList = actionToRestore ? 'discarded' : null; // Treat archive as discarded
    }

    if (!actionToRestore) {
      logger.warn('aiAnalysisSlice', 'restorePrompt: Prompt not found in removed/discarded lists', {
        promptId,
      });
      return;
    }

    // Reset the action state for fresh display
    const restoredAction: SuggestedAction = {
      ...actionToRestore,
      answered: false,
      answeredAt: undefined,
      answerStatus: undefined,
      answer: undefined,
      partialAnswer: undefined,
      dedupReason: undefined,
      closestMatchId: undefined,
      // Keep the original priority but ensure it appears at top
      priority: Math.max((actionToRestore.priority ?? 50) - 10, 1),
    };

    // Remove from source lists
    state.removedSuggestions = state.removedSuggestions.filter((a) => a.id !== promptId);
    state.discardedSuggestions = state.discardedSuggestions.filter((a) => a.id !== promptId);

    // Clear from tracking arrays
    state.answeredPrompts = state.answeredPrompts.filter((id) => id !== promptId);
    state.partialPrompts = state.partialPrompts.filter((id) => id !== promptId);
    state.fadingPrompts = state.fadingPrompts.filter((id) => id !== promptId);

    // Update or add to suggested actions
    const existingIndex = state.suggestedActions.findIndex((a) => a.id === promptId);
    if (existingIndex !== -1) {
      // Update existing card with reset state
      state.suggestedActions[existingIndex] = restoredAction;
    } else {
      // Add back to suggested actions
      state.suggestedActions.push(restoredAction);
    }

    // Also update in allSuggestedActions archive
    const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === promptId);
    if (archiveIndex !== -1) {
      state.allSuggestedActions[archiveIndex] = {
        ...state.allSuggestedActions[archiveIndex],
        answered: false,
        answeredAt: undefined,
        answerStatus: undefined,
        answer: undefined,
        partialAnswer: undefined,
      };
    }

    // Re-sort and apply display caps
    const combinedActions = [...state.suggestedActions, ...state.queuedActions];
    const sortedActions = [...combinedActions].sort(
      (a, b) => (a.priority ?? 50) - (b.priority ?? 50),
    );
    const { visible, queued } = splitActionsByDisplayCaps(sortedActions);
    state.suggestedActions = visible;
    state.queuedActions = queued;

    // Set priority change time for stability lock
    state.lastPriorityChangeTimes[promptId] = Date.now();

    logger.info('aiAnalysisSlice', 'Restored prompt to active suggestions', {
      promptId,
      sourceList,
      question: restoredAction.details?.substring(0, 80),
    });
  },

  // Update note for a specific action (incident report only)
  updateActionNote: (
    state: AiAnalysisState,
    action: PayloadAction<{ id: string; note: string }>,
  ) => {
    const { id, note } = action.payload;

    // Update in suggestedActions
    const actionIndex = state.suggestedActions.findIndex((a) => a.id === id);
    if (actionIndex !== -1) {
      state.suggestedActions[actionIndex].note = note;
    }

    // Update in allSuggestedActions archive
    const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === id);
    if (archiveIndex !== -1) {
      state.allSuggestedActions[archiveIndex].note = note;
    }

    // Update in resolvedSuggestions
    const resolvedIndex = state.resolvedSuggestions.findIndex((a) => a.id === id);
    if (resolvedIndex !== -1) {
      state.resolvedSuggestions[resolvedIndex].note = note;
    }
  },

  // Update note for a specific activity event (incident report only)
  updateActivityEventNote: (
    state: AiAnalysisState,
    action: PayloadAction<{ index: number; note: string }>,
  ) => {
    const { index, note } = action.payload;
    const events = state.activityFeed.events;

    if (index >= 0 && index < events.length) {
      const event = events[index];
      if (typeof event === 'string') {
        // Convert string to ActivityEvent object
        events[index] = {
          shortSummary: event,
          note,
          id: simpleHash(event + index),
        } as ActivityEvent;
      } else {
        // Update existing object
        (event as ActivityEvent).note = note;
      }
    }
  },

  // Update note for caller entity (incident report only)
  updateCallerNote: (state: AiAnalysisState, action: PayloadAction<string>) => {
    state.entities.caller.note = action.payload;
  },

  // Set caller user-entered data (from participant metadata)
  // When a new caller joins, REPLACE the entire caller data with their values.
  // Fields not provided by the new caller are cleared (not preserved from previous caller).
  // This ensures we don't mix data from different callers.
  setCallerUserEnteredData: (
    state: AiAnalysisState,
    action: PayloadAction<{
      name?: string;
      phone?: string;
      email?: string;
      callReason?: string;
      address?: string;
      dob?: string;
      /** Geolocation captured at the citizen's pre-join 'share my location'
       *  consent. Only present when the citizen opted in AND the browser
       *  geolocation permission was granted. */
      location?: {
        latitude: number;
        longitude: number;
        accuracy: number;
        capturedAt: number;
      };
    }>,
  ) => {
    const { name, phone, email, callReason, address, dob, location } = action.payload;
    // Generate a caller ID if one doesn't exist (required for entity connections)
    const existingId = state.entities.caller.id;
    const callerId = existingId || `caller-${Math.random().toString(36).substring(2, 10)}`;
    // Replace entire caller data with new caller's values
    // Use empty string for fields not provided (clears old data)
    state.entities.caller = {
      id: callerId,
      name: name || '',
      phone: phone || '',
      email: email || '',
      callReason: callReason || '',
      address: address || '',
      dob: dob || '',
      location: location || undefined,
      userEntered: true,
      note: state.entities.caller.note, // Preserve officer's note
      lastUpdatedAt: Date.now(),
    };
  },

  // Restore the caller saved for this call into the live caller. The check and
  // the write are one step; the contract lives on restoreSavedCallerInto.
  restoreSavedCaller: (state: AiAnalysisState, action: PayloadAction<Caller>) => {
    const restored = restoreSavedCallerInto(state.entities.caller, action.payload);
    if (!restored) return;
    const restoredAt = Date.now();
    restored.lastUpdatedAt = restoredAt;
    state.entities.caller = restored;
    state.entityCategoryLastUpdate.caller = restoredAt;
  },

  // Clear caller data (when caller disconnects from live call)
  clearCallerData: (state: AiAnalysisState) => {
    state.entities.caller = {
      name: '',
      phone: '',
      address: '',
      dob: '',
      email: '',
      callReason: '',
      userEntered: false,
    };
  },

  // Update note for a suspect (incident report only)
  updateSuspectNote: (
    state: AiAnalysisState,
    action: PayloadAction<{ index: number; note: string }>,
  ) => {
    const { index, note } = action.payload;
    if (index >= 0 && index < state.entities.activity.length) {
      state.entities.activity[index].note = note;
    }
  },

  // Update note for a person entity (incident report only)
  updatePersonNote: (
    state: AiAnalysisState,
    action: PayloadAction<{ index: number; note: string }>,
  ) => {
    const { index, note } = action.payload;
    if (index >= 0 && index < state.entities.people.length) {
      state.entities.people[index].note = note;
    }
  },

  // Update note for a location entity (incident report only)
  updateLocationNote: (
    state: AiAnalysisState,
    action: PayloadAction<{ index: number; note: string }>,
  ) => {
    const { index, note } = action.payload;
    if (index >= 0 && index < state.entities.locations.length) {
      state.entities.locations[index].note = note;
    }
  },

  // Update note for an object entity (incident report only)
  updateObjectNote: (
    state: AiAnalysisState,
    action: PayloadAction<{ index: number; note: string }>,
  ) => {
    const { index, note } = action.payload;
    if (index >= 0 && index < state.entities.objects.length) {
      state.entities.objects[index].note = note;
    }
  },

  // Update note for a policy/law entity (incident report only)
  updatePolicyNote: (
    state: AiAnalysisState,
    action: PayloadAction<{ index: number; note: string }>,
  ) => {
    const { index, note } = action.payload;
    if (index >= 0 && index < state.entities.policiesAndLaws.length) {
      state.entities.policiesAndLaws[index].note = note;
    }
  },

  // ========== PINNED ITEMS FUNCTIONALITY ==========

  // Pin a suggested action
  pinSuggestedAction: (
    state: AiAnalysisState,
    action: PayloadAction<{ actionId: string; note?: string }>,
  ) => {
    const { actionId, note } = action.payload;

    // Check if already pinned
    const existingItem = state.pinnedItems.find(
      (item) => item.type === 'suggested_action' && item.actionId === actionId,
    );
    if (existingItem) {
      if (note) {
        if (!existingItem.note) {
          existingItem.note = note;
        } else if (!existingItem.note.includes(note)) {
          existingItem.note = `${existingItem.note} | ${note}`;
        }
      }
      return;
    }

    // Find the action across ALL action sources — on incident report, actions may live
    // in queuedActions, discardedSuggestions, removedSuggestions, or resolvedSuggestions
    const actionData =
      state.suggestedActions.find((a) => a.id === actionId) ||
      state.allSuggestedActions.find((a) => a.id === actionId) ||
      state.queuedActions.find((a) => a.id === actionId) ||
      state.discardedSuggestions?.find((a) => a.id === actionId) ||
      state.removedSuggestions?.find((a) => a.id === actionId) ||
      state.resolvedSuggestions?.find((a) => a.id === actionId);

    if (!actionData) return;

    const pinnedItem: PinnedItem = {
      id: `pin_action_${actionId}_${Date.now()}`,
      type: 'suggested_action',
      pinnedAt: Date.now(),
      actionId: actionId,
      actionType: actionData.type,
      actionDetails: actionData.details,
      actionPriority: actionData.priority,
      note: note || undefined,
    };

    state.pinnedItems.unshift(pinnedItem); // Add to beginning
  },

  // Pin an entity
  pinEntity: (
    state: AiAnalysisState,
    action: PayloadAction<{
      entityType:
        | 'caller'
        | 'person'
        | 'location'
        | 'object'
        | 'suspect'
        | 'policy'
        | 'organization'
        | 'other';
      entityIndex?: number;
      id?: string;
    }>,
  ) => {
    const { entityType, entityIndex, id } = action.payload;

    // Generate unique ID based on entity type and index
    const uniqueKey = entityIndex !== undefined ? `${entityType}_${entityIndex}` : entityType;

    // Check if already pinned
    if (
      state.pinnedItems.some(
        (item) =>
          item.type === 'entity' &&
          item.entityType === entityType &&
          item.entityIndex === entityIndex,
      )
    ) {
      return;
    }

    let entityData: any;

    // Get entity data based on type
    switch (entityType) {
      case 'caller':
        entityData = { ...state.entities.caller };
        break;
      case 'person':
        if (entityIndex !== undefined) {
          entityData = { ...state.entities.people[entityIndex] };
        }
        break;
      case 'location':
        if (entityIndex !== undefined) {
          entityData = { ...state.entities.locations[entityIndex] };
        }
        break;
      case 'object':
        if (entityIndex !== undefined) {
          entityData = { ...state.entities.objects[entityIndex] };
        }
        break;
      case 'suspect':
        if (entityIndex !== undefined) {
          entityData = { ...state.entities.activity[entityIndex] };
        }
        break;
      case 'policy':
        if (entityIndex !== undefined) {
          entityData = { ...state.entities.policiesAndLaws[entityIndex] };
        }
        break;
      case 'organization':
        if (entityIndex !== undefined) {
          entityData = { ...state.entities.organizations[entityIndex] };
        }
        break;
      case 'other':
        if (entityIndex !== undefined) {
          entityData = { ...state.entities.other[entityIndex] };
        }
        break;
    }

    if (!entityData) return;

    const pinnedItem: PinnedItem = {
      id: id || `pin_entity_${uniqueKey}_${Date.now()}`,
      type: 'entity',
      pinnedAt: Date.now(),
      entityType,
      entityIndex,
      entityData,
    };

    state.pinnedItems.unshift(pinnedItem);
  },

  // Pin a custom note
  pinCustomNote: (
    state: AiAnalysisState,
    action: PayloadAction<{ content: string; icon?: string; id?: string }>,
  ) => {
    const { content, icon, id } = action.payload;

    const pinnedItem: PinnedItem = {
      id: id || `pin_custom_${Date.now()}`,
      type: 'custom_note',
      pinnedAt: Date.now(),
      content,
      customIcon: icon,
    };

    state.pinnedItems.unshift(pinnedItem);
  },

  // Pin a chat message
  pinChat: (
    state: AiAnalysisState,
    action: PayloadAction<{
      messageId: string;
      sender: string;
      message: string;
      timestamp: number;
      chatType: 'officer' | 'participant';
      id?: string;
    }>,
  ) => {
    const { messageId, sender, message, timestamp, chatType, id } = action.payload;

    // Check if already pinned
    if (
      state.pinnedItems.some((item) => item.type === 'chat' && item.chatMessageId === messageId)
    ) {
      return;
    }

    const pinnedItem: PinnedItem = {
      id: id || `pin_chat_${messageId}_${Date.now()}`,
      type: 'chat',
      pinnedAt: Date.now(),
      chatMessageId: messageId,
      chatSender: sender,
      chatMessage: message,
      chatTimestamp: timestamp,
      chatType,
    };

    state.pinnedItems.unshift(pinnedItem);
  },

  // Pin a transcript segment
  pinTranscript: (
    state: AiAnalysisState,
    action: PayloadAction<{
      transcriptId: string;
      speaker: string;
      text: string;
      timestamp: number;
      id?: string;
      note?: string;
    }>,
  ) => {
    const { transcriptId, speaker, text, timestamp, id, note } = action.payload;

    // Check if already pinned
    if (
      state.pinnedItems.some(
        (item) => item.type === 'transcript' && item.transcriptId === transcriptId,
      )
    ) {
      return;
    }

    const pinnedItem: PinnedItem = {
      id: id || `pin_transcript_${transcriptId}_${Date.now()}`,
      type: 'transcript',
      pinnedAt: Date.now(),
      transcriptId,
      transcriptSpeaker: speaker,
      transcriptText: text,
      transcriptTimestamp: timestamp,
      ...(note ? { note } : {}),
    };

    state.pinnedItems.unshift(pinnedItem);
  },

  // Pin a TPS result
  pinTPSResult: (
    state: AiAnalysisState,
    action: PayloadAction<{
      occurrence_number: string;
      type: string;
      datetime: string;
      intersection: string;
      address?: string;
      neighbourhood?: string;
      division?: string;
      status?: string;
      notes?: string;
      vehicle?: {
        make?: string;
        model?: string;
        year?: string;
        color?: string;
        plate?: string;
      };
      id?: string;
    }>,
  ) => {
    const { id, ...incident } = action.payload;

    // Check if already pinned
    if (
      state.pinnedItems.some(
        (item) =>
          item.type === 'tps_result' &&
          item.tpsIncident?.occurrence_number === incident.occurrence_number,
      )
    ) {
      return;
    }

    const pinnedItem: PinnedItem = {
      id: id || `pin_tps_${incident.occurrence_number}_${Date.now()}`,
      type: 'tps_result',
      pinnedAt: Date.now(),
      tpsIncident: incident,
    };

    state.pinnedItems.unshift(pinnedItem);
  },

  // Pin an activity/timeline event
  pinActivityEvent: (
    state: AiAnalysisState,
    action: PayloadAction<{
      eventId: string;
      summary: string;
      incidentTime?: string;
      incidentTimeConfidence?: 'high' | 'medium' | 'low';
      transcriptEvidence?: Array<{ quote: string; speaker: 'caller' | 'officer' }>;
      note?: string;
      id?: string;
    }>,
  ) => {
    const { eventId, summary, incidentTime, incidentTimeConfidence, transcriptEvidence, note, id } =
      action.payload;

    // Check if already pinned
    if (
      state.pinnedItems.some(
        (item) => item.type === 'activity_event' && item.activityEventId === eventId,
      )
    ) {
      return;
    }

    const pinnedItem: PinnedItem = {
      id: id || `pin_activity_${eventId}_${Date.now()}`,
      type: 'activity_event',
      pinnedAt: Date.now(),
      activityEventId: eventId,
      activityEventSummary: summary,
      activityEventTime: incidentTime,
      activityEventTimeConfidence: incidentTimeConfidence,
      activityEventEvidence: transcriptEvidence,
      content: summary, // For display in pinned items list
      note,
    };

    state.pinnedItems.unshift(pinnedItem);
  },

  // Pin a canvas annotation (cross-service integration from ui-canvas iframe)
  pinCanvasAnnotation: (
    state: AiAnalysisState,
    action: PayloadAction<{
      source: 'canvas';
      text: string; // Description text from the pin
      annotation?: string; // Any annotation or notes
      timestampISO: string; // ISO timestamp when pin was created
      tool?: string; // Tool used when pinning
      color?: string; // Color being used when pinned
      size?: number; // Brush size when pinned
      linkedTranscriptId?: string; // Reference to matching transcript segment
      linkedTranscriptText?: string; // Quoted transcript text
      linkedTranscriptSpeaker?: string; // Who spoke the linked transcript
      id?: string;
    }>,
  ) => {
    const {
      source,
      text,
      annotation,
      timestampISO,
      tool,
      color,
      size,
      linkedTranscriptId,
      linkedTranscriptText,
      linkedTranscriptSpeaker,
      id,
    } = action.payload;

    // Parse timestamp to milliseconds
    const pinTimestamp = new Date(timestampISO).getTime() || Date.now();

    const pinnedItem: PinnedItem = {
      id: id || `pin_canvas_${pinTimestamp}_${Date.now()}`,
      type: 'canvas_pin',
      pinnedAt: Date.now(),
      canvasSource: source,
      canvasTool: tool,
      canvasColor: color,
      canvasSize: size,
      canvasText: text,
      canvasAnnotation: annotation,
      linkedTranscriptId,
      linkedTranscriptText,
      linkedTranscriptSpeaker,
      content: text, // For display in pinned items list
    };

    state.pinnedItems.unshift(pinnedItem);
  },

  // Set all pinned items (restoration)
  setPinnedItems: (state: AiAnalysisState, action: PayloadAction<PinnedItem[]>) => {
    state.pinnedItems = action.payload;
  },

  // Unpin an item
  unpinItem: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const itemId = action.payload;
    state.pinnedItems = state.pinnedItems.filter((item) => item.id !== itemId);
  },

  // Update note on pinned item
  updatePinnedItemNote: (
    state: AiAnalysisState,
    action: PayloadAction<{ id: string; note: string }>,
  ) => {
    const { id, note } = action.payload;
    const item = state.pinnedItems.find((item) => item.id === id);
    if (item) {
      item.note = note;
    }
  },

  // ========== RAG DOCUMENT PINNING ==========

  // Pin a RAG document
  pinRagDocument: (
    state: AiAnalysisState,
    action: PayloadAction<{
      documentId: string;
      documentTitle: string;
      repository: string;
      repositoryId: string;
      relevanceScore: number;
      aiReasoning: string;
      relevantChunks: Array<{ id: string; text: string; sectionPath?: string; score: number }>;
      surfacedFrom: 'entity' | 'query_ai' | 'related_docs' | 'govern' | 'question_gen';
      officerNote?: string;
      id?: string;
    }>,
  ) => {
    const {
      documentId,
      documentTitle,
      repository,
      repositoryId,
      relevanceScore,
      aiReasoning,
      relevantChunks,
      surfacedFrom,
      officerNote,
      id,
    } = action.payload;

    // Check if already pinned
    if (
      state.pinnedItems.some(
        (item) => item.type === 'rag_document' && item.ragDocumentId === documentId,
      )
    ) {
      return;
    }

    const pinnedItem: PinnedItem = {
      id: id || `pin_rag_${documentId}_${Date.now()}`,
      type: 'rag_document',
      pinnedAt: Date.now(),
      ragDocumentId: documentId,
      ragDocumentTitle: documentTitle,
      ragRepository: repository,
      ragRepositoryId: repositoryId,
      ragRelevanceScore: relevanceScore,
      ragAiReasoning: aiReasoning,
      ragRelevantChunks: relevantChunks,
      ragSurfacedFrom: surfacedFrom,
      note: officerNote,
      content: documentTitle, // For display in pinned items list
    };

    state.pinnedItems.unshift(pinnedItem);
  },

  // ========== RELATED DOCUMENTS SNAPSHOT (KB tab persistence) ==========
  // Replace the whole snapshot with the latest LIVE fetch. Persisted with the
  // call via saveCallData and rehydrated in the incident-report view so the
  // Knowledge tab keeps the in-call AI descriptions for future reviews.
  setRelatedDocumentsSnapshot: (
    state: AiAnalysisState,
    action: PayloadAction<Array<Record<string, unknown>>>,
  ) => {
    state.relatedDocumentsSnapshot = action.payload;
  },

  // Set-level KB synthesis (the "Description" content), snapshotted alongside
  // the documents so the incident-report view renders it once at the top.
  setRelatedDocumentsOverallSummary: (
    state: AiAnalysisState,
    action: PayloadAction<string | null>,
  ) => {
    state.relatedDocumentsOverallSummary = action.payload;
  },

  // Anderra's applied map layer config, kept so call end can freeze it and the
  // after-call map can replay it (useMapLayerConfig).
  setMapLayerConfig: (
    state: AiAnalysisState,
    action: PayloadAction<MapLayerConfigSnapshot | null>,
  ) => {
    state.mapLayerConfig = action.payload;
  },

  // Unpin a RAG document by document ID
  unpinRagDocument: (state: AiAnalysisState, action: PayloadAction<string>) => {
    const documentId = action.payload;
    state.pinnedItems = state.pinnedItems.filter(
      (item) => !(item.type === 'rag_document' && item.ragDocumentId === documentId),
    );
  },

  // Update officer note on a pinned RAG document
  updateRagDocumentNote: (
    state: AiAnalysisState,
    action: PayloadAction<{ documentId: string; note: string }>,
  ) => {
    const { documentId, note } = action.payload;
    const item = state.pinnedItems.find(
      (item) => item.type === 'rag_document' && item.ragDocumentId === documentId,
    );
    if (item) {
      item.note = note;
    }
  },

  // ========== QUICKNOTES FUNCTIONALITY ==========

  // Start a QuickNote - pins the item and begins AI generation
  startQuickNote: (
    state: AiAnalysisState,
    action: PayloadAction<{
      itemId: string;
      itemType: PinnedItem['type'];
      reason: string;
      // Optional: pass item data if not already pinned
      actionId?: string;
      actionType?:
        | 'question'
        | 'task'
        | 'suggestion'
        | 'conflicting_statement'
        | 'deception_detection'
        | 'revisit_thread';
      actionDetails?: string;
      actionPriority?: number;
    }>,
  ) => {
    const { itemId, itemType, reason, actionId, actionType, actionDetails, actionPriority } =
      action.payload;

    // For suggested actions, pin the item first if not already pinned
    if (itemType === 'suggested_action' && actionId) {
      // Check if already pinned
      const alreadyPinned = state.pinnedItems.some(
        (item) => item.type === 'suggested_action' && item.actionId === actionId,
      );

      if (!alreadyPinned) {
        const pinnedItem: PinnedItem = {
          id: itemId,
          type: 'suggested_action',
          pinnedAt: Date.now(),
          actionId: actionId,
          actionType: actionType,
          actionDetails: actionDetails,
          actionPriority: actionPriority,
        };
        state.pinnedItems.unshift(pinnedItem);
      }
    }

    // Add pending quicknote with loading status
    const existingPending = state.pendingQuickNotes.find((pn) => pn.itemId === itemId);
    if (!existingPending) {
      state.pendingQuickNotes.push({
        itemId,
        itemType,
        reason,
        aiGeneratedNote: '',
        aiReasoning: '',
        status: 'loading',
      });
    }

    logger.info('aiAnalysisSlice', 'Started QuickNote', { itemId, itemType, reason });
  },

  // Receive AI-generated note from backend
  receiveQuickNoteAI: (
    state: AiAnalysisState,
    action: PayloadAction<{
      itemId: string;
      aiGeneratedNote: string;
      aiReasoning: string;
    }>,
  ) => {
    const { itemId, aiGeneratedNote, aiReasoning } = action.payload;
    const pending = state.pendingQuickNotes.find((pn) => pn.itemId === itemId);

    if (pending) {
      pending.aiGeneratedNote = aiGeneratedNote;
      pending.aiReasoning = aiReasoning;
      pending.status = 'pending';
      logger.info('aiAnalysisSlice', 'Received QuickNote AI response', {
        itemId,
        noteLength: aiGeneratedNote.length,
        reasoningLength: aiReasoning.length,
      });
    }
  },

  // Update the AI-generated note text (for editing before accept)
  updatePendingQuickNote: (
    state: AiAnalysisState,
    action: PayloadAction<{
      itemId: string;
      aiGeneratedNote?: string;
      aiReasoning?: string;
    }>,
  ) => {
    const { itemId, aiGeneratedNote, aiReasoning } = action.payload;
    const pending = state.pendingQuickNotes.find((pn) => pn.itemId === itemId);

    if (pending) {
      if (aiGeneratedNote !== undefined) {
        pending.aiGeneratedNote = aiGeneratedNote;
        pending.status = 'pending'; // Update status to pending when note is received
      }
      if (aiReasoning !== undefined) {
        pending.aiReasoning = aiReasoning;
      }
    }
  },

  // Accept the AI-generated note - saves it to the pinned item
  acceptQuickNote: (state: AiAnalysisState, action: PayloadAction<{ itemId: string }>) => {
    const { itemId } = action.payload;
    const pending = state.pendingQuickNotes.find((pn) => pn.itemId === itemId);

    if (pending) {
      // Find the pinned item and update its note
      const pinnedItem = state.pinnedItems.find((p) => p.id === itemId);
      if (pinnedItem) {
        pinnedItem.note = pending.aiGeneratedNote;
        pinnedItem.aiReasoning = pending.aiReasoning; // Preserve AI reasoning for info icon
      }

      // Remove from pending
      state.pendingQuickNotes = state.pendingQuickNotes.filter((pn) => pn.itemId !== itemId);

      logger.info('aiAnalysisSlice', 'Accepted QuickNote', {
        itemId,
        note: pending.aiGeneratedNote.substring(0, 50),
      });
    }
  },

  // Reject the AI-generated note - uses raw reason text instead
  rejectQuickNote: (state: AiAnalysisState, action: PayloadAction<{ itemId: string }>) => {
    const { itemId } = action.payload;
    const pending = state.pendingQuickNotes.find((pn) => pn.itemId === itemId);

    if (pending) {
      // Find the pinned item and set note to raw reason
      const pinnedItem = state.pinnedItems.find((p) => p.id === itemId);
      if (pinnedItem) {
        // Convert reason to readable format (e.g., "suspicious" -> "SUSPICIOUS")
        pinnedItem.note = pending.reason.toUpperCase();
      }

      // Remove from pending
      state.pendingQuickNotes = state.pendingQuickNotes.filter((pn) => pn.itemId !== itemId);

      logger.info('aiAnalysisSlice', 'Rejected QuickNote', { itemId, reason: pending.reason });
    }
  },

  // Cancel a QuickNote - removes from pending without saving
  cancelQuickNote: (state: AiAnalysisState, action: PayloadAction<{ itemId: string }>) => {
    const { itemId } = action.payload;
    state.pendingQuickNotes = state.pendingQuickNotes.filter((pn) => pn.itemId !== itemId);
    logger.info('aiAnalysisSlice', 'Cancelled QuickNote', { itemId });
  },

  // ========== END QUICKNOTES FUNCTIONALITY ==========

  // ========== END PINNED ITEMS FUNCTIONALITY ==========

  // ========== AI QUERY/SEARCH TRACKING ==========

  // Track an AI query (manual AI questions during call)
  trackAiQuery: (
    state: AiAnalysisState,
    action: PayloadAction<{
      query: string;
      response: string;
    }>,
  ) => {
    const { query, response } = action.payload;
    const id = `query_${Date.now()}_${simpleHash(query)}`;

    state.aiQueries.push({
      id,
      query,
      response,
      timestamp: new Date().toISOString(),
    });

    logger.info('aiAnalysisSlice', 'Tracked AI query', {
      id,
      queryPreview: query.substring(0, 100),
      responseLength: response.length,
    });
  },

  // Track an AI search (AI-powered searches)
  trackAiSearch: (
    state: AiAnalysisState,
    action: PayloadAction<{
      query: string;
      results: unknown[];
      filters?: Record<string, unknown>;
    }>,
  ) => {
    const { query, results, filters } = action.payload;
    const id = `search_${Date.now()}_${simpleHash(query)}`;

    state.aiSearches.push({
      id,
      query,
      results,
      filters,
      timestamp: new Date().toISOString(),
    });

    logger.info('aiAnalysisSlice', 'Tracked AI search', {
      id,
      queryPreview: query.substring(0, 100),
      resultsCount: results.length,
    });
  },

  // Clear AI queries and searches (used on reset)
  clearAiQueriesAndSearches: (state: AiAnalysisState) => {
    state.aiQueries = [];
    state.aiSearches = [];
  },

  // ========== END AI QUERY/SEARCH TRACKING ==========

  // Reset all state
  resetAiAnalysis: (state: AiAnalysisState) => {
    state.suggestedActions = [];
    state.queuedActions = [];
    state.allSuggestedActions = []; // CRITICAL: Reset archive to allow re-adding system tasks
    state.discardedSuggestions = [];
    state.resolvedSuggestions = [];
    state.removedSuggestions = []; // Also reset removed suggestions
    state.partialSuggestions = [];
    state.answeredPrompts = [];
    state.partialPrompts = [];
    state.detectedPrompts = [];
    state.fadingPrompts = [];
    state.lockedPrompts = []; // Also reset locked prompts
    state.failedPrompts = [];
    state.pinnedItems = [];
    state.pendingQuickNotes = [];
    state.aiQueries = [];
    state.aiSearches = [];
    state.entities = {
      caller: { name: '', address: '', phone: '', dob: '' },
      incident: undefined,
      officers: [],
      people: [],
      organizations: [],
      locations: [],
      objects: [],
      activity: [],
      policiesAndLaws: [],
      other: [],
      connections: {
        relationships: [],
      },
    };
    state.activityFeed = { events: [] };
    state.extractionErrors = {};
    state.canonicalRevision = 0;
    state.canonicalStateRevision = 0;
    // Persisted with the call: a new call must not inherit, lock in or save
    // the previous call's map layers.
    state.mapLayerConfig = null;
    // Likewise the Knowledge panel's snapshot (knowledgePersistence.ts): it is
    // saved into this call's full_state, so it must start empty.
    state.relatedDocumentsSnapshot = [];
    state.relatedDocumentsOverallSummary = null;
  },

  hydrateAiAnalysisState: (
    _state: AiAnalysisState,
    action: PayloadAction<Partial<AiAnalysisState>>,
  ) => {
    // CRITICAL: Merge with initialState first to ensure all new fields have defaults.
    // Saved snapshots from database may be missing fields added after the snapshot was created.
    // Without this, fields like fadingPrompts, lockedPrompts, etc. would be undefined.
    const savedState = action.payload;
    const statusSources: Array<Partial<SuggestedAction>> = [
      ...(savedState.allSuggestedActions || []),
      ...(savedState.suggestedActions || []),
      ...(savedState.queuedActions || []),
      ...(savedState.resolvedSuggestions || []),
    ];
    const derivedAnswered = new Set<string>();
    const derivedPartial = new Set<string>();
    const validPartialIds = new Set<string>();

    statusSources.forEach((item) => {
      const id = item?.id;
      if (!id) return;

      const status = item.answerStatus;
      const isComplete = status === 'complete' || item.answered === true;
      const hasValidPartialShape = !isComplete && hasValidPartialAnswerShape(item);
      const isPartial = status === 'partial' && hasValidPartialShape;

      if (isComplete) {
        derivedAnswered.add(id);
      } else if (isPartial) {
        derivedPartial.add(id);
      }

      if (hasValidPartialShape) {
        validPartialIds.add(id);
      }
    });

    const existingAnswered = Array.isArray(savedState.answeredPrompts)
      ? savedState.answeredPrompts
      : [];
    const existingPartial = Array.isArray(savedState.partialPrompts)
      ? savedState.partialPrompts.filter(
          (id): id is string => typeof id === 'string' && validPartialIds.has(id),
        )
      : [];
    const answeredPrompts = new Set<string>([...existingAnswered, ...derivedAnswered]);
    const partialPrompts = new Set<string>([...existingPartial, ...derivedPartial]);

    answeredPrompts.forEach((id) => partialPrompts.delete(id));
    // Use allSuggestedActions (the complete archive) as the primary source,
    // falling back to suggestedActions + queuedActions if archive is empty.
    // allSuggestedActions contains ALL actions including answered, discarded, and removed.
    const combinedActions = savedState.allSuggestedActions?.length
      ? [...savedState.allSuggestedActions, ...(savedState.queuedActions || [])]
      : [...(savedState.suggestedActions || []), ...(savedState.queuedActions || [])];
    // Deduplicate by ID (allSuggestedActions may overlap with queuedActions)
    const seenIds = new Set<string>();
    const uniqueActions = combinedActions
      .filter((a) => {
        if (!a?.id || seenIds.has(a.id)) return false;
        seenIds.add(a.id);
        return true;
      })
      .map((a) => {
        // Normalize: DB stores 'description' or 'text' (backend format), Redux uses 'details'
        const anyA = a as any;
        let normalized: any = a;
        if (!a.details && (anyA.description || anyA.text)) {
          normalized = { ...a, details: anyA.description || anyA.text };
        }
        return sanitizeActionAnswerState(normalized);
      });
    const { visible, queued } = splitActionsByDisplayCaps(uniqueActions);
    // Normalize all action arrays from DB: 'description'/'text' (backend) → 'details' (Redux)
    const normalizeActions = (actions?: any[]) =>
      (actions || []).map((a: any) => {
        let normalized: any = a;
        if (!a?.details && (a?.description || a?.text)) {
          normalized = { ...a, details: a.description || a.text };
        }
        return sanitizeActionAnswerState(normalized);
      });
    const normalizedPartialSuggestions = normalizeActions(savedState.partialSuggestions).filter(
      (item) =>
        item.id &&
        partialPrompts.has(item.id) &&
        item.answerStatus === 'partial' &&
        hasValidPartialAnswerShape(item),
    );

    // Normalize activityFeed: handle legacy array format and ensure { events: [...] } shape.
    // Conference.tsx previously saved `activityFeed: aiAnalysis.activityFeed || []` which
    // would produce an empty array instead of { events: [] } when activityFeed was falsy.
    // The Redux store requires { events: ActivityEvent[] } shape.
    let normalizedActivityFeed = savedState.activityFeed;
    if (Array.isArray(normalizedActivityFeed)) {
      // Legacy format: activityFeed was saved as a plain array of events
      normalizedActivityFeed = { events: normalizedActivityFeed };
    } else if (!normalizedActivityFeed || typeof normalizedActivityFeed !== 'object') {
      normalizedActivityFeed = { events: [] };
    } else if (!Array.isArray(normalizedActivityFeed.events)) {
      normalizedActivityFeed = { ...normalizedActivityFeed, events: [] };
    }
    const normalizedSavedEntities = normalizeEntityTimestamps(
      normalizeEntitiesPayload(savedState.entities),
    );

    return {
      ...initialState, // Start with all defaults
      ...savedState, // Overlay saved state (missing fields keep defaults)
      entityPassSettled: true, // #1701: a loaded analysis settles the entity tabs
      entities: normalizedSavedEntities || initialState.entities,
      activityFeed: normalizedActivityFeed, // Override with normalized shape
      suggestedActions: visible,
      queuedActions: queued,
      allSuggestedActions: normalizeActions(savedState.allSuggestedActions),
      resolvedSuggestions: normalizeActions(savedState.resolvedSuggestions),
      discardedSuggestions: normalizeActions(savedState.discardedSuggestions),
      removedSuggestions: normalizeActions(savedState.removedSuggestions),
      partialSuggestions: normalizedPartialSuggestions,
      answeredPrompts: Array.from(answeredPrompts),
      partialPrompts: Array.from(partialPrompts),
      displayOrder: visible.map((a) => a.id),
    };
  },

  hydrateCanonicalActionProjection: (
    state: AiAnalysisState,
    action: PayloadAction<
      Partial<
        Pick<
          AiAnalysisState,
          | 'suggestedActions'
          | 'queuedActions'
          | 'allSuggestedActions'
          | 'resolvedSuggestions'
          | 'discardedSuggestions'
          | 'removedSuggestions'
          | 'partialSuggestions'
          | 'answeredPrompts'
          | 'partialPrompts'
        >
      > & { canonicalRevision?: number; canonicalStateRevision?: number }
    >,
  ) => {
    const projection = action.payload;
    // Revision gate. The projection carries a monotonic `canonicalRevision`
    // (max row revision server-side). A broadcast or re-hydration with a
    // revision strictly less than our current applied revision is STALE —
    // applying it would clobber a freshly-landed mutation, e.g. clearing an
    // id from `answeredPrompts` mid-countdown then restoring it on the next
    // delta, which restarts the auto-archive timer and makes completed items
    // linger far longer than the configured countdown. Same-revision is also
    // skipped — it's idempotent and avoids unnecessary re-renders.
    //
    // Initial hydration (`state.canonicalRevision === 0`) always applies so
    // we don't deadlock on a server that genuinely starts at 0.
    const projectionRevision =
      typeof projection.canonicalRevision === 'number' &&
      Number.isFinite(projection.canonicalRevision)
        ? projection.canonicalRevision
        : 0;
    const projectionStateRevision =
      typeof projection.canonicalStateRevision === 'number' &&
      Number.isFinite(projection.canonicalStateRevision) &&
      projection.canonicalStateRevision > 0
        ? projection.canonicalStateRevision
        : 0;
    if (projectionStateRevision > 0 && state.canonicalStateRevision > 0) {
      // Phase 5 transactional gate: call_speech_queue_state.canonical_revision is
      // strictly monotonic per canonical mutation. Unlike the row-derived max it
      // cannot tie within a timestamp tick or go BACKWARD when the newest row is
      // deleted, so an overlap-ordered broadcast can never suppress a newer
      // projection. Only compared when BOTH sides carry it — the two scales are
      // never mixed.
      if (projectionStateRevision <= state.canonicalStateRevision) {
        return;
      }
    } else if (state.canonicalRevision > 0 && projectionRevision <= state.canonicalRevision) {
      // Row-derived fallback (legacy calls / pre-migration payloads).
      return;
    }
    // Capture the current reword overlays BEFORE the projection replaces the
    // lists, so a reword that hasn't round-tripped through canonical survives the
    // refresh instead of flipping the text back to the original (see
    // buildImprovementOverlayIndex docs above).
    const overlayIndex = buildImprovementOverlayIndex([
      ...state.suggestedActions,
      ...state.queuedActions,
      ...state.partialSuggestions,
      ...state.allSuggestedActions,
    ]);
    const normalizeActions = (actions?: SuggestedAction[]) =>
      (actions || []).map((item: SuggestedAction) => {
        const anyItem = item as any;
        const normalized =
          !item?.details && (anyItem?.description || anyItem?.text)
            ? { ...item, details: anyItem.description || anyItem.text }
            : item;
        const sanitized = sanitizeActionAnswerState(normalized, { trustAnswerStatus: true });
        return reapplyImprovementOverlay(sanitized, overlayIndex);
      });

    const suggestedActions = normalizeActions(projection.suggestedActions);
    const queuedActions = normalizeActions(projection.queuedActions);
    const allSuggestedActions = normalizeActions(projection.allSuggestedActions);
    const resolvedSuggestions = normalizeActions(projection.resolvedSuggestions);
    const discardedSuggestions = normalizeActions(projection.discardedSuggestions);
    const removedSuggestions = normalizeActions(projection.removedSuggestions);
    const answeredPrompts = Array.isArray(projection.answeredPrompts)
      ? projection.answeredPrompts.filter((id): id is string => typeof id === 'string')
      : [];
    const answeredSet = new Set(answeredPrompts);
    const partialPrompts = Array.isArray(projection.partialPrompts)
      ? projection.partialPrompts.filter(
          (id): id is string => typeof id === 'string' && !answeredSet.has(id),
        )
      : [];
    const partialSet = new Set(partialPrompts);
    const partialSuggestions = normalizeActions(projection.partialSuggestions).filter(
      (item) => item.id && partialSet.has(item.id) && item.answerStatus === 'partial',
    );
    const projectedIds = new Set([
      ...suggestedActions.map((item) => item.id),
      ...queuedActions.map((item) => item.id),
      ...allSuggestedActions.map((item) => item.id),
      ...resolvedSuggestions.map((item) => item.id),
      ...discardedSuggestions.map((item) => item.id),
      ...removedSuggestions.map((item) => item.id),
    ]);

    // An earlier create response or independent realtime snapshot can arrive
    // while other system cards are still being saved. Reconcile their drafts
    // inside this transaction so subscribers never observe a partial script.
    // Only active drafts survive: purged/removed cards cannot be resurrected.
    // Any canonical occurrence (including a terminal list) acknowledges the ID.
    const pendingActions = dedupeById(
      [...state.suggestedActions, ...state.queuedActions].filter(
        (item) => item.canonicalCreatePending && !projectedIds.has(item.id) &&
          !answeredSet.has(item.id),
      ),
      'hydrateCanonicalActionProjection.pending',
    );
    const pendingIds = new Set(pendingActions.map((item) => item.id));
    const pendingAnswered = state.answeredPrompts.filter((id) => pendingIds.has(id));
    const pendingPartial = state.partialPrompts.filter((id) => pendingIds.has(id));
    for (const id of pendingIds) projectedIds.add(id);

    state.suggestedActions = [...suggestedActions, ...pendingActions]
      .sort(compareRequiredSystemActions);
    state.queuedActions = queuedActions;
    state.allSuggestedActions = [...allSuggestedActions, ...pendingActions];
    state.resolvedSuggestions = resolvedSuggestions;
    state.discardedSuggestions = discardedSuggestions;
    state.removedSuggestions = removedSuggestions;
    state.partialSuggestions = partialSuggestions;
    state.answeredPrompts = [...new Set([...answeredPrompts, ...pendingAnswered])];
    state.partialPrompts = [...new Set([...partialPrompts, ...pendingPartial])];
    state.displayOrder = state.suggestedActions.map((item) => item.id);
    state.detectedPrompts = state.detectedPrompts.filter((id) => projectedIds.has(id));
    state.failedPrompts = state.failedPrompts.filter((id) => projectedIds.has(id));
    state.fadingPrompts = state.fadingPrompts.filter((id) => projectedIds.has(id));
    state.lockedPrompts = state.lockedPrompts.filter((id) => projectedIds.has(id));
    state.lastItemsChangeTime = Date.now();
    state.canonicalRevision = projectionRevision;
    if (projectionStateRevision > 0) {
      // Keep the last-seen transactional revision when a legacy payload applies
      // (never reset to 0 — that would reopen the gate to older normalized deltas).
      state.canonicalStateRevision = projectionStateRevision;
    }
  },

  // Clear extraction errors (when retrying or manually dismissing)
  clearExtractionErrors: (
    state: AiAnalysisState,
    action: PayloadAction<{
      type?: 'entities' | 'activityFeed' | 'suggestedActions' | 'all';
    }>,
  ) => {
    const errorType = action.payload?.type || 'all';
    if (errorType === 'all') {
      state.extractionErrors = {};
    } else if (errorType === 'entities') {
      delete state.extractionErrors.entities;
    } else if (errorType === 'activityFeed') {
      delete state.extractionErrors.activityFeed;
    } else if (errorType === 'suggestedActions') {
      delete state.extractionErrors.suggestedActions;
    }
  },

  // Set entire AI analysis payload (replaces everything)
  setAiAnalysisPayload: (
    state: AiAnalysisState,
    action: PayloadAction<{
      aiAnalysis?: { suggestedActions?: any[]; discardedActions?: any[] };
      activityFeed?: { events?: string[] };
      entities?: Partial<Entities>;
      extractionErrors?: ExtractionErrors;
    }>,
  ) => {
    const payload = action.payload;
    const normalizedEntities = normalizeEntityTimestamps(
      normalizeEntitiesPayload(payload.entities),
    );

    if (payload.aiAnalysis?.suggestedActions) {
      // Clear suggested actions extraction error when we receive valid data
      if (state.extractionErrors.suggestedActions) {
        delete state.extractionErrors.suggestedActions;
      }
      const oldOrder = [...state.suggestedActions, ...state.queuedActions].map((a) => a.id);
      const now = Date.now();
      const mappedActions = payload.aiAnalysis.suggestedActions.map((item: any, idx: number) => {
        // Backend should ALWAYS provide IDs now (both initial and LLM suggestions)
        if (!item.id) {
          logger.error('aiAnalysisSlice', 'Received suggestion without ID from backend!', {
            description: item.description || item.details,
            index: idx,
          });
          // Fallback ID generation (should never happen)
          item.id = `fallback_${Date.now()}_${idx}`;
        }

        const mappedType = item.type || 'suggestion';
        const officerAddressed = item.officerAddressed === true;
        const officerSideCompleted =
          officerAddressed && (mappedType === 'task' || mappedType === 'suggestion');

        return sanitizeActionAnswerState(
          {
            id: item.id,
            ...mapActionProvenance(item),
            type: mappedType,
            details: item.details || item.description || '',
            originalDetails: item.originalDetails,
            improvementPatch: item.improvementPatch,
            reason: item.reason,
            status: item.status,
            score: item.score,
            time: item.time,
            answered: item.answered ?? (officerSideCompleted ? true : undefined),
            answeredAt:
              item.answeredAt || (officerSideCompleted ? item.officerAddressedAt : undefined),
            autoDetected: item.autoDetected,
            priority: parsePriority(item.priority),
            userDeprioritized: item.userDeprioritized,
            userDeprioritizedAt: item.userDeprioritizedAt,
            severity: item.severity,
            severityReason: item.severityReason,
            medicalContext: item.medicalContext,
            relevancyScore: item.relevancyScore,
            conflict_details: item.conflict_details,
            deception_indicators: item.deception_indicators,
            confidence_score: item.confidence_score,
            dedupDecision: item.dedupDecision,
            dedupReason: item.dedupReason,
            noveltyScore: item.noveltyScore,
            similarityScore: item.similarityScore,
            closestMatchId: item.closestMatchId,
            dedupCandidates: item.dedupCandidates,
            // Completion criteria for answer evaluation (four-tier hierarchy)
            completion_criteria: item.completion_criteria,
            // Question importance and answer value tracking
            importanceToIncident: item.importanceToIncident,
            answerValuePotential: item.answerValuePotential,
            required: item.required,
            answer: item.answer,
            partialAnswer: item.partialAnswer,
            highlightedQuestion: item.highlightedQuestion,
            questionParts: item.questionParts,
            answeredParts: item.answeredParts,
            unansweredParts: item.unansweredParts,
            answerReasoning: item.answerReasoning,
            answerStatus: item.answerStatus || (officerSideCompleted ? 'complete' : undefined),
            officerAddressed: item.officerAddressed,
            officerAddressedAt: item.officerAddressedAt,
            officerAddressedPartial: item.officerAddressedPartial,
            officerQuote: item.officerQuote,
            officerAddressedReason: item.officerAddressedReason,
            // Trust the backend's answerStatus (consistent with the realtime
            // projection path) so a momentarily-incomplete highlight frame does
            // not strip a valid `partial`, which made partial cards blink out of
            // the officer's queue.
          },
          { trustAnswerStatus: true },
        );
      });
      // Preserve any in-flight reword overlays that the canonical payload does
      // not carry (the payload is rebuilt from the canonical store, which never
      // persisted improvementPatch) so the displayed text does not flip back to
      // the original. Captured from state while it still holds the prior actions.
      const overlayIndex = buildImprovementOverlayIndex([
        ...state.suggestedActions,
        ...state.queuedActions,
        ...state.partialSuggestions,
        ...state.allSuggestedActions,
      ]);
      const mappedWithOverlay = mappedActions.map((a) =>
        reapplyImprovementOverlay(a, overlayIndex),
      );
      // CRITICAL: Sort by priority BEFORE splitting into visible/queued
      // Lower priority number = higher priority (1=critical, 50=baseline, 100=deferred)
      const dedupedMappedActions = dedupeById(mappedWithOverlay, 'setAiAnalysisPayload');
      const sortedMappedActions = [...dedupedMappedActions].sort(
        (a, b) => (a.priority ?? 50) - (b.priority ?? 50),
      );
      const { visible, queued } = splitActionsByDisplayCaps(sortedMappedActions);
      state.suggestedActions = visible;
      state.queuedActions = queued;
      const newOrder = [...state.suggestedActions, ...state.queuedActions].map((a) => a.id);
      updatePriorityChangeTimesForOrder(state, oldOrder, newOrder, now);
    }

    if (payload.aiAnalysis?.discardedActions) {
      state.discardedSuggestions = payload.aiAnalysis.discardedActions.map(
        (item: any, idx: number) => {
          if (!item.id) {
            logger.error(
              'aiAnalysisSlice',
              'Received discarded suggestion without ID from backend!',
              {
                description: item.description || item.details,
                index: idx,
              },
            );
            item.id = `fallback_discarded_${Date.now()}_${idx}`;
          }

          const mappedType = item.type || 'suggestion';
          const officerAddressed = item.officerAddressed === true;
          const officerSideCompleted =
            officerAddressed && (mappedType === 'task' || mappedType === 'suggestion');

          return sanitizeActionAnswerState(
            {
              id: item.id,
              ...mapActionProvenance(item),
              type: mappedType,
              details: item.details || item.description || '',
              originalDetails: item.originalDetails,
              improvementPatch: item.improvementPatch,
              reason: item.reason,
              status: item.status,
              score: item.score,
              time: item.time,
              answered: item.answered ?? (officerSideCompleted ? true : undefined),
              answeredAt:
                item.answeredAt || (officerSideCompleted ? item.officerAddressedAt : undefined),
              autoDetected: item.autoDetected,
              priority: parsePriority(item.priority),
              userDeprioritized: item.userDeprioritized,
              userDeprioritizedAt: item.userDeprioritizedAt,
              severity: item.severity,
              severityReason: item.severityReason,
              medicalContext: item.medicalContext,
              relevancyScore: item.relevancyScore,
              conflict_details: item.conflict_details,
              deception_indicators: item.deception_indicators,
              confidence_score: item.confidence_score,
              dedupDecision: item.dedupDecision,
              dedupReason: item.dedupReason,
              noveltyScore: item.noveltyScore,
              similarityScore: item.similarityScore,
              closestMatchId: item.closestMatchId,
              dedupCandidates: item.dedupCandidates,
              // Completion criteria for answer evaluation (four-tier hierarchy)
              completion_criteria: item.completion_criteria,
              // Question importance and answer value tracking
              importanceToIncident: item.importanceToIncident,
              answerValuePotential: item.answerValuePotential,
              required: item.required,
              answer: item.answer,
              partialAnswer: item.partialAnswer,
              highlightedQuestion: item.highlightedQuestion,
              questionParts: item.questionParts,
              answeredParts: item.answeredParts,
              unansweredParts: item.unansweredParts,
              answerReasoning: item.answerReasoning,
              answerStatus: item.answerStatus,
              officerAddressed: item.officerAddressed,
              officerAddressedAt: item.officerAddressedAt,
              officerAddressedPartial: item.officerAddressedPartial,
              officerQuote: item.officerQuote,
              officerAddressedReason: item.officerAddressedReason,
              // Trust backend answerStatus (see the visible-actions path above).
            },
            { trustAnswerStatus: true },
          );
        },
      );
    }

    // Backend may still send notes - we ignore them now (using manual pinning instead)

    // CRITICAL: Only update if we have actual events - empty arrays should NOT wipe existing data
    // and a SHORTER feed is unioned in, never allowed to shrink the existing one
    // (mergeActivityFeedEvents — 9 events became 1 after a room re-creation, 2026-08-17).
    if (payload.activityFeed?.events && payload.activityFeed.events.length > 0) {
      state.activityFeed.events = mergeActivityFeedEvents(
        state.activityFeed.events as (string | ActivityEvent)[],
        payload.activityFeed.events as (string | ActivityEvent)[],
      ) as typeof state.activityFeed.events;
      // Clear activity feed extraction error when we receive valid data
      if (state.extractionErrors.activityFeed) {
        delete state.extractionErrors.activityFeed;
      }
    }

    if (normalizedEntities) {
      if (normalizedEntities.caller) {
        // CRITICAL: Preserve user-entered caller data when merging
        // Same logic as appendAiAnalysisPayload to prevent name from vanishing
        const existingCaller = state.entities.caller;
        const newCaller = normalizedEntities.caller;
        state.entities.caller = {
          ...newCaller,
          // Preserve name and phone if user-entered
          name:
            existingCaller.userEntered && existingCaller.name
              ? existingCaller.name
              : newCaller.name || existingCaller.name,
          phone:
            existingCaller.userEntered && existingCaller.phone
              ? existingCaller.phone
              : newCaller.phone || existingCaller.phone,
          callReason: newCaller.callReason || existingCaller.callReason,
          userEntered: existingCaller.userEntered || newCaller.userEntered,
          email: newCaller.email || existingCaller.email,
          dob: newCaller.dob || existingCaller.dob,
          address: newCaller.address || existingCaller.address,
          callbackNumber: newCaller.callbackNumber || existingCaller.callbackNumber,
          lastUpdatedAt: Date.now(),
        };
      }
      if (normalizedEntities.people) {
        state.entities.people = normalizedEntities.people;
      }
      if (normalizedEntities.officers) {
        state.entities.officers = normalizedEntities.officers;
      }
      if (normalizedEntities.organizations) {
        state.entities.organizations = normalizedEntities.organizations;
      }
      if (normalizedEntities.locations) {
        state.entities.locations = normalizedEntities.locations;
      }
      if (normalizedEntities.objects) {
        state.entities.objects = normalizedEntities.objects;
      }
      if (normalizedEntities.activity) {
        state.entities.activity = normalizedEntities.activity;
      }
      if (normalizedEntities.policiesAndLaws) {
        state.entities.policiesAndLaws = normalizedEntities.policiesAndLaws;
      }
      if (normalizedEntities.other) {
        state.entities.other = normalizedEntities.other;
      }
      if (normalizedEntities.incident) {
        state.entities.incident = normalizedEntities.incident;
      }
      if (normalizedEntities.connections) {
        // Cast to any to handle legacy fields from backend that aren't in frontend types
        const connections = normalizedEntities.connections as any;
        const allRelationships: any[] = [...(connections.relationships || [])];

        // Convert legacy fields to relationships format
        if (connections.peoplesRelationship && Array.isArray(connections.peoplesRelationship)) {
          connections.peoplesRelationship.forEach((rel: any) => {
            const relationshipType = rel.relationshipType || 'related_to';
            allRelationships.push({
              id: `legacy_people_${rel.person1}_${rel.person2}`,
              sourceEntityId: rel.person1 || '',
              targetEntityId: rel.person2 || '',
              relationshipType,
              label: `${rel.person1} - ${relationshipType} - ${rel.person2}`,
            });
          });
        }
        if (connections.locationRelationship && Array.isArray(connections.locationRelationship)) {
          connections.locationRelationship.forEach((rel: any) => {
            const relationshipType = rel.relationshipType || 'located_at';
            allRelationships.push({
              id: `legacy_location_${rel.location}_${rel.object}`,
              sourceEntityId: rel.location || '',
              targetEntityId: rel.object || '',
              relationshipType,
              label: `${rel.object} - ${relationshipType} - ${rel.location}`,
            });
          });
        }
        if (connections.patternConnections && Array.isArray(connections.patternConnections)) {
          connections.patternConnections.forEach((pat: any) => {
            allRelationships.push({
              id: `legacy_pattern_${pat.entity1}_${pat.entity2}`,
              sourceEntityId: pat.entity1 || '',
              targetEntityId: pat.entity2 || '',
              relationshipType: 'pattern',
              label: pat.pattern || `Pattern: ${pat.entity1} - ${pat.entity2}`,
            });
          });
        }

        state.entities.connections = { relationships: allRelationships };
      }

      // Clear entity extraction error when we receive valid entities
      if (state.extractionErrors.entities) {
        delete state.extractionErrors.entities;
      }
    }

    // Handle extraction errors (sent separately when LLM calls fail)
    if (payload.extractionErrors) {
      const now = Date.now();
      if (payload.extractionErrors.entities) {
        // Validate error structure and provide detailed logging
        const entityError = payload.extractionErrors.entities;
        const isValidError =
          entityError.error === true && entityError.errorType && entityError.errorMessage;

        if (!isValidError) {
          logger.warn('aiAnalysisSlice', 'Malformed entity extraction error received', {
            error: entityError,
            hasErrorFlag: entityError.error === true,
            hasErrorType: !!entityError.errorType,
            hasErrorMessage: !!entityError.errorMessage,
            fullPayload: payload.extractionErrors,
          });
        }

        state.extractionErrors.entities = {
          ...entityError,
          timestamp: now,
        };

        logger.error('aiAnalysisSlice', 'Entity extraction error received', {
          errorType: entityError.errorType || 'unknown',
          errorMessage: entityError.errorMessage || 'No error message provided',
          fullError: entityError,
          timestamp: now,
          context: 'setAiAnalysisPayload',
        });
      }
      if (payload.extractionErrors.activityFeed) {
        const feedError = payload.extractionErrors.activityFeed;
        const isValidError =
          feedError.error === true && feedError.errorType && feedError.errorMessage;

        if (!isValidError) {
          logger.warn('aiAnalysisSlice', 'Malformed activity feed extraction error received', {
            error: feedError,
            hasErrorFlag: feedError.error === true,
            hasErrorType: !!feedError.errorType,
            hasErrorMessage: !!feedError.errorMessage,
            fullPayload: payload.extractionErrors,
          });
        }

        state.extractionErrors.activityFeed = {
          ...feedError,
          timestamp: now,
        };

        logger.error('aiAnalysisSlice', 'Activity feed extraction error received', {
          errorType: feedError.errorType || 'unknown',
          errorMessage: feedError.errorMessage || 'No error message provided',
          fullError: feedError,
          timestamp: now,
          context: 'setAiAnalysisPayload',
        });
      }
      if (payload.extractionErrors.suggestedActions) {
        const actionsError = payload.extractionErrors.suggestedActions;
        const isValidError =
          actionsError.error === true && actionsError.errorType && actionsError.errorMessage;

        if (!isValidError) {
          logger.warn('aiAnalysisSlice', 'Malformed suggested actions extraction error received', {
            error: actionsError,
            hasErrorFlag: actionsError.error === true,
            hasErrorType: !!actionsError.errorType,
            hasErrorMessage: !!actionsError.errorMessage,
            fullPayload: payload.extractionErrors,
          });
        }

        state.extractionErrors.suggestedActions = {
          ...actionsError,
          timestamp: now,
        };

        logger.error('aiAnalysisSlice', 'Suggested actions extraction error received', {
          errorType: actionsError.errorType || 'unknown',
          errorMessage: actionsError.errorMessage || 'No error message provided',
          fullError: actionsError,
          timestamp: now,
          context: 'setAiAnalysisPayload',
        });
      }
    }
  },

  // Append AI analysis payload (merges with existing, prepends new items)
  appendAiAnalysisPayload: (
    state: AiAnalysisState,
    action: PayloadAction<{
      aiAnalysis?: { suggestedActions?: any[]; discardedActions?: any[] };
      activityFeed?: { events?: string[] };
      entities?: Partial<Entities>;
      extractionErrors?: ExtractionErrors;
      officerAskedBadges?: Array<{
        action_id: string;
        badge: string;
        officer_quote: string;
        confidence: number;
      }>;
    }>,
  ) => {
    const newData = action.payload;

    // Log the incoming payload
    logger.info('aiAnalysisSlice', 'appendAiAnalysisPayload called', {
      hasAiAnalysis: !!newData.aiAnalysis,
      hasSuggestedActions: !!newData.aiAnalysis?.suggestedActions,
      incomingSuggestedActionsCount: newData.aiAnalysis?.suggestedActions?.length || 0,
      incomingSuggestedActions: newData.aiAnalysis?.suggestedActions || [],
      currentStateCount: {
        suggestedActions: state.suggestedActions.length,
      },
      currentSuggestedActions: state.suggestedActions,
    });

    // Exact-match-only dedup. Semantic similarity is handled by the backend
    // LLM dedup (/dedupe/check) — NO regex/pattern matching on the frontend.
    const areSimilar = (text1: string, text2: string): boolean => {
      const normalize = (text: string) =>
        text
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      return normalize(text1) === normalize(text2);
    };

    // Helper function to prepend new items while avoiding duplicates and limiting total items
    const prependUnique = <T extends { details?: string; description?: string; content?: string }>(
      newItems: T[],
      existingItems: T[],
      maxItems: number = 30,
    ): T[] => {
      const isAlertType = (value: unknown) =>
        value === 'conflicting_statement' || value === 'deception_detection';

      const shouldCompare = (incoming: T, existing: T) => {
        const incomingType = (incoming as any)?.type;
        const existingType = (existing as any)?.type;

        if (
          typeof incomingType === 'string' &&
          typeof existingType === 'string' &&
          incomingType !== existingType
        ) {
          // Avoid cross-type deduping for alert types so they don't disappear silently
          if (isAlertType(incomingType) || isAlertType(existingType)) {
            return false;
          }
        }

        return true;
      };

      const duplicates: string[] = [];
      const uniqueNewItems = newItems.filter((newItem) => {
        const newKey = newItem.details || newItem.description || newItem.content || '';

        const isDuplicate = existingItems.some((existing) => {
          if (!shouldCompare(newItem, existing)) {
            return false;
          }

          const existingKey = existing.details || existing.description || existing.content || '';
          return areSimilar(newKey, existingKey);
        });

        if (isDuplicate) {
          duplicates.push(newKey.substring(0, 100));
        }

        return !isDuplicate;
      });

      logger.info('aiAnalysisSlice', 'prependUnique deduplication', {
        newItemsCount: newItems.length,
        existingItemsCount: existingItems.length,
        uniqueNewItemsCount: uniqueNewItems.length,
        filteredDuplicatesCount: duplicates.length,
        duplicateKeys: duplicates,
      });

      // Append new items to end (not prepend) to maintain stable order - UI sorting handles priority
      return [...existingItems, ...uniqueNewItems].slice(0, maxItems);
    };

    // Merge discarded actions (deduped items with reasons)
    if (newData.aiAnalysis?.discardedActions) {
      const incomingDiscarded = newData.aiAnalysis.discardedActions.map(
        (item: any, idx: number) => {
          if (!item.id) {
            logger.error(
              'aiAnalysisSlice',
              'Received discarded suggestion without ID from backend (appendAiAnalysisPayload)!',
              {
                description: item.description || item.details,
                index: idx,
              },
            );
            item.id = `fallback_discarded_append_${Date.now()}_${idx}`;
          }

          return sanitizeActionAnswerState({
            id: item.id,
            ...mapActionProvenance(item),
            type: item.type || 'suggestion',
            details: item.details || item.description || '',
            originalDetails: item.originalDetails,
            improvementPatch: item.improvementPatch,
            reason: item.reason,
            status: item.status,
            score: item.score,
            time: item.time,
            answered: item.answered,
            answeredAt: item.answeredAt,
            autoDetected: item.autoDetected,
            priority: parsePriority(item.priority),
            userDeprioritized: item.userDeprioritized,
            userDeprioritizedAt: item.userDeprioritizedAt,
            severity: item.severity,
            severityReason: item.severityReason,
            medicalContext: item.medicalContext,
            relevancyScore: item.relevancyScore,
            conflict_details: item.conflict_details,
            deception_indicators: item.deception_indicators,
            confidence_score: item.confidence_score,
            dedupDecision: item.dedupDecision,
            dedupReason: item.dedupReason,
            noveltyScore: item.noveltyScore,
            similarityScore: item.similarityScore,
            closestMatchId: item.closestMatchId,
            // Completion criteria for answer evaluation (four-tier hierarchy)
            completion_criteria: item.completion_criteria,
            // Question importance and answer value tracking
            importanceToIncident: item.importanceToIncident,
            answerValuePotential: item.answerValuePotential,
            required: item.required,
            answer: item.answer,
            partialAnswer: item.partialAnswer,
            highlightedQuestion: item.highlightedQuestion,
            questionParts: item.questionParts,
            answeredParts: item.answeredParts,
            unansweredParts: item.unansweredParts,
            answerReasoning: item.answerReasoning,
            answerStatus: item.answerStatus,
          });
        },
      );

      const existingIds = new Set(state.discardedSuggestions.map((action) => action.id));
      const mergedDiscarded = [...state.discardedSuggestions];
      incomingDiscarded.forEach((action) => {
        if (existingIds.has(action.id)) {
          return;
        }
        mergedDiscarded.push(action);
        existingIds.add(action.id);
      });

      state.discardedSuggestions = mergedDiscarded;
    }

    // Merge suggested actions
    if (newData.aiAnalysis?.suggestedActions) {
      // Clear suggested actions extraction error when we receive valid data
      if (state.extractionErrors.suggestedActions) {
        delete state.extractionErrors.suggestedActions;
      }
      const now = Date.now();
      const newActions = newData.aiAnalysis.suggestedActions.map((item: any, idx: number) => {
        // Backend should ALWAYS provide IDs now (both initial and LLM suggestions)
        if (!item.id) {
          logger.error(
            'aiAnalysisSlice',
            'Received suggestion without ID from backend (appendAiAnalysisPayload)!',
            {
              description: item.description || item.details,
              index: idx,
            },
          );
          // Fallback ID generation (should never happen)
          item.id = `fallback_append_${Date.now()}_${idx}`;
        }

        const mappedType = item.type || 'suggestion';
        const officerAddressed = item.officerAddressed === true;
        const officerSideCompleted =
          officerAddressed && (mappedType === 'task' || mappedType === 'suggestion');

        return sanitizeActionAnswerState({
          id: item.id,
          ...mapActionProvenance(item),
          type: mappedType,
          details: item.details || item.description || '',
          originalDetails: item.originalDetails,
          improvementPatch: item.improvementPatch,
          reason: item.reason,
          status: item.status,
          score: item.score,
          time: item.time,
          answered: item.answered ?? (officerSideCompleted ? true : undefined),
          answeredAt:
            item.answeredAt || (officerSideCompleted ? item.officerAddressedAt : undefined),
          autoDetected: item.autoDetected,
          priority: parsePriority(item.priority),
          userDeprioritized: item.userDeprioritized,
          userDeprioritizedAt: item.userDeprioritizedAt,
          severity: item.severity,
          severityReason: item.severityReason,
          medicalContext: item.medicalContext,
          relevancyScore: item.relevancyScore,
          conflict_details: item.conflict_details,
          deception_indicators: item.deception_indicators,
          confidence_score: item.confidence_score,
          // Staleness tracking - initialize for new items
          createdAt: item.createdAt || now,
          timesIgnored: item.timesIgnored || 0,
          dedupDecision: item.dedupDecision,
          dedupReason: item.dedupReason,
          noveltyScore: item.noveltyScore,
          similarityScore: item.similarityScore,
          closestMatchId: item.closestMatchId,
          dedupCandidates: item.dedupCandidates,
          // Completion criteria for answer evaluation (four-tier hierarchy)
          completion_criteria: item.completion_criteria,
          // Question importance and answer value tracking
          importanceToIncident: item.importanceToIncident,
          answerValuePotential: item.answerValuePotential,
          required: item.required,
          // Answer state — carried through so that V2 UPDATEs to an answered
          // action (extend / correct) can be merged into Redux below.
          answer: item.answer,
          partialAnswer: item.partialAnswer,
          highlightedQuestion: item.highlightedQuestion,
          questionParts: item.questionParts,
          answeredParts: item.answeredParts,
          unansweredParts: item.unansweredParts,
          answerReasoning: item.answerReasoning,
          answerStatus: item.answerStatus || (officerSideCompleted ? 'complete' : undefined),
          correctedAt: item.correctedAt,
          officerAddressed: item.officerAddressed,
          officerAddressedAt: item.officerAddressedAt,
          officerAddressedPartial: item.officerAddressedPartial,
          officerQuote: item.officerQuote,
          officerAddressedReason: item.officerAddressedReason,
        });
      });

      // Intercept V2 UPDATE refreshes for already-answered actions: the backend
      // emits the existing action ID with a fresh `answer` when the caller
      // extends or corrects a prior answer. Apply those to every array that
      // holds a copy of the action (critically including resolvedSuggestions,
      // the carousel's source of truth) and drop them from the new-actions
      // pipeline so they don't resurface in the active list.
      const answeredIdSet = new Set(state.answeredPrompts);
      const existingActionById = new Map<string, SuggestedAction>();
      for (const existing of [
        ...state.suggestedActions,
        ...state.queuedActions,
        ...state.allSuggestedActions,
        ...state.resolvedSuggestions,
        ...state.discardedSuggestions,
        ...state.removedSuggestions,
        ...state.partialSuggestions,
      ] as SuggestedAction[]) {
        if (existing.id && !existingActionById.has(existing.id)) {
          existingActionById.set(existing.id, existing);
        }
      }
      const idMatchedUpdates: SuggestedAction[] = [];
      const passthroughActions: SuggestedAction[] = [];
      const staleDowngradeIds: string[] = [];
      for (const candidate of newActions) {
        if (candidate.id && answeredIdSet.has(candidate.id) && candidate.answer) {
          idMatchedUpdates.push(candidate);
          continue;
        }

        const existing = candidate.id ? existingActionById.get(candidate.id) : undefined;
        if (existing) {
          const existingComplete = hasCompleteAnswerState(existing, answeredIdSet);
          const incomingComplete = hasCompleteAnswerState(candidate);
          if (existingComplete && !incomingComplete) {
            staleDowngradeIds.push(candidate.id);
            continue;
          }

          const existingAddressed = hasOfficerAddressedState(existing);
          const incomingAddressed = hasOfficerAddressedState(candidate);
          if (
            existingAddressed &&
            !incomingAddressed &&
            candidate.answerStatus !== 'partial' &&
            !candidate.partialAnswer
          ) {
            staleDowngradeIds.push(candidate.id);
            continue;
          }

          // A partial candidate deliberately drops the addressed flags (see
          // markPromptPartial) — do not restore them from the local copy, or the
          // action would stay in the "addressed" group with parts still open.
          const candidateIsPartial =
            candidate.answerStatus === 'partial' || Boolean(candidate.partialAnswer);
          passthroughActions.push(
            existingAddressed && !incomingAddressed && !candidateIsPartial
              ? copyOfficerAddressedState(candidate, existing)
              : candidate,
          );
          continue;
        }

        passthroughActions.push(candidate);
      }

      if (staleDowngradeIds.length > 0) {
        logger.info('aiAnalysisSlice', 'Skipped stale suggested-action state downgrades', {
          ids: staleDowngradeIds,
          reason:
            'Incoming payload omitted completed/officer-addressed state already present locally',
        });
      }

      if (idMatchedUpdates.length > 0) {
        for (const upd of idMatchedUpdates) {
          const applyUpdate = (target: SuggestedAction | undefined) => {
            if (!target || !upd.answer) return;
            target.answer = upd.answer;
            if (upd.answeredParts) target.answeredParts = upd.answeredParts;
            if (
              upd.unansweredParts &&
              target.answerStatus !== 'complete' &&
              target.answered !== true
            ) {
              target.unansweredParts = upd.unansweredParts;
            }
            if (upd.answerReasoning) target.answerReasoning = upd.answerReasoning;
            if (target.answerStatus === 'complete' || target.answered === true) {
              clearPartialOnlyAnswerFields(target);
            }
            if (upd.correctedAt) {
              (target as SuggestedAction & { correctedAt?: string }).correctedAt = upd.correctedAt;
            }
          };
          applyUpdate(state.suggestedActions.find((a) => a.id === upd.id));
          applyUpdate(state.queuedActions.find((a) => a.id === upd.id));
          applyUpdate(state.allSuggestedActions.find((a) => a.id === upd.id));
          applyUpdate(state.resolvedSuggestions.find((a) => a.id === upd.id));
          applyUpdate(state.discardedSuggestions.find((a) => a.id === upd.id));
        }
        logger.info('aiAnalysisSlice', 'Merged V2 answer extensions/corrections', {
          count: idMatchedUpdates.length,
          ids: idMatchedUpdates.map((u) => u.id),
        });
      }

      // Filter out redundant dispatch suggestions (officer is already on call)
      const dedupedNewActions = dedupeById(passthroughActions, 'appendAiAnalysisPayload');
      const nonRedundantActions = dedupedNewActions.filter((newAction) => {
        const isRedundant = isNonEmergencyEscalationText(newAction.details);

        if (isRedundant) {
          logger.info('aiAnalysisSlice', 'Filtered out redundant dispatch suggestion', {
            filteredAction: newAction.details,
            reason: 'Officer is already on call - no need to dispatch units',
          });
        }
        return !isRedundant;
      });

      // Filter out new actions that are similar to already answered questions
      // IMPORTANT: Check allSuggestedActions (complete history) not just suggestedActions,
      // because answered actions get removed from suggestedActions but stay in history
      const answeredActions = state.allSuggestedActions.filter((a) => a.answered);
      const filteredNewActions = nonRedundantActions.filter((newAction) => {
        const isAnswered = answeredActions.some((answered) =>
          areSimilar(newAction.details, answered.details),
        );
        if (isAnswered) {
          logger.info('aiAnalysisSlice', '🚫 Filtered out already-answered question', {
            filteredAction: newAction.details,
            similarToAnswered: answeredActions.find((a) => areSimilar(newAction.details, a.details))
              ?.details,
            reason: 'Similar to already answered question',
          });
        }
        return !isAnswered;
      });

      // ─────────────────────────────────────────────────────────────────────
      // REQUIRED-OBLIGATION CLONE FILTER (fixes "required item dances in/out")
      // ─────────────────────────────────────────────────────────────────────
      // The Required sticky strip drops a card once its action is no longer
      // actively required in the main work area. Reappearance happens when
      // the backend RE-EMITS the same policy obligation as a *new* action —
      // a fresh `id` and slightly reworded `details` (the generator paraphrased
      // the question). The exact-normalized-text filters above (prependUnique,
      // the answered-history pass) only catch byte-identical re-emits, so a
      // reworded clone slips through, gets added as a brand-new open
      // `required: true` card, and the obligation pops back into the strip.
      // Once it's answered again it vanishes again — the dance.
      //
      // Fix: for incoming `required` actions only, also drop any that LOOSELY
      // match a prior `required` obligation of the same type anywhere in the
      // session history (`allSuggestedActions`, which includes still-open and
      // already-answered ones). "Loosely" = high word-token overlap on the
      // normalized text — much looser than exact equality, tight enough not to
      // conflate two genuinely different required items (there are only ever a
      // couple per call and their wording is distinct). The existing card (open
      // or answered) stays as the single source of truth for that obligation.
      const normalizeTokens = (text: string): string[] =>
        (text || '')
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, ' ')
          .trim()
          .split(' ')
          .filter(Boolean);
      const looselyMatchesRequiredObligation = (
        a: { details?: string; type?: string },
        b: { details?: string; type?: string },
      ): boolean => {
        if ((a.type || '') !== (b.type || '')) return false;
        const ta = normalizeTokens(a.details || '');
        const tb = normalizeTokens(b.details || '');
        if (ta.length === 0 || tb.length === 0) return false;
        const setB = new Set(tb);
        const shared = ta.reduce((n, tok) => n + (setB.has(tok) ? 1 : 0), 0);
        const overlap = shared / Math.min(ta.length, tb.length);
        return overlap >= 0.7;
      };
      const priorRequiredObligations = state.allSuggestedActions.filter((a) => a.required);
      const filteredNewActionsNoRequiredClones = filteredNewActions.filter((newAction) => {
        if (!newAction.required) return true;
        // A required clone of itself would re-detect with the same id — that's
        // handled by prependUnique's id-agnostic text dedup AND by the
        // answered-history pass; here we additionally guard the reworded /
        // new-id case against the full required-obligation history.
        const clonedFrom = priorRequiredObligations.find(
          (prior) =>
            prior.id !== newAction.id && looselyMatchesRequiredObligation(newAction, prior),
        );
        if (clonedFrom) {
          logger.info('aiAnalysisSlice', '🚫 Dropped re-emitted required obligation clone', {
            incomingId: newAction.id,
            incomingDetails: (newAction.details || '').substring(0, 80),
            matchedExistingId: clonedFrom.id,
            matchedExistingDetails: (clonedFrom.details || '').substring(0, 80),
            matchedExistingAnswered:
              clonedFrom.answered === true || clonedFrom.answerStatus === 'complete',
            reason:
              'Required obligation already exists (open or answered) — keeping the original card',
          });
          return false;
        }
        return true;
      });

      // Preserve answered/fading state from existing actions
      const existingActionsWithState: SuggestedAction[] = [
        ...state.suggestedActions,
        ...state.queuedActions,
      ] as SuggestedAction[];
      const oldOrder = existingActionsWithState.map((a) => a.id);

      // Merge with existing and split into active (max 15) and queued
      const allActions: SuggestedAction[] = prependUnique<SuggestedAction>(
        filteredNewActionsNoRequiredClones,
        existingActionsWithState,
        100, // Keep up to 100 total (active + queued)
      );

      // Sort by priority BEFORE splitting into active/queued
      // This ensures high-priority new items get into the active 10
      // Lower priority number = higher priority (1=critical, 50=baseline, 100=deferred)
      // Priority is the only factor - types are intentionally mixed
      const sortedActions = [...allActions].sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50));

      // Split: apply display caps for top 15, rest go to queue
      const { visible: activeActions, queued: queuedActions } =
        splitActionsByDisplayCaps(sortedActions);

      const orderNow = Date.now();
      const newOrder = sortedActions.map((a) => a.id);
      updatePriorityChangeTimesForOrder(state, oldOrder, newOrder, orderNow);

      state.suggestedActions = activeActions;
      state.queuedActions = queuedActions;

      // Add new actions to allSuggestedActions archive (for incident report historical view)
      filteredNewActionsNoRequiredClones.forEach((newAction) => {
        if (!state.allSuggestedActions.find((a) => a.id === newAction.id)) {
          state.allSuggestedActions.push(newAction);
        }
      });

      logger.info('aiAnalysisSlice', 'Suggested actions merged', {
        totalActionsCount: allActions.length,
        activeCount: state.suggestedActions.length,
        queuedCount: state.queuedActions.length,
        allActionsArchiveCount: state.allSuggestedActions.length,
        activeActions: state.suggestedActions,
        queuedActions: state.queuedActions,
      });
    }

    // Backend may still send notes - we ignore them now (using manual pinning instead)

    // Smart merge activity feed events to prevent flickering (preserve existing objects when unchanged)
    // CRITICAL: Only update if we have actual events - empty arrays should NOT wipe existing data
    if (newData.activityFeed?.events && newData.activityFeed.events.length > 0) {
      // Clear activity feed extraction error when we receive valid data
      if (state.extractionErrors.activityFeed) {
        delete state.extractionErrors.activityFeed;
      }
      // Helper: Compare two activity events for equality
      const eventsEqual = (a: any, b: any): boolean => {
        if (!a || !b) return false;
        // For string events, direct comparison
        if (typeof a === 'string' && typeof b === 'string') {
          return a === b;
        }
        // For object events, compare all fields except 'note'
        if (typeof a === 'object' && typeof b === 'object') {
          const keysA = Object.keys(a);
          const keysB = Object.keys(b);
          if (keysA.length !== keysB.length) return false;
          return keysA.every((key) => {
            if (key === 'note') return true; // Skip note field
            return JSON.stringify(a[key]) === JSON.stringify(b[key]);
          });
        }
        return false;
      };

      // A shorter incoming feed is unioned in, never allowed to shrink the
      // existing one (mergeActivityFeedEvents, 2026-08-17); an equal/longer
      // one is the authoritative regeneration and goes through the
      // reference-preserving positional merge below.
      const incomingEvents = mergeActivityFeedEvents(
        state.activityFeed.events as (string | ActivityEvent)[],
        newData.activityFeed.events as (string | ActivityEvent)[],
      );
      // Merge events - preserve existing objects if unchanged
      const mergedEvents = incomingEvents.map((newEvent: any, index: number) => {
        if (index < state.activityFeed.events.length) {
          const existingEvent = state.activityFeed.events[index];
          if (eventsEqual(existingEvent, newEvent)) {
            // Preserve existing object (keeps React references, prevents re-render)
            return existingEvent;
          }
        }
        // New or changed event
        return newEvent;
      });

      state.activityFeed.events = mergedEvents;
    }

    const normalizedIncomingEntities = normalizeEntityTimestamps(
      normalizeEntitiesPayload(newData.entities),
    );

    // Smart merge entities to prevent flickering (preserve existing objects when unchanged)
    if (normalizedIncomingEntities) {
      // Log entity counts for debugging
      const entityCounts = {
        people: normalizedIncomingEntities.people?.length || 0,
        locations: normalizedIncomingEntities.locations?.length || 0,
        organizations: normalizedIncomingEntities.organizations?.length || 0,
        objects: normalizedIncomingEntities.objects?.length || 0,
        activity: normalizedIncomingEntities.activity?.length || 0,
        other: normalizedIncomingEntities.other?.length || 0,
        relationships: normalizedIncomingEntities.connections?.relationships?.length || 0,
        hasIncident: !!normalizedIncomingEntities.incident,
      };
      logger.info('aiAnalysisSlice', '[ENTITIES] Received entity update', entityCounts);

      // Helper: Compare two entities for equality (deep comparison of key fields)
      const entitiesEqual = (a: any, b: any): boolean => {
        if (!a || !b) return false;
        // Compare the UNION of content keys (shallow comparison is sufficient
        // for our use case). Deliberately NOT a key-count check: a stored entity
        // carries `lastUpdatedAt` and an incoming AI payload does not, so
        // comparing counts declared every entity changed on every pass — which
        // is what reset the "time since change" badge for entities whose
        // properties had never been touched. Walking the union instead means a
        // key present on one side and absent on the other is still detected as a
        // real difference, via its `undefined` value.
        const keys = new Set<string>();
        for (const key of Object.keys(a)) {
          if (!EQUALITY_IGNORED_ENTITY_KEYS.has(key)) keys.add(key);
        }
        for (const key of Object.keys(b)) {
          if (!EQUALITY_IGNORED_ENTITY_KEYS.has(key)) keys.add(key);
        }
        for (const key of keys) {
          if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) return false;
        }
        return true;
      };

      const getRelationshipKey = (
        sourceId: string,
        targetId: string,
        relType: string,
        entity: any,
      ): string => {
        // Relationship identity is server-owned. This fallback is only for legacy
        // relationship arrays that still pass through the generic entity merger.
        if (typeof entity.identityKey === 'string' && entity.identityKey.trim()) {
          return `identity:${entity.identityKey}`;
        }
        if (typeof entity.id === 'string' && entity.id.trim()) {
          return `id:${entity.id}`;
        }
        const roleBand = typeof entity.roleBand === 'string' ? entity.roleBand : 'secondary';
        return `legacy-rel:${sourceId}:${targetId}:${relType}:${roleBand}`;
      };

      // Extract the first N significant words from a description for fingerprinting.
      // This helps distinguish "Karen, the store owner" from "Karen, the caller's sister".
      const descriptionFingerprint = (desc: string | undefined, maxWords: number = 4): string => {
        if (!desc || typeof desc !== 'string') return '';
        const stopWords = new Set([
          'the',
          'a',
          'an',
          'and',
          'or',
          'is',
          'was',
          'of',
          'in',
          'at',
          'to',
          'for',
          'by',
          'on',
          'with',
        ]);
        const words = desc
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '')
          .split(/\s+/)
          .filter((w) => w.length > 2 && !stopWords.has(w));
        return words.slice(0, maxWords).sort().join(',');
      };

      const getEntityKey = (entity: any): string | null => {
        if (!entity || typeof entity !== 'object') return null;
        // For relationships: dedup by source+target+type (NOT by unique id)
        // This prevents duplicate edges between the same entity pair.
        const sourceId =
          typeof entity.sourceEntityId === 'string'
            ? entity.sourceEntityId
            : typeof entity.sourceId === 'string'
              ? entity.sourceId
              : '';
        const targetId =
          typeof entity.targetEntityId === 'string'
            ? entity.targetEntityId
            : typeof entity.targetId === 'string'
              ? entity.targetId
              : '';
        if (sourceId && targetId) {
          const relType =
            typeof entity.relationshipType === 'string'
              ? entity.relationshipType
              : typeof entity.relationship_type === 'string'
                ? entity.relationship_type
                : 'related_to';
          return getRelationshipKey(sourceId, targetId, relType, entity);
        }
        // Primary: use stable entity ID. PatchAiz V2 assigns IDs once via the entity
        // index and reuses them across extraction cycles, so the ID is the most reliable
        // dedup key. This prevents duplicates when geocoding enriches an address (which
        // would change a name+address-based key).
        if (typeof entity.id === 'string' && entity.id.trim()) {
          return `id:${entity.id}`;
        }
        // Fallback: name-based key for legacy entities without IDs.
        // Uses normalized name + role + description fingerprint to distinguish genuinely
        // different entities that share a name (e.g., two sisters named Karen).
        const name = typeof entity.name === 'string' ? entity.name.trim().toLowerCase() : '';
        if (name) {
          const qualifier = entity.role || entity.locationType || entity.type || '';
          const address =
            typeof entity.address === 'string' ? entity.address.trim().toLowerCase() : '';
          const descFp = descriptionFingerprint(entity.shortDescription || entity.description);
          const parts = ['name', name];
          if (address) parts.push(address);
          if (qualifier) parts.push(qualifier);
          if (descFp) parts.push(descFp);
          return parts.join(':').toLowerCase();
        }
        return null;
      };

      const GENERIC_ENTITY_TEXT_PATTERNS = [
        /^unknown(?:\s+\w+)?$/i,
        /^unnamed(?:\s+\w+)?$/i,
        /^unlabeled(?:\s+\w+)?$/i,
        /^property$/i,
        /^object$/i,
        /^item$/i,
        /^location$/i,
        /^organization$/i,
        /^person$/i,
        /^activity$/i,
        /^incident$/i,
      ];

      const hasMeaningfulEntityValue = (value: unknown): boolean => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string') return value.trim().length > 0;
        if (Array.isArray(value)) return value.some((item) => hasMeaningfulEntityValue(item));
        if (typeof value === 'object') {
          return Object.values(value as Record<string, unknown>).some((item) =>
            hasMeaningfulEntityValue(item),
          );
        }
        return true;
      };

      const isGenericEntityText = (value: unknown): boolean => {
        if (typeof value !== 'string') return false;
        const normalized = value.trim();
        if (!normalized) return false;
        return GENERIC_ENTITY_TEXT_PATTERNS.some((pattern) => pattern.test(normalized));
      };

      const mergeEntityValue = (
        existingValue: unknown,
        incomingValue: unknown,
        key: string,
        preferIncoming: boolean,
      ): unknown => {
        if (key === 'note') {
          return hasMeaningfulEntityValue(existingValue) ? existingValue : incomingValue;
        }

        if (key === 'id') {
          return hasMeaningfulEntityValue(existingValue) ? existingValue : incomingValue;
        }

        if (!hasMeaningfulEntityValue(existingValue)) {
          return incomingValue;
        }

        if (!hasMeaningfulEntityValue(incomingValue)) {
          return existingValue;
        }

        if (key === 'confidence' || key === 'confidenceScore') {
          if (typeof existingValue === 'number' && typeof incomingValue === 'number') {
            return Math.max(existingValue, incomingValue);
          }
        }

        if (Array.isArray(existingValue) && Array.isArray(incomingValue)) {
          const seen = new Set<string>();
          const mergedArray: unknown[] = [];

          for (const item of [...existingValue, ...incomingValue]) {
            const token = JSON.stringify(item);
            if (seen.has(token)) continue;
            seen.add(token);
            mergedArray.push(item);
          }

          if (
            !preferIncoming &&
            mergedArray.length === existingValue.length &&
            entitiesEqual(existingValue, mergedArray)
          ) {
            return existingValue;
          }

          return mergedArray;
        }

        if (
          existingValue &&
          incomingValue &&
          typeof existingValue === 'object' &&
          typeof incomingValue === 'object' &&
          !Array.isArray(existingValue) &&
          !Array.isArray(incomingValue)
        ) {
          const existingRecord = existingValue as Record<string, unknown>;
          const incomingRecord = incomingValue as Record<string, unknown>;
          const mergedRecord: Record<string, unknown> = { ...existingRecord };

          for (const nestedKey of Object.keys(incomingRecord)) {
            mergedRecord[nestedKey] = mergeEntityValue(
              existingRecord[nestedKey],
              incomingRecord[nestedKey],
              nestedKey,
              preferIncoming,
            );
          }

          return mergedRecord;
        }

        if (typeof existingValue === 'string' && typeof incomingValue === 'string') {
          const existingText = existingValue.trim();
          const incomingText = incomingValue.trim();

          if (!existingText) return incomingText;
          if (!incomingText) return existingText;
          if (existingText === incomingText) return existingValue;

          if (preferIncoming) {
            return incomingText;
          }

          if (isGenericEntityText(existingText) && !isGenericEntityText(incomingText)) {
            return incomingText;
          }

          if (!isGenericEntityText(existingText) && isGenericEntityText(incomingText)) {
            return existingValue;
          }

          if (incomingText.length > existingText.length) {
            return incomingText;
          }

          return existingValue;
        }

        return preferIncoming ? incomingValue : existingValue;
      };

      const mergeMatchedEntity = <T extends Record<string, any>>(
        existingItem: T,
        incomingItem: T,
        preferIncoming: boolean,
        updatedAt: number,
        geocoded: boolean,
      ): T => {
        // A geocode answer for text the location no longer has is dropped whole:
        // its unit AND the address/name it carries.
        if (geocoded && isStaleGeocodeUnit(existingItem, incomingItem)) {
          return existingItem;
        }
        const merged: Record<string, unknown> = { ...existingItem };
        const allKeys = new Set([...Object.keys(existingItem), ...Object.keys(incomingItem)]);

        for (const key of allKeys) {
          if (key === 'lastUpdatedAt') continue;
          merged[key] = mergeEntityValue(
            (existingItem as Record<string, unknown>)[key],
            (incomingItem as Record<string, unknown>)[key],
            key,
            preferIncoming,
          );
        }

        // The geocode fields are one unit: applied verbatim for the current text,
        // reset to pending when the text moved on without a new unit.
        if (geocoded) applyGeocodeUnit(existingItem, incomingItem, merged);

        // Compare what we are about to STORE, not what arrived. `mergeEntityValue`
        // routinely REJECTS an incoming difference — confidence takes Math.max,
        // null/empty values are not "meaningful", shorter text loses to longer.
        // Comparing against the raw incoming item therefore reset the badge for
        // updates that changed nothing: existing confidence 88 + incoming 70 keeps
        // 88, and incoming `dob: null` keeps the stored DOB, yet both used to
        // re-stamp. There is deliberately no fast path on the raw items: one
        // invariant ("stamp iff the merged content differs") cannot drift from
        // itself, and any normalisation inside the merger is then applied
        // consistently.
        if (entitiesEqual(existingItem, merged)) {
          return existingItem;
        }

        merged.lastUpdatedAt = updatedAt;
        return merged as T;
      };

      // Helper: Merge entity arrays while preserving existing items on partial updates
      const mergeEntityArray = <T extends Record<string, any>>(
        existing: T[],
        incoming: T[],
        geocoded = false,
      ): T[] => {
        if (!incoming || incoming.length === 0) return existing;
        if (!existing || existing.length === 0) {
          // First arrival — stamp all entities with lastUpdatedAt
          const now = Date.now();
          return incoming.map((item) =>
            (item as any).lastUpdatedAt ? item : { ...item, lastUpdatedAt: now },
          );
        }

        const existingByKey = new Map<string, { index: number; item: T }>();
        existing.forEach((item, index) => {
          const key = getEntityKey(item);
          if (key) {
            existingByKey.set(key, { index, item });
          }
        });

        const incomingKeys = incoming
          .map((item) => getEntityKey(item))
          .filter((key): key is string => Boolean(key));
        const incomingKeySet = new Set(incomingKeys);
        const existingKeySet = new Set(existingByKey.keys());
        const hasFullKeyCoverage =
          existingKeySet.size > 0 &&
          Array.from(existingKeySet).every((key) => incomingKeySet.has(key));

        const mergeNow = Date.now();

        // Stamp new entities arriving for the first time
        const stampNew = (item: T): T => {
          if (!(item as any).lastUpdatedAt) {
            return { ...item, lastUpdatedAt: mergeNow };
          }
          return item;
        };

        // Hand back the ORIGINAL array when every element came through
        // reference-identical. `mergeMatchedEntity` already preserves item
        // identity for unchanged entities, but rebuilding the array still
        // produced a new reference — and the callers gate
        // `entityCategoryLastUpdate.<category> = mergeNow` on `merged !==
        // state.entities.<category>`. So an unchanged pass still bumped the
        // per-category timestamp, which feeds `entitiesUpdateTick` in
        // useAutoResponseMode and re-fired the officer-assist queue-update POST
        // on every analysis pass. Same defect class as the badge itself, one
        // layer up.
        const preserveIfUnchanged = (next: T[]): T[] =>
          next.length === existing.length && next.every((item, idx) => item === existing[idx])
            ? existing
            : next;

        if (hasFullKeyCoverage) {
          // Incoming looks like a full snapshot - honor incoming order and allow removals
          return preserveIfUnchanged(
            incoming.map((item) => {
              const key = getEntityKey(item);
              if (key && existingByKey.has(key)) {
                const existingItem = existingByKey.get(key)!.item;
                return mergeMatchedEntity(existingItem, item, true, mergeNow, geocoded);
              }
              return stampNew(item);
            }),
          );
        }

        // Partial update - update matches, keep existing, append new
        const result = [...existing];
        const updatedIndexes = new Set<number>();

        for (const item of incoming) {
          const key = getEntityKey(item);
          if (key && existingByKey.has(key)) {
            const match = existingByKey.get(key)!;
            updatedIndexes.add(match.index);
            result[match.index] = mergeMatchedEntity(match.item, item, false, mergeNow, geocoded);
            continue;
          }

          const fallbackIndex = existing.findIndex(
            (existingItem, idx) => !updatedIndexes.has(idx) && entitiesEqual(existingItem, item),
          );
          if (fallbackIndex >= 0) {
            updatedIndexes.add(fallbackIndex);
            result[fallbackIndex] = existing[fallbackIndex];
          } else {
            result.push(stampNew(item));
          }
        }

        return preserveIfUnchanged(result);
      };

      // Update caller (single entity, not array)
      // IMPORTANT: Merge caller data instead of replacing to preserve user-entered metadata
      // Fields like name, phone, callReason, email, dob, address come from participant metadata
      // and should NOT be overwritten by AI analysis which doesn't include these fields
      // BUG FIX: Previously name and phone were not preserved, causing caller name to vanish
      if (normalizedIncomingEntities.caller) {
        const existingCaller = state.entities.caller;
        const newCaller = normalizedIncomingEntities.caller;

        // Merge: Preserve user-entered fields, use AI data as fallback for empty fields
        // If existing data is user-entered, preserve it; otherwise use AI data
        const mergedCaller = {
          ...newCaller,
          // CRITICAL: Preserve name and phone if user-entered (from participant metadata)
          // These are the most important fields that were being lost!
          name:
            existingCaller.userEntered && existingCaller.name
              ? existingCaller.name
              : newCaller.name || existingCaller.name,
          phone:
            existingCaller.userEntered && existingCaller.phone
              ? existingCaller.phone
              : newCaller.phone || existingCaller.phone,
          // Preserve other metadata fields if not provided in new data
          callReason: newCaller.callReason || existingCaller.callReason,
          userEntered: existingCaller.userEntered || newCaller.userEntered,
          email: newCaller.email || existingCaller.email,
          dob: newCaller.dob || existingCaller.dob,
          address: newCaller.address || existingCaller.address,
          callbackNumber: newCaller.callbackNumber || existingCaller.callbackNumber,
          // Preserve the officer's note. This literal spreads the INCOMING caller
          // and then re-lists the fields worth keeping, so anything omitted here is
          // destroyed on a real caller change — and `note` was omitted, so an AI
          // address update silently erased officer-authored text. setCallerUserEntered
          // data already does this (`note: state.entities.caller.note`); this branch
          // just never did.
          note: existingCaller.note ?? (newCaller as { note?: string }).note,
        };

        if (!entitiesEqual(state.entities.caller, mergedCaller)) {
          const callerUpdatedAt = Date.now();
          mergedCaller.lastUpdatedAt = callerUpdatedAt;
          state.entities.caller = mergedCaller;
          state.entityCategoryLastUpdate.caller = callerUpdatedAt;
        }
      }

      // Merge incident entity (central node)
      if (normalizedIncomingEntities.incident) {
        // For incident, we replace or merge based on whether we already have one
        if (
          !state.entities.incident ||
          !entitiesEqual(state.entities.incident, normalizedIncomingEntities.incident)
        ) {
          state.entities.incident = normalizedIncomingEntities.incident as Incident;
          state.entityCategoryLastUpdate.incident = Date.now();
        }
      }

      // Merge officers array
      if (normalizedIncomingEntities.officers) {
        const merged = mergeEntityArray(
          state.entities.officers,
          normalizedIncomingEntities.officers,
        );
        if (merged !== state.entities.officers) {
          state.entityCategoryLastUpdate.officers = Date.now();
        }
        state.entities.officers = merged;
      }

      // Merge entity arrays with smart comparison
      // Track category update timestamps for freshness display
      const mergeNow = Date.now();
      if (normalizedIncomingEntities.people) {
        const merged = mergeEntityArray(state.entities.people, normalizedIncomingEntities.people);
        if (merged !== state.entities.people) state.entityCategoryLastUpdate.people = mergeNow;
        state.entities.people = merged;
      }
      if (normalizedIncomingEntities.organizations) {
        const merged = mergeEntityArray(
          state.entities.organizations,
          normalizedIncomingEntities.organizations,
        );
        if (merged !== state.entities.organizations)
          state.entityCategoryLastUpdate.organizations = mergeNow;
        state.entities.organizations = merged;
      }
      if (normalizedIncomingEntities.locations) {
        const merged = mergeEntityArray(
          state.entities.locations,
          normalizedIncomingEntities.locations,
          true,
        );
        if (merged !== state.entities.locations)
          state.entityCategoryLastUpdate.locations = mergeNow;
        state.entities.locations = merged;
      }
      if (normalizedIncomingEntities.objects) {
        const merged = mergeEntityArray(state.entities.objects, normalizedIncomingEntities.objects);
        if (merged !== state.entities.objects) state.entityCategoryLastUpdate.objects = mergeNow;
        state.entities.objects = merged;
      }
      if (normalizedIncomingEntities.activity) {
        const merged = mergeEntityArray(
          state.entities.activity,
          normalizedIncomingEntities.activity,
        );
        if (merged !== state.entities.activity) state.entityCategoryLastUpdate.behaviors = mergeNow;
        state.entities.activity = merged;
      }
      if (normalizedIncomingEntities.policiesAndLaws) {
        const merged = mergeEntityArray(
          state.entities.policiesAndLaws,
          normalizedIncomingEntities.policiesAndLaws,
        );
        if (merged !== state.entities.policiesAndLaws)
          state.entityCategoryLastUpdate.policies = mergeNow;
        state.entities.policiesAndLaws = merged;
      }
      if (normalizedIncomingEntities.other) {
        const merged = mergeEntityArray(state.entities.other, normalizedIncomingEntities.other);
        if (merged !== state.entities.other) state.entityCategoryLastUpdate.other = mergeNow;
        state.entities.other = merged;
      }
      if (normalizedIncomingEntities.connections) {
        // Cast to any to handle legacy fields from backend that aren't in frontend types
        const connections = normalizedIncomingEntities.connections as any;
        const relationshipSnapshot: any[] = [];
        let hasRelationshipSnapshot = false;

        // Server relationships are an authoritative full snapshot. The client
        // preserves the array as sent and does not perform semantic pair dedupe.
        if (Array.isArray(connections.relationships)) {
          hasRelationshipSnapshot = true;
          relationshipSnapshot.push(...connections.relationships);
        }

        // Convert legacy fields to relationships format for backward compatibility
        // peoplesRelationship: { person1, person2, relationshipType }
        if (connections.peoplesRelationship && Array.isArray(connections.peoplesRelationship)) {
          hasRelationshipSnapshot = true;
          const convertedPeopleRels = connections.peoplesRelationship.map(
            (rel: { person1?: string; person2?: string; relationshipType?: string }) => {
              const relationshipType = rel.relationshipType || 'related_to';
              return {
                id: `legacy_people_${rel.person1}_${rel.person2}`,
                sourceEntityId: rel.person1 || '',
                targetEntityId: rel.person2 || '',
                relationshipType,
                label: `${rel.person1} - ${relationshipType} - ${rel.person2}`,
              };
            },
          );
          relationshipSnapshot.push(...convertedPeopleRels);
        }

        // locationRelationship: { location, object, relationshipType }
        if (connections.locationRelationship && Array.isArray(connections.locationRelationship)) {
          hasRelationshipSnapshot = true;
          const convertedLocationRels = connections.locationRelationship.map(
            (rel: { location?: string; object?: string; relationshipType?: string }) => {
              const relationshipType = rel.relationshipType || 'located_at';
              return {
                id: `legacy_location_${rel.location}_${rel.object}`,
                sourceEntityId: rel.location || '',
                targetEntityId: rel.object || '',
                relationshipType,
                label: `${rel.object} - ${relationshipType} - ${rel.location}`,
              };
            },
          );
          relationshipSnapshot.push(...convertedLocationRels);
        }

        // patternConnections: { entity1, entity2, pattern }
        if (connections.patternConnections && Array.isArray(connections.patternConnections)) {
          hasRelationshipSnapshot = true;
          const convertedPatterns = connections.patternConnections.map(
            (pat: { entity1?: string; entity2?: string; pattern?: string }) => ({
              id: `legacy_pattern_${pat.entity1}_${pat.entity2}`,
              sourceEntityId: pat.entity1 || '',
              targetEntityId: pat.entity2 || '',
              relationshipType: 'pattern',
              label: pat.pattern || `Pattern: ${pat.entity1} - ${pat.entity2}`,
            }),
          );
          relationshipSnapshot.push(...convertedPatterns);
        }

        if (hasRelationshipSnapshot) {
          const existingRelationships = state.entities.connections.relationships;
          if (!entitiesEqual(existingRelationships, relationshipSnapshot)) {
            state.entities.connections.relationships = relationshipSnapshot;
            state.entityCategoryLastUpdate.connections = mergeNow;
          }
        }
      }

      // Clear entity extraction error when we receive valid entities
      if (state.extractionErrors.entities) {
        delete state.extractionErrors.entities;
      }
    }

    // Handle extraction errors (sent separately when LLM calls fail)
    if (newData.extractionErrors) {
      const now = Date.now();
      if (newData.extractionErrors.entities) {
        // Validate error structure and provide detailed logging
        const entityError = newData.extractionErrors.entities;
        const isValidError =
          entityError.error === true && entityError.errorType && entityError.errorMessage;

        if (!isValidError) {
          logger.warn('aiAnalysisSlice', 'Malformed entity extraction error received', {
            error: entityError,
            hasErrorFlag: entityError.error === true,
            hasErrorType: !!entityError.errorType,
            hasErrorMessage: !!entityError.errorMessage,
            fullPayload: newData.extractionErrors,
          });
        }

        state.extractionErrors.entities = {
          ...entityError,
          timestamp: now,
        };

        logger.error('aiAnalysisSlice', 'Entity extraction error received (append)', {
          errorType: entityError.errorType || 'unknown',
          errorMessage: entityError.errorMessage || 'No error message provided',
          fullError: entityError,
          timestamp: now,
          context: 'appendAiAnalysisPayload',
        });
      }
      if (newData.extractionErrors.activityFeed) {
        const feedError = newData.extractionErrors.activityFeed;
        const isValidError =
          feedError.error === true && feedError.errorType && feedError.errorMessage;

        if (!isValidError) {
          logger.warn('aiAnalysisSlice', 'Malformed activity feed extraction error received', {
            error: feedError,
            hasErrorFlag: feedError.error === true,
            hasErrorType: !!feedError.errorType,
            hasErrorMessage: !!feedError.errorMessage,
            fullPayload: newData.extractionErrors,
          });
        }

        state.extractionErrors.activityFeed = {
          ...feedError,
          timestamp: now,
        };

        logger.error('aiAnalysisSlice', 'Activity feed extraction error received (append)', {
          errorType: feedError.errorType || 'unknown',
          errorMessage: feedError.errorMessage || 'No error message provided',
          fullError: feedError,
          timestamp: now,
          context: 'appendAiAnalysisPayload',
        });
      }
      if (newData.extractionErrors.suggestedActions) {
        const actionsError = newData.extractionErrors.suggestedActions;
        const isValidError =
          actionsError.error === true && actionsError.errorType && actionsError.errorMessage;

        if (!isValidError) {
          logger.warn('aiAnalysisSlice', 'Malformed suggested actions extraction error received', {
            error: actionsError,
            hasErrorFlag: actionsError.error === true,
            hasErrorType: !!actionsError.errorType,
            hasErrorMessage: !!actionsError.errorMessage,
            fullPayload: newData.extractionErrors,
          });
        }

        state.extractionErrors.suggestedActions = {
          ...actionsError,
          timestamp: now,
        };

        logger.error('aiAnalysisSlice', 'Suggested actions extraction error received (append)', {
          errorType: actionsError.errorType || 'unknown',
          errorMessage: actionsError.errorMessage || 'No error message provided',
          fullError: actionsError,
          timestamp: now,
          context: 'appendAiAnalysisPayload',
        });
      }
    }

    // Handle officer addressed badges (when officer verbally addresses a suggested action)
    if (newData.officerAskedBadges) {
      const badges = newData.officerAskedBadges as Array<{
        action_id: string;
        badge: string;
        officer_quote: string;
        reason?: string;
        confidence: number;
      }>;

      logger.info('aiAnalysisSlice', 'Processing officerAddressedBadges', {
        badgeCount: badges.length,
        badges,
      });

      badges.forEach((badge) => {
        const actionId = badge.action_id;
        const timestamp = new Date().toISOString();

        // Update in suggestedActions
        const suggestedIndex = state.suggestedActions.findIndex((a) => a.id === actionId);
        if (suggestedIndex !== -1) {
          state.suggestedActions[suggestedIndex].officerAddressed = true;
          state.suggestedActions[suggestedIndex].officerAddressedAt = timestamp;
          // Always store the officer's quote in the dedicated field
          state.suggestedActions[suggestedIndex].officerQuote = badge.officer_quote;
          // Store the reason if provided
          if (badge.reason) {
            state.suggestedActions[suggestedIndex].officerAddressedReason = badge.reason;
          }
          // Also store in answerReason if not already set (backward compatibility)
          if (!state.suggestedActions[suggestedIndex].answerReason) {
            state.suggestedActions[suggestedIndex].answerReason =
              `Officer addressed: "${badge.officer_quote}"`;
          }
          logger.info('aiAnalysisSlice', 'Updated suggestedAction with addressed badge', {
            actionId,
            details: state.suggestedActions[suggestedIndex].details,
            officerQuote: badge.officer_quote,
            reason: badge.reason,
            confidence: badge.confidence,
          });
        }

        // Update in queuedActions
        const queuedIndex = state.queuedActions.findIndex((a) => a.id === actionId);
        if (queuedIndex !== -1) {
          state.queuedActions[queuedIndex].officerAddressed = true;
          state.queuedActions[queuedIndex].officerAddressedAt = timestamp;
          // Always store the officer's quote in the dedicated field
          state.queuedActions[queuedIndex].officerQuote = badge.officer_quote;
          // Store the reason if provided
          if (badge.reason) {
            state.queuedActions[queuedIndex].officerAddressedReason = badge.reason;
          }
          // Also store in answerReason if not already set (backward compatibility)
          if (!state.queuedActions[queuedIndex].answerReason) {
            state.queuedActions[queuedIndex].answerReason =
              `Officer addressed: "${badge.officer_quote}"`;
          }
          logger.info('aiAnalysisSlice', 'Updated queuedAction with addressed badge', {
            actionId,
            details: state.queuedActions[queuedIndex].details,
            officerQuote: badge.officer_quote,
            reason: badge.reason,
          });
        }

        // Update in allSuggestedActions (archive)
        const archiveIndex = state.allSuggestedActions.findIndex((a) => a.id === actionId);
        if (archiveIndex !== -1) {
          state.allSuggestedActions[archiveIndex].officerAddressed = true;
          state.allSuggestedActions[archiveIndex].officerAddressedAt = timestamp;
          // Always store the officer's quote in the dedicated field
          state.allSuggestedActions[archiveIndex].officerQuote = badge.officer_quote;
          // Store the reason if provided
          if (badge.reason) {
            state.allSuggestedActions[archiveIndex].officerAddressedReason = badge.reason;
          }
          // Also store in answerReason if not already set (backward compatibility)
          if (!state.allSuggestedActions[archiveIndex].answerReason) {
            state.allSuggestedActions[archiveIndex].answerReason =
              `Officer addressed: "${badge.officer_quote}"`;
          }
          logger.info('aiAnalysisSlice', 'Updated allSuggestedActions with addressed badge', {
            actionId,
            details: state.allSuggestedActions[archiveIndex].details,
            reason: badge.reason,
          });
        }

        const targetAction =
          suggestedIndex !== -1
            ? state.suggestedActions[suggestedIndex]
            : queuedIndex !== -1
              ? state.queuedActions[queuedIndex]
              : archiveIndex !== -1
                ? state.allSuggestedActions[archiveIndex]
                : undefined;
        const isOfficerSideType =
          targetAction?.type === 'task' || targetAction?.type === 'suggestion';
        if (isOfficerSideType) {
          if (!state.answeredPrompts.includes(actionId)) {
            state.answeredPrompts.push(actionId);
          }
          state.partialPrompts = state.partialPrompts.filter((id) => id !== actionId);
          state.partialSuggestions = state.partialSuggestions.filter((a) => a.id !== actionId);
          state.detectedPrompts = state.detectedPrompts.filter((id) => id !== actionId);

          const completeOfficerSideAction = (target: SuggestedAction | undefined) => {
            if (!target) return;
            target.answerStatus = 'complete';
            target.answered = true;
            target.answeredAt = timestamp;
            if (badge.officer_quote && !target.answer) {
              target.answer = badge.officer_quote;
            }
          };

          completeOfficerSideAction(state.suggestedActions[suggestedIndex]);
          completeOfficerSideAction(state.queuedActions[queuedIndex]);
          completeOfficerSideAction(state.allSuggestedActions[archiveIndex]);
        }

        // Log if action not found
        if (suggestedIndex === -1 && queuedIndex === -1 && archiveIndex === -1) {
          logger.warn('aiAnalysisSlice', 'Action not found for officer addressed badge', {
            actionId,
            officerQuote: badge.officer_quote,
            reason: badge.reason,
          });
        }
      });
    }
  },

  /**
   * Update timestamp tracking when transcript changes
   * Used by prioritization routine to detect if re-prioritization is needed
   */
  updateLastTranscriptTime: (state: AiAnalysisState) => {
    state.lastTranscriptChangeTime = Date.now();
  },

  /**
   * Update timestamp tracking when items change
   * Used by prioritization routine to detect if re-prioritization is needed
   */
  updateLastItemsTime: (state: AiAnalysisState) => {
    state.lastItemsChangeTime = Date.now();
  },

  // Mark an action as having been spoken via TTS (persists a Volume2 badge on the card)
  markActionSpoken: (state: AiAnalysisState, action: PayloadAction<{ actionId: string }>) => {
    const { actionId } = action.payload;
    const timestamp = new Date().toISOString();

    const idx = state.suggestedActions.findIndex((a) => a.id === actionId);
    if (idx !== -1) {
      state.suggestedActions[idx].spokenAt = timestamp;
    }

    const qIdx = state.queuedActions.findIndex((a) => a.id === actionId);
    if (qIdx !== -1) {
      state.queuedActions[qIdx].spokenAt = timestamp;
    }

    const archIdx = state.allSuggestedActions.findIndex((a) => a.id === actionId);
    if (archIdx !== -1) {
      state.allSuggestedActions[archIdx].spokenAt = timestamp;
    }
  },

  // ── Speak attempt tracking (multi-instance TTS icons) ──

  addSpeakAttempt: (
    state: AiAnalysisState,
    action: PayloadAction<{
      actionId: string;
      attemptId: string;
      source?: 'manual' | 'auto-response';
    }>,
  ) => {
    const { actionId, attemptId, source } = action.payload;
    const timestamp = new Date().toISOString();
    const attempt = { id: attemptId, queuedAt: timestamp, status: 'queued' as const, source };

    for (const list of [state.suggestedActions, state.queuedActions, state.allSuggestedActions]) {
      const item = list.find((a) => a.id === actionId);
      if (item) {
        if (!item.speakAttempts) item.speakAttempts = [];
        if (item.speakAttempts.some((a) => a.id === attemptId)) continue;
        item.speakAttempts.push(attempt);
        // Step 11: do NOT backfill spokenAt here. With autonomous officer
        // mode, an action queued for speech may be deferred (officer pivots
        // or ad-libs). spokenAt is now set only by markActionSpoken from
        // the playout-echo branch, which knows the actually-spoken action_id.
      }
    }
  },

  startSpeakAttempt: (
    state: AiAnalysisState,
    action: PayloadAction<{ actionId: string; attemptId: string }>,
  ) => {
    const { actionId, attemptId } = action.payload;
    const timestamp = new Date().toISOString();

    for (const list of [state.suggestedActions, state.queuedActions, state.allSuggestedActions]) {
      const item = list.find((a) => a.id === actionId);
      const attempt = item?.speakAttempts?.find((a) => a.id === attemptId);
      if (attempt) {
        attempt.status = 'speaking';
        attempt.startedAt = timestamp;
      }
    }
  },

  completeSpeakAttempt: (
    state: AiAnalysisState,
    action: PayloadAction<{ actionId: string; attemptId: string }>,
  ) => {
    const { actionId, attemptId } = action.payload;
    const timestamp = new Date().toISOString();

    for (const list of [state.suggestedActions, state.queuedActions, state.allSuggestedActions]) {
      const item = list.find((a) => a.id === actionId);
      const attempt = item?.speakAttempts?.find((a) => a.id === attemptId);
      if (attempt) {
        attempt.status = 'completed';
        attempt.completedAt = timestamp;
      }
    }
  },

  failSpeakAttempt: (
    state: AiAnalysisState,
    action: PayloadAction<{ actionId: string; attemptId: string }>,
  ) => {
    const { actionId, attemptId } = action.payload;
    const timestamp = new Date().toISOString();

    for (const list of [state.suggestedActions, state.queuedActions, state.allSuggestedActions]) {
      const item = list.find((a) => a.id === actionId);
      const attempt = item?.speakAttempts?.find((a) => a.id === attemptId);
      if (attempt) {
        attempt.status = 'failed';
        attempt.completedAt = timestamp;
      }
    }
  },

  // Officer clicked the Volume2 icon on an item that was already in the speech
  // queue. Mark the attempt cancelled rather than failed so the audit trail can
  // distinguish "TTS pipeline broke" from "officer changed their mind".
  // Only flips queued|speaking → cancelled; ignores attempts that already
  // settled (completed/failed/cancelled) so a stale event can't undo history.
  cancelSpeakAttempt: (
    state: AiAnalysisState,
    action: PayloadAction<{ actionId: string; attemptId: string }>,
  ) => {
    const { actionId, attemptId } = action.payload;
    const timestamp = new Date().toISOString();

    for (const list of [state.suggestedActions, state.queuedActions, state.allSuggestedActions]) {
      const item = list.find((a) => a.id === actionId);
      const attempt = item?.speakAttempts?.find((a) => a.id === attemptId);
      if (attempt && (attempt.status === 'queued' || attempt.status === 'speaking')) {
        attempt.status = 'cancelled';
        attempt.cancelledAt = timestamp;
      }
    }
  },

  setAnalysisStatus: (
    state: AiAnalysisState,
    action: PayloadAction<{ status: string; startedAt?: number }>,
  ) => {
    const { status, startedAt } = action.payload;
    if (status === 'running' && startedAt) {
      state.analysisStartedAt = startedAt;
    } else {
      // #1701: a pass that was running just completed -> entity tabs are
      // settled; the Loading state is for the first pass only.
      if (state.analysisStartedAt != null) state.entityPassSettled = true;
      state.analysisStartedAt = null;
    }
  },

  // #1701: the saved analysis is being loaded; entity tabs show a loading state
  // (not "no data found yet") until this clears.
  setAnalysisHydrating: (state: AiAnalysisState, action: PayloadAction<boolean>) => {
    state.analysisHydrating = action.payload;
    // Hydration finished (even with nothing saved): the tabs are settled.
    if (!action.payload) state.entityPassSettled = true;
  },

  // Fill in deferred detail fields (quotes, parts, reasoning) for an already-decided action.
  // The fast decision call sets answerStatus+tier+confidence immediately;
  // this reducer populates the audit trail from the batched detail extraction.
  updateAnswerDetails: (
    state: AiAnalysisState,
    action: PayloadAction<{
      promptId: string;
      answer?: string;
      partialAnswer?: string;
      answerReasoning?: string;
      transcriptQuote?: string;
      questionParts?: Array<{
        text: string;
        status: 'answered' | 'unanswered' | 'plain';
        partRef?: string;
      }>;
      answeredParts?: Array<{
        part: string;
        answer: string;
        transcriptQuote?: string;
        reasoning?: string;
      }>;
      unansweredParts?: string[];
    }>,
  ) => {
    const {
      promptId,
      answer,
      partialAnswer,
      answerReasoning,
      questionParts,
      answeredParts,
      unansweredParts,
    } = action.payload;

    // Search across all action arrays
    const arrays = [
      state.suggestedActions,
      state.queuedActions,
      state.allSuggestedActions,
      state.resolvedSuggestions,
    ];
    for (const arr of arrays) {
      const idx = arr.findIndex((a) => a.id === promptId);
      if (idx !== -1) {
        if (answer !== undefined) arr[idx].answer = answer;
        if (answerReasoning !== undefined) arr[idx].answerReasoning = answerReasoning;
        if (answeredParts && answeredParts.length > 0) arr[idx].answeredParts = answeredParts;

        // SINGLE-SOURCE-OF-TRUTH: the canonical projection
        // (hydrateCanonicalActionProjection) is the authoritative writer of the
        // displayed-text fields (highlightedQuestion / questionParts /
        // answeredParts / unansweredParts). This reducer is only the data-channel
        // FALLBACK (dispatched from useTranscriptMonitor's answer-details-update
        // handler, including the recurring audit-only batch with no answer text).
        //
        // It must therefore be STRICTLY NON-REGRESSING for the displayed text:
        // it may only PROMOTE an existing action to a richer, strictly-valid
        // annotated partial shape. It must NEVER null/blank highlightedQuestion
        // or questionParts, and never downgrade a valid annotation to plain —
        // doing so produces the plain<->annotated alternation, because the next
        // canonical broadcast re-applies the annotation that this reducer cleared.
        //
        // The auto-response-gating fields (answer / answerReasoning /
        // answeredParts, written above) are unaffected — only the four
        // displayed-text fields are guarded here.
        const isPartial = arr[idx].answerStatus === 'partial';
        if (isPartial && questionParts && questionParts.length > 0) {
          const rebuiltHighlight = questionParts
            .map((p) => {
              if (p.status === 'answered') return `**${p.text}**`;
              if (p.status === 'unanswered') return `~~${p.text}~~`;
              return p.text;
            })
            .join('');

          const candidate = {
            ...arr[idx],
            partialAnswer: partialAnswer ?? arr[idx].partialAnswer,
            questionParts,
            answeredParts: answeredParts ?? arr[idx].answeredParts,
            unansweredParts: unansweredParts ?? arr[idx].unansweredParts,
            highlightedQuestion: rebuiltHighlight,
          };

          // Only adopt the incoming segments when they form a strictly-valid
          // annotated partial shape (both answered AND unanswered evidence).
          // If they don't, leave whatever the canonical projection already set
          // in place — do NOT clear it (no clearInvalidPartialState here).
          if (hasValidPartialAnswerShape(candidate)) {
            arr[idx].questionParts = questionParts;
            arr[idx].highlightedQuestion = rebuiltHighlight;
            if (partialAnswer !== undefined) arr[idx].partialAnswer = partialAnswer;
            if (unansweredParts && unansweredParts.length > 0)
              arr[idx].unansweredParts = unansweredParts;
          }
        }
        // Non-partial (complete / undefined) and invalid-partial payloads:
        // intentionally NO-OP on the displayed-text fields. The canonical
        // projection owns complete-shape derivation (sanitizeActionAnswerState)
        // and any clearing of partial-only fields; this fallback must not race
        // it by blanking text the canonical store set.
      }
    }
  },
};
